/**
 * Fonte ÚNICA de verdade dos atributos do jogo.
 * Nunca espalhe valores como "damage = 10" pelos arquivos — leia daqui.
 */
import type { EssenceType } from "../essence/EssenceTypes";
import type { WeaponType, HazardKind } from "../items/EquipmentTypes";

export interface AttackShape {
  windup: number;
  hitframe: number;
  recovery: number;
  range: number;
  arc: number;
  knockback: number;
}

export interface EssenceDrop {
  type: EssenceType;
  min: number;
  max: number;
  chance: number; // 0..1
}

// Fase 8: novas silhuetas estilizadas (não mais só orb/runner/brute)
//   beast  = quadrúpede ágil (Shardfang)   heavy = quadrúpede pesado c/ chifres (Craghorn)
//   flyer  = criatura flutuante (Aetherwing) shell = criatura defensiva (Glintback)
//   warden = Elite da Região 2 (Shard Warden)
// Fase 10 (Tempest Reach): novas silhuetas — strider (Galestrider, bípede leve/rápido),
//   spark (Arcbeak, criatura aérea energética), bulwark (Stormback, quadrúpede com carapaça),
//   stalker (Cloudstalker, predador de emboscada), sentinel (Tempest Warden, Elite alto).
export type EnemyShape =
  | "orb" | "runner" | "brute" | "beast" | "heavy" | "flyer" | "shell" | "warden"
  | "strider" | "spark" | "bulwark" | "stalker" | "sentinel";

// Comportamento de combate (Fase 8/10). Ausente => "melee" (padrão histórico).
//   strafe = circula/dash lateral (Galestrider) · shield = Static Shield c/ ponto exposto (Stormback)
//   ambush = fade parcial + surge (Cloudstalker) · tempest = Elite Arc Dash + Storm Field (Warden)
//   hybrid = 10.6F: bate de PERTO e atira de LONGE (não fica só de um jeito nem faz kite)
export type EnemyBehavior =
  | "melee" | "lunge" | "charge" | "ranged" | "guard" | "warden"
  | "strafe" | "shield" | "ambush" | "tempest" | "hybrid";

export interface GroundSmashDef {
  telegraph: number; // segundos de indicador no chão ANTES do dano
  radius: number;
  damage: number;
  cooldown: number;
  knockback: number;
}

export interface EliteConfig {
  aura: number; // cor da aura
  hookStagger: number; // segundos de stagger quando o Hook acerta o Elite
  groundSmash: GroundSmashDef;
}

/** Ataque à distância (Aetherwing / Shard Burst / Arc Bolt). */
export interface RangedDef {
  cooldown: number;
  windup: number;
  projectileSpeed: number;
  projectileColor: number;
  damage?: number; // default = enemy.damage
  burst?: number; // Fase 10 (Arcbeak): nº de projéteis em sequência (default 1)
  burstGap?: number; // intervalo entre projéteis do burst (s)
  /** 10.6F — alcance do tiro (behavior "hybrid"). Sem isto o tiro usaria o alcance de melee. */
  range?: number;
}

/** Investida telegrafada (Craghorn charge / Shardfang lunge / Crystal Charge). */
export interface ChargeDef {
  telegraph: number; // indicador antes de disparar
  speed: number;
  damage: number;
  maxDist: number;
  cooldown: number;
  knockback: number;
  recovery: number;
}

/** Escudo defensivo (Glintback Crystal Guard / Stormback Static Shield). */
export interface GuardDef {
  reduction: number; // fração de dano reduzida enquanto ativo (0..1)
  duration: number;
  cooldown: number;
  hookBreaks?: boolean; // Fase 10 (Stormback): Hook quebra o escudo + stagger curto
  stagger?: number; // duração do stagger ao quebrar o escudo pelo Hook
}

/** Emboscada (Cloudstalker): fade parcial -> telegraph -> surge (dash) -> golpe. */
export interface AmbushDef {
  stalkRange: number; // distância de perseguição/flanqueio
  fadeOpacity: number; // opacidade mínima no fade (nunca invisível — §19/§20)
  telegraph: number; // s de aviso (olhos/cristais + rastro) antes do surge
  surgeSpeed: number;
  surgeDamage: number;
  cooldown: number;
  knockback: number;
}

/** Storm Field (Tempest Warden): 2–4 zonas telegrafadas que energizam. */
export interface StormFieldDef {
  zones: number; // nº de zonas (2–4)
  radius: number;
  telegraph: number; // s de aviso antes de energizar
  damage: number;
  cooldown: number;
}

/** Tema visual de uma região (chão, luz, névoa, cor de acento/cristal). */
export interface VisualTheme {
  ground: number;
  grid: number;
  ambient: number;
  ambientIntensity: number;
  sun: number;
  sunIntensity: number;
  fog: number;
  accent: number; // cristais / energia
  stone: number; // pedras / ruínas
}

/** Sub-área nomeada de uma região (faixa de Z) — usada no banner/descobertas. */
export interface SubAreaDef {
  id: string;
  nameKey: string;
  zMin: number;
  zMax: number;
  tint: number;
}

/** Definição escalável de REGIÃO do mundo (Fase 8 — arquitetura, sem "if region ==="). */
export interface WorldRegionDef {
  id: string;
  nameKey: string;
  tier: 1 | 2 | 3;
  recommendedLevel: number;
  spawn: { x: number; z: number }; // ponto de nascimento (= Sanctuary)
  sanctuary: { center: { x: number; z: number }; radius: number };
  arenaHalfSize: number;
  /** 10.5M — grade de TERRITÓRIOS da região (colunas × linhas cobrindo a arena inteira).
   *  10.6O — colWeights/rowWeights: proporções irregulares (soma livre, normalizada internamente). */
  grid: { cols: number; rows: number; colWeights?: number[]; rowWeights?: number[] };
  enemyTable: { id: string; weight: number }[];
  maxAlive: number;
  subAreas: SubAreaDef[];
  theme: VisualTheme;
  /** 10.9BS — céu/névoa PRÓPRIOS da região (o ciclo dia/noite interpola a partir daqui). */
  mood?: RegionMood;
  // Rift Gate desta região: destino + se depende do boss da região
  rift?: { x: number; z: number; toRegion: string; requiresBoss?: string; nameKey: string };
}

/** 10.9BS — clima visual de uma região: cor do céu, cor da névoa e densidade (FogExp2). */
export interface RegionMood {
  sky: number;
  fog: number;
  density: number;
  /** 10.9GR — correção de cor da região (passe de acabamento): tinta, saturação, contraste, sombras, vinheta. */
  grade?: { tint: number; saturation: number; contrast: number; lift: number; vignette: number };
  /** 10.9GR — força do bloom nesta região (padrão 0.45). */
  bloom?: number;
}

/** 10.5M — BIOMA de um território: paleta + densidade da decoração procedural. */
export interface BiomeDef {
  /** 10.6N — perigo ambiental do bioma. Sem o anel certo o Caçador perde vida ali dentro. */
  hazard?: HazardKind;
  ground: number; // tinta do chão daquele pedaço
  trunk: number;
  foliage: number[];
  rock: number;
  crystal: number;
  trees: number;
  rocks: number;
  grass: number;
  crystals: number;
}

/** 10.5M — TERRITÓRIO: um "pedaço" do mapa com nome, tier, bioma e GANGUE próprios. */
export interface TerritoryDef {
  id: string;
  regionId: string;
  nameKey: string;
  col: number;
  row: number;
  tier: number; // 1 = perto da base, 5 = fim de mundo
  biome: string; // chave em territories.biomes
  gang: { id: string; weight: number }[]; // quem domina o território
  champion?: string; // elite que lidera a gangue (1 por território)
  home?: boolean; // território da base (sem gangue perto do Sanctuary)
}

export interface EnemyDef {
  id: string;
  maxHp: number;
  damage: number;
  moveSpeed: number;
  aggroRange: number;
  attackRange: number;
  attackCooldown: number;
  attackWindup: number;
  xpReward: number;
  radius: number;
  separation: number;
  knockbackResist: number; // 0 = leve, 1 = imóvel
  color: number;
  scale: number;
  shape: EnemyShape;
  drops: EssenceDrop[];
  armor?: number; // default 0
  elite?: EliteConfig; // presente => inimigo Elite (aura, ground smash, hook stagger)
  // ---- Fase 8: comportamento/silhueta estendidos ----
  behavior?: EnemyBehavior; // default "melee"
  /** 10.6G — arma contra a qual ele é FRACO (leva mais dano). */
  weakTo?: WeaponType;
  /** 10.6G — arma contra a qual ele RESISTE bem (vermelho: leva bem menos dano). */
  resists?: WeaponType;
  /** 10.6G — arma que ele AGUENTA razoavelmente (laranja: nível intermediário). */
  tough?: WeaponType;
  flying?: boolean; // Aetherwing: voa (melee não alcança; Hook derruba; Bow acerta)
  tier?: 1 | 2 | 3; // região de origem (loot/scaling)
  ranged?: RangedDef; // behavior "ranged"
  charge?: ChargeDef; // behavior "charge"/"lunge"/"strafe"/"tempest" (dash/arc dash)
  guard?: GuardDef; // behavior "guard"/"shield"
  ambush?: AmbushDef; // behavior "ambush" (Cloudstalker)
  stormField?: StormFieldDef; // behavior "tempest" (Tempest Warden)
  hookPullResist?: number; // 0..1 extra: reduz a velocidade de pull do Hook (peso)
  groundedDuration?: number; // Fase 10 (Arcbeak): tempo derrubado pelo Hook (default GameConfig.hook.groundDuration)
  crystalColor?: number; // cor dos cristais/ornamentos da criatura
  damageType?: "slashing" | "piercing" | "blunt"; // 10.5K: tipo do golpe (default pela silhueta — ver Enemy.ts)
  /** 10.9BT — pele fixa desta criatura (sobrescreve a pele escolhida pela região). */
  skin?: "chitin" | "scales" | "stone" | "hide" | "crystal";
}

/** Efeito de cada upgrade do Void Altar. */
export interface AltarUpgradeDef {
  key: EssenceType;
  stat: "baseDamage" | "maxHp" | "moveSpeed" | "armor" | "critChance";
  amount: number;
  descKey: string; // i18n do efeito (ex.: "+1 Damage")
}

/** 10.9BP — raio da SAFE ZONE do Santuário (única constante; `sanctuary.radius` e
 * `world.regions[].sanctuary.radius` leem daqui pra não divergir). */
const SANCTUARY_RADIUS = 14;

/** 10.9BP — LAYOUT DO HUB (Santuário) num lugar só. Estruturas em ANEL (raio ~9.5 a partir do
 * centro (0,0)), cada uma num "ponto cardeal"; a parede fica em SANCTUARY_RADIUS (14). Todas as
 * outras entradas de posição (mailbox/altar/bossZone.portalHub/TestPad/PvP) APONTAM pra cá. */
const HUB = {
  ringRadius: 9.5,
  altar: { x: 0, z: -9.5 }, // norte
  mailbox: { x: -9.5, z: 0 }, // oeste
  pvpPortal: { x: -7, z: 7 }, // sudoeste
  bossPortal: { x: 7, z: 7 }, // sudeste (Fenda dos Titãs)
  testPad: { x: 0, z: 20 }, // sul — fora do anel, perto da parede (só dev/teste)
  training: { x: 9.5, z: 0 }, // leste — 10.9BY: Boneco de Treino (skills offline)
};

export const GameConfig = {
  hub: HUB,
  player: {
    maxHp: 100,
    moveSpeed: 5,
    baseDamage: 10,
    attacksPerSecond: 1,
    critChance: 0.05,
    critDamageMult: 1.5,
    armor: 0,

    accel: 45,
    decel: 35,
    rotationSpeed: 14,
    radius: 0.5,

    // Sprint (substitui o Dash): aumento temporário e CONTROLÁVEL de velocidade.
    sprint: {
      speedMult: 1.6,
      duration: 2.0,
      cooldown: 4.0,
    },

    // "Feel" por tipo de arma (Fase 7): escala só a ANIMAÇÃO (windup/recovery) e o
    // avanço visual (lunge). NÃO altera dano nem cadência (attacksPerSecond manda no DPS).
    //   sword = rápido e controlado · axe = pesado, windup maior · bow = draw/release
    weaponFeel: {
      sword: { windupScale: 0.9, recoveryScale: 1.0, lunge: 0.18 },
      axe: { windupScale: 1.7, recoveryScale: 1.25, lunge: 0.12 },
      bow: { windupScale: 1.35, recoveryScale: 1.0, lunge: -0.12 },
      mace: { windupScale: 1.85, recoveryScale: 1.35, lunge: 0.14 }, // clava: pesada, impacto forte
      staff: { windupScale: 1.25, recoveryScale: 1.05, lunge: -0.1 }, // cajado: canaliza e recua (à distância)
      fist: { windupScale: 0.8, recoveryScale: 0.9, lunge: 0.14 },
    } as Record<string, { windupScale: number; recoveryScale: number; lunge: number }>,

    // timing de animação do ataque (range/arc/knockback/velocidade vêm da ARMA)
    attack: {
      windup: 0.12,
      hitframe: 0.1,
      recovery: 0.18,
      range: 2.4, // fallback; a arma sobrescreve
      arc: Math.PI * 0.7,
      knockback: 4,
    } as AttackShape,
  },

  // Arma padrão quando NADA está equipado (desarmado). As armas reais vêm de `equipment`.
  weapon: {
    unarmed: {
      weaponType: "sword" as const,
      attackType: "melee" as "melee" | "ranged",
      damageType: "blunt" as "slashing" | "piercing" | "blunt",
      baseDamage: 4,
      attackRange: 1.8,
      attacksPerSecond: 1.2,
      arc: Math.PI * 0.6,
      knockback: 3,
      projectileSpeed: 0,
      visualId: "fist",
    },
  },

  // Auto-target: seleção automática de alvo (mobile = sem clicar no inimigo).
  autoTarget: {
    radius: 9, // alcance de aquisição de alvo
    refreshInterval: 0.2, // não recalcula todo frame
    dropHysteresis: 2.5, // metros extras antes de largar o alvo atual (estabilidade)
  },

  // Estado de combate / regeneração / retorno dos inimigos.
  enemyCombat: {
    disengage: 5, // s sem dano/perseguição => sai de combate
    regenDelay: 5, // s fora de combate antes de regenerar
    regenRate: 0.1, // fração do MaxHP por segundo
    maxLeash: 24, // distância máx. da origem antes de forçar RETURN
    // 10.9CG — PERAMBULAR (Thiago: "os monstros precisam ficar se movimentando pelo mapa"). Em idle o
    // monstro escolhe um ponto a 8–18 m, anda até lá devagar, para 2–6 s e escolhe outro; a "casa"
    // (origem da coleira) acompanha — assim, com o tempo, ele percorre o mapa inteiro da região.
    wander: { minDist: 8, maxDist: 18, speedMult: 0.45, pauseMin: 2, pauseMax: 6 },
    // 10.9CM — os servos do necromante agora TÊM VIDA. Inimigo com um servo a até `soulAggro` m
    // troca o alvo do player para o servo (o servo TANQUEIA). Quando o servo morre, volta ao player.
    soulAggro: 6,
  },

  // Void Sanctuary — base segura REAL (safe zone): sem inimigos, sem combate, imune,
  // cura o Player, ponto de respawn. `buffer` cria uma faixa externa de anti-cheese:
  // dentro dela combate/hook seguem bloqueados (mas o Player já não é imune) — só volta
  // a lutar após sair TOTALMENTE do buffer (histerese contra abuso da borda).
  sanctuary: {
    center: { x: 0, z: 0 },
    radius: SANCTUARY_RADIUS, // safe zone (imune, cura, sem combate) — 10.9BP: 9 → 14 (hub em anel)
    buffer: 0.5 as number, // 10.4U: combate liga quase na hora que sai da safe zone (antes 2m dava "dead zone")
    playerRegenDelay: 1.5, // s sem tomar dano dentro da base antes de curar
    playerRegenRate: 0.08, // fração do MaxHP por segundo
    healFxInterval: 0.3 as number, // cadência das partículas de cura (mobile-friendly)
    // Fase 8.1: temas visuais por região (mesma LÓGICA, aparência própria)
    themes: {
      forest: { variant: "forest", aura: 0x4a7bff, ring: 0x8ea2ff, wall: 0x5c7bff, crystal: 0x9d7bff, crystalEmissive: 0x6a3bff, stone: 0x3a3550, light: 0x7c4dff },
      wilds: { variant: "wilds", aura: 0x2f8fbf, ring: 0x62e8ff, wall: 0x4ec3ff, crystal: 0x8ef0ff, crystalEmissive: 0x2f9fd6, stone: 0x50627d, light: 0x4ec3ff },
      tempest: { variant: "tempest", aura: 0x5aa8e0, ring: 0x7fd6ff, wall: 0x6fc4ff, crystal: 0xbfefff, crystalEmissive: 0x4aa6e0, stone: 0x8496ad, light: 0x9fd0ff },
    },
  },

  // 10.6O — regen de campo nerfada: era impossível morrer (1.8% = vida cheia em ~55s).
  fieldRegen: {
    delay: 8,
    rate: 0.007, // 0.7% da vida máxima por segundo
    healFxInterval: 0.55 as number,
  },

  // VIP (entitlement de CONTEÚDO, NÃO billing). Em TESTE: acesso ILIMITADO (todos são VIP)
  // para planejar/testar regalias e áreas VIP. Depois virá de conta/entitlement. Nenhum
  // fluxo de pagamento é implementado aqui — só o gate de conteúdo lê isto.
  // 10.9FB — Thiago: "tira o VIP gratis, pra comprar, vamos comecar a limitar". Pacotes de 💎 e
  // Passe VIP entregavam tudo SEM COBRAR (modo teste). Desligado ate plugar o pagamento de verdade
  // (Google Play Billing): os botoes mostram "Em breve". 💎 seguem vindo do jogo e dos anuncios.
  // 10.9IJ — Thiago: "nao libera vip". Estava em `true` = modo teste, que ENTREGAVA o Passe VIP
  // e os pacotes de diamante SEM COBRAR. Pros testes oficiais isso volta a `false`: os botoes
  // mostram "Em breve". Os niveis de VIP por pontos acumulados continuam existindo, e os
  // diamantes continuam vindo do jogo (login, missao diaria, profundidade, anuncio).
  billing: { enabled: false },
  vip: {
    unlimitedTesting: false, // false = VIP por tier (sistema final); true = todos VIP (teste)
    // regalias VIP (só recompensa/conteúdo — NÃO poder de combate direto/pay-to-win forte):
    perks: {
      dropRarityBoost: true, // sorteio de drop com raridade melhorada (2 rolls, melhor)
      absorbMult: 1.5, // +50% na absorção (vida/dano/cura por monstro)
      essenceMult: 1.5, // +50% de essência coletada
    },
  },

  // 10.6R — DIAMANTES: moeda premium ganha JOGANDO (NÃO é dinheiro real).
  // Usada para desbloquear áreas VIP e conteúdo exclusivo.
  // 10.9BK — Thiago: "subir de nível e matar boss NÃO dá diamante, só moeda dourada". 💎 fica
  // reservado a login diário, missão diária, evento/temporada/PvP e compra. Boss paga 🪙 (abaixo).
  diamonds: {
    perDailyLogin: 5,
    perBossFirstKill: 0,
    perBossRefight: 0,
    perAreaBoss: 0,
    perDepthMilestone: 10, // a cada 5 profundidades
    perDailyMission: 3,
    // 10.9K2 — 💎 por vitória no PvP (1 duelo, 2 no 3x3) tinha 0 limite: duas contas combinadas
    // farmavam diamante infinito. Teto por dia (a 🪙 da vitória continua sem teto).
    pvpDailyCap: 10,
  },
  // 10.9BK — o que os bosses pagam em FRAGMENTOS no lugar dos diamantes
  bossCoins: {
    firstKill: 400,   // 1ª vez num boss fixo (Alfa / Colossus)
    refight: 80,      // repetição
    areaBoss: 200,    // boss aleatório de área
    eventBoss: 250,   // boss da Invasão do Vazio…
    eventPerTier: 60, // …+ por tier alcançado
    milestoneMail: 300, // correio de marco do boss (era 20 💎)
  },

  // 10.6R — CORREIO: caixa de correio no Santuário para mensagens + recompensas.
  mailbox: {
    position: HUB.mailbox, // 10.9BP — posição vem do layout do hub
    interactRadius: 2.8,
    maxMessages: 50,
  },

  // 10.6R — ÁREAS VIP: zonas exclusivas em cada região, desbloqueadas com diamantes.
  // Conteúdo melhorado (inimigos mais fortes, loot melhor, XP bônus).
  vipAreas: {
    zones: [
      { id: "crystallineHollow", regionId: "forgottenForest", nameKey: "vip.crystallineHollow", descKey: "vip.crystallineHollow.desc", cost: 50, xpMult: 1.5, dropBoost: true, tier: 3 },
      { id: "aetherSanctum", regionId: "shatteredWilds", nameKey: "vip.aetherSanctum", descKey: "vip.aetherSanctum.desc", cost: 120, xpMult: 1.8, dropBoost: true, tier: 4 },
      { id: "stormNexus", regionId: "tempestReach", nameKey: "vip.stormNexus", descKey: "vip.stormNexus.desc", cost: 250, xpMult: 2.0, dropBoost: true, tier: 5 },
    ],
  },

  // 10.9BG — TIERS VIP por PONTOS (Thiago: "precisa de X diamante pra chegar no tier 1… até o 6").
  // 10.9BV — Thiago: "quem compra o pacote de R$500 já fica Mestre" → limiares subiram: Mestre pede
  // 30.000 pts (~2,5 pacotes Lenda), Diamante 10.000 (1 Lenda), Ouro 1.500 (~1 pacote Grande).
  // Não se COMPRA tier: cada 💎 gasto no jogo ou comprado em pacote/passe vira 1 ponto VIP,
  // e o tier sobe SOZINHO ao cruzar `points`. `cost` = pontos exigidos (acumulados, nunca descem).
  // 10.9J7 — AUDITORIA (Thiago pediu "veja o preço dos itens VIP"): weaponSlot (+1 arma na
  // Corrente, todo tier, desde o Nv1) e absorbMult (multiplicava o HP/dano PERMANENTE comprado na
  // Loja de Atributos) eram poder de combate real vindo de 💎 — e 💎 se compra com dinheiro (cost
  // dos vipTiers é em PONTOS VIP, que sobem 1:1 com 💎 gasto). Contradizia a regra confirmada pelo
  // Thiago ("VIP = só conteúdo, nunca combate por dinheiro"). Removido weaponSlot (a Corrente volta
  // a ter só os 4 slots por NÍVEL, iguais pra todo mundo); absorbMult zerado (a Loja de Atributos
  // agora só aceita 🪙 Fragmentos — ver buyAttrItem — então não é mais "pagar por atributo", e um
  // multiplicador de VIP em cima dela voltaria a ligar poder a 💎 pela porta dos fundos).
  // No lugar, `bagBonus`: espaço extra de bolsa GRÁTIS por tier — conveniência pura, sem tocar em
  // dano/vida/armadura (o espaço COMPRADO continua existindo à parte, em progress.bagSlots).
  // 10.9L0 — DECISÃO DO THIAGO (2026-09-24): o `weaponSlot` VOLTOU — "um diferencial pra fazer o
  // personagem bater mais, uma vantagem pra quem é VIP". É vantagem de combate de propósito (exceção
  // à regra "VIP = só conteúdo"). O bagBonus continua junto. absorbMult segue zerado.
  vipTiers: [
    {
      id: "bronze", cost: 100, nameKey: "vip.tier.bronze", icon: "🥉",
      perks: { xpMult: 1.15, coinMult: 1.0, essenceMult: 1.0, absorbMult: 1.0, dailyDiamonds: 3, bagBonus: 4, weaponSlot: true, dropRarityBoost: false },
      areas: ["crystallineHollow"],
    },
    {
      id: "silver", cost: 500, nameKey: "vip.tier.silver", icon: "🥈",
      perks: { xpMult: 1.30, coinMult: 1.20, essenceMult: 1.25, absorbMult: 1.0, dailyDiamonds: 8, bagBonus: 8, weaponSlot: true, dropRarityBoost: true },
      areas: ["crystallineHollow", "aetherSanctum"],
    },
    {
      id: "gold", cost: 1500, nameKey: "vip.tier.gold", icon: "🥇",
      perks: { xpMult: 1.50, coinMult: 1.50, essenceMult: 1.50, absorbMult: 1.0, dailyDiamonds: 15, bagBonus: 12, weaponSlot: true, dropRarityBoost: true },
      areas: ["crystallineHollow", "aetherSanctum", "stormNexus"],
    },
    {
      id: "platinum", cost: 4000, nameKey: "vip.tier.platinum", icon: "💠",
      perks: { xpMult: 1.70, coinMult: 1.70, essenceMult: 1.70, absorbMult: 1.0, dailyDiamonds: 25, bagBonus: 16, weaponSlot: true, dropRarityBoost: true },
      areas: ["crystallineHollow", "aetherSanctum", "stormNexus"],
    },
    {
      id: "diamond", cost: 10000, nameKey: "vip.tier.diamond", icon: "💎",
      perks: { xpMult: 1.90, coinMult: 1.90, essenceMult: 1.90, absorbMult: 1.0, dailyDiamonds: 40, bagBonus: 20, weaponSlot: true, dropRarityBoost: true },
      areas: ["crystallineHollow", "aetherSanctum", "stormNexus"],
    },
    {
      id: "master", cost: 30000, nameKey: "vip.tier.master", icon: "👑",
      perks: { xpMult: 2.20, coinMult: 2.20, essenceMult: 2.20, absorbMult: 1.0, dailyDiamonds: 60, bagBonus: 26, weaponSlot: true, dropRarityBoost: true },
      areas: ["crystallineHollow", "aetherSanctum", "stormNexus"],
    },
  ],

  // 10.6U — PASSE VIP: assinatura de 30 dias com bônus multiplicativos exclusivos.
  // Diferente dos tiers (permanentes): o Passe é temporário e MULTIPLICA XP/moedas.
  vipPass: {
    // 10.9AE — comprado com DINHEIRO REAL (modo teste, como os diamondPacks) e concede um
    // BÔNUS de diamantes junto. Antes era 400💎; agora R$ + diamondBonus.
    priceBRL: 19.99,     // preço em dinheiro real (modo teste — não cobra de verdade ainda)
    diamondBonus: 300,   // 💎 concedidos JUNTO com o passe (o "bônus de diamante" pedido)
    durationDays: 30,
    xpMult: 1.5,      // +50% XP em TUDO (soma nos outros bônus)
    coinMult: 1.5,    // +50% moedas
    dailyDiamonds: 25, // bônus diário no correio (além do tier)
    // 10.9K3 — Thiago: "uma recompensa pra quem compra passe". Roupa EXCLUSIVA (só visual, sem
    // combate) que fica com o jogador pra sempre, mesmo depois que o passe vence.
    outfitId: "aurora",
    // 10.9J7 — "passSlot" (6º slot de arma, poder de combate por 💎) nunca chegou a ser lido em
    // lugar nenhum do código (achado em auditoria) — removido antes de alguém implementar em cima
    // dele. O Passe já tem valor real sem isso: xpMult/coinMult/dailyDiamonds em cima do tier.
  },

  // 10.9J8 — AUDITORIA: o bônus de login era sempre o MESMO prêmio, todo dia, pra sempre — sem
  // nenhum motivo pra voltar especificamente amanhã (o gancho clássico de retenção D2). Sobe por 7
  // dias e, do 7º em diante, FICA no valor do dia 7 enquanto a sequência durar (não recomeça —
  // quem volta todo dia é premiado); pular 1 dia reseta pro dia 1 (ver Mail.ts checkDailyLogin).
  // Dia 1 igual ao valor antigo (30/5) pra não mudar a primeira impressão de ninguém.
  dailyLogin: [
    { coins: 30, diamonds: 5 },
    { coins: 40, diamonds: 5 },
    { coins: 50, diamonds: 8 },
    { coins: 70, diamonds: 8 },
    { coins: 90, diamonds: 10 },
    { coins: 120, diamonds: 12 },
    { coins: 200, diamonds: 20 }, // dia 7 em diante
  ],
  // 10.9K2 — Thiago: "resgate diário/semanal de diamantes pra quem logar todo dia". A cada 7 dias
  // SEGUIDOS (7, 14, 21…) chega este baú além do bônus do dia. Os Elixires de Proteção eram
  // vendidos só por 💎 — e acima do Nv~140 da forja eram praticamente obrigatórios; aqui o free
  // ganha jogando também.
  weeklyLoginChest: { diamonds: 30, coins: 500, elixirs: 2 },

  // 10.6U — LOJA DE ATRIBUTOS: comprar melhorias PERMANENTES (vida/dano/regen).
  // 3 tiers de crescimento por atributo. Aplica direto no GameProgress (absorbHp/Power/Regen).
  // 10.6Z — REBALANCEADO: valores 4× menores. Antes: +1200 HP no lv 9 quebrava o jogo
  // (player virava 20K HP e monstros não mordiam mais). Agora incremento é útil mas não deus.
  // 10.9J7 — AUDITORIA: custava 💎 (compra-se com dinheiro real) = poder de combate PERMANENTE
  // vendido direto por dinheiro, contradizendo a regra "VIP/loja = só conteúdo". Convertido pra
  // 🪙 Fragmentos (moeda que se GANHA jogando) pela taxa do antigo coinPack (removido na 10.9K2: 30💎=2000🪙,
  // ou seja 1💎≈66,7🪙) — os preços relativos entre os itens não mudam, só a moeda em si.
  // Tier "Mega" novo: dava pra esgotar essa loja cedo e ela ficar irrelevante no fim de jogo
  // (achado na auditoria); segue o mesmo padrão de crescimento (~3× o custo do Big, ligeiramente
  // melhor 🪙/unidade) — primeiro número, ajustar com feedback de jogo real.
  attrShop: {
    items: [
      // VIDA — permanente, empilha em absorbHp
      { id: "hpSmall", cost: 2000, kind: "hp", amount: 12, nameKey: "attr.hp.small", icon: "❤️" },
      { id: "hpMed", cost: 8000, kind: "hp", amount: 60, nameKey: "attr.hp.med", icon: "❤️" },
      { id: "hpBig", cost: 26700, kind: "hp", amount: 250, nameKey: "attr.hp.big", icon: "❤️" },
      { id: "hpMega", cost: 80000, kind: "hp", amount: 940, nameKey: "attr.hp.mega", icon: "❤️" },
      // DANO — permanente, empilha em absorbPower (dano base da mão)
      { id: "dmgSmall", cost: 2000, kind: "damage", amount: 1, nameKey: "attr.dmg.small", icon: "⚔️" },
      { id: "dmgMed", cost: 8000, kind: "damage", amount: 6, nameKey: "attr.dmg.med", icon: "⚔️" },
      { id: "dmgBig", cost: 26700, kind: "damage", amount: 22, nameKey: "attr.dmg.big", icon: "⚔️" },
      { id: "dmgMega", cost: 80000, kind: "damage", amount: 73, nameKey: "attr.dmg.mega", icon: "⚔️" },
      // REGEN — permanente, HP/s
      { id: "regenSmall", cost: 2670, kind: "regen", amount: 0.15, nameKey: "attr.regen.small", icon: "💚" },
      { id: "regenMed", cost: 10000, kind: "regen", amount: 0.6, nameKey: "attr.regen.med", icon: "💚" },
      // 10.9J6 — era amount:1.8 (277,8/un — PIOR que o Med, 250/un: quebrava o "comprar mais sai
      // mais barato" que hp/dano seguem certinho). 2.2 deixa o Big ~9% mais barato por unidade que
      // o Med, a mesma menor melhoria já usada entre dmgMed→dmgBig.
      { id: "regenBig", cost: 33300, kind: "regen", amount: 2.2, nameKey: "attr.regen.big", icon: "💚" },
      { id: "regenMega", cost: 100000, kind: "regen", amount: 7.3, nameKey: "attr.regen.mega", icon: "💚" },
    ],
  },

  // 10.6T — LOJA DE DIAMANTES: itens gastáveis com 💎 (uso contínuo do premium).
  // Categorias: baús (loot), boosters (buff temporário), consumíveis (fragmentos/shards),
  // skins exclusivas. Bônus VIP: preços com desconto por tier.
  // 10.9I — REORGANIZADA: a vantagem premium é SPEED + CONVENIÊNCIA + cosmético (justo),
  // não poder bruto. Os itens mais ESSENCIAIS (boosters temporários) vêm primeiro e baratos:
  // aceleram o progresso de quem compra SEM inflar stats permanentes (não "facilita demais").
  diamondShop: {
    items: [
      // BOOSTERS — a vantagem mais JUSTA: acelera por tempo limitado, some ao expirar.
      { id: "boostXp", cost: 30, kind: "booster", boosterType: "xp", duration: 1800, mult: 2, nameKey: "dshop.boostXp", icon: "⚡" , cat: "boost" as const },
      { id: "boostCoin", cost: 30, kind: "booster", boosterType: "coin", duration: 1800, mult: 2, nameKey: "dshop.boostCoin", icon: "💰" , cat: "boost" as const },
      { id: "boostLuck", cost: 45, kind: "booster", boosterType: "luck", duration: 1800, mult: 1, nameKey: "dshop.boostLuck", icon: "🍀" , cat: "boost" as const },
      { id: "boostCombo", cost: 110, kind: "booster", boosterType: "combo", duration: 3600, mult: 2, nameKey: "dshop.boostCombo", icon: "🌟" , cat: "boost" as const },
      // CONVENIÊNCIA — troca 💎 por recursos do jogo (atalho, não poder).
      // 10.9K2 — AUDITORIA DE JUSTIÇA: "coinPack" (30💎 = 2.000🪙) removido. A Loja de Atributos
      // cobra 🪙 e vende HP/dano PERMANENTE que a Ameaça não enxerga, sem limite — com o pacote, isso
      // virava dinheiro → 💎 → 🪙 → poder de combate, contra a regra "só conteúdo". Agora 🪙 só
      // vem jogando (VIP/passe/booster ainda aceleram, com teto de 4×).
      { id: "shardPack", cost: 25, kind: "shards", amount: 20, nameKey: "dshop.shardPack", icon: "✨" , cat: "resource" as const },
      // 10.9K9 — a "Fusão Automática" (150💎, 10.9K5) saiu: a evolução por cópias já é automática pra
      // todo mundo. Quem comprou recebe os 150💎 de volta pelo Correio (Game.postLoadSetup).
      // 10.9HA — MOCHILA: +10 espaços por compra (30 → 100). Preço sobe 20💎 a cada compra (40, 60 … 160).
      { id: "bagSlots", cost: 40, kind: "bag", amount: 10, nameKey: "dshop.bagSlots", icon: "🎒" , cat: "resource" as const },
      // 10.9O — ELIXIR DE PROTEÇÃO: impede a arma/equip. CAIR de nível quando a forja falha.
      // É a vantagem justa de quem paga (forja mais longe sem medo), sem inflar stats de graça.
      { id: "elixir3", cost: 120, kind: "elixir", amount: 3, nameKey: "dshop.elixir3", icon: "🧪" , cat: "protect" as const },
      { id: "elixir10", cost: 350, kind: "elixir", amount: 10, nameKey: "dshop.elixir10", icon: "⚗️" , cat: "protect" as const },
      // 10.9AD — BÊNÇÃO (Bless): ao MORRER, protege contra a perda de um item da bolsa (consome 1).
      { id: "bless1", cost: 40, kind: "bless", amount: 1, nameKey: "dshop.bless1", icon: "🕊️" , cat: "protect" as const },
      { id: "bless5", cost: 170, kind: "bless", amount: 5, nameKey: "dshop.bless5", icon: "🕊️" , cat: "protect" as const },
      // BAÚS — loot com RNG melhor + CHANCE de arma de boss nos grandes (10.9O).
      { id: "chestRare", cost: 50, kind: "chest", rarityMin: 2, nameKey: "dshop.chestRare", icon: "🎁" , cat: "chest" as const },
      { id: "chestEpic", cost: 180, kind: "chest", rarityMin: 3, nameKey: "dshop.chestEpic", icon: "🎁" , cat: "chest" as const },
      { id: "chestLegend", cost: 700, kind: "chest", rarityMin: 4, nameKey: "dshop.chestLegend", icon: "🎁" , cat: "chest" as const },
      { id: "chestMythic", cost: 2200, kind: "chest", rarityMin: 5, nameKey: "dshop.chestMythic", icon: "🏆" , cat: "chest" as const },
      // COSMÉTICO — zero vantagem, só estilo.
      { id: "outfitGold", cost: 500, kind: "outfit", outfitId: "gold", nameKey: "dshop.outfitGold", icon: "👑" , cat: "cosmetic" as const },
      // 10.9J8 — AUDITORIA: de 10 roupas no jogo, só 1 era vendida por 💎 (e essa 1 tinha o bug do
      // preço zerado — corrigido na 10.9J6). Coleção cosmética é o gancho de gasto mais seguro
      // dentro da regra "VIP/loja = só conteúdo": zero efeito em combate, só quem paga tem.
      { id: "outfitObsidian", cost: 400, kind: "outfit", outfitId: "obsidian", nameKey: "dshop.outfitObsidian", icon: "🖤" , cat: "cosmetic" as const },
      { id: "outfitRoyal", cost: 650, kind: "outfit", outfitId: "royal", nameKey: "dshop.outfitRoyal", icon: "👑" , cat: "cosmetic" as const },
    ],
    // desconto por tier VIP (0=sem, 1=Bronze -5%, 2=Prata -10%, 3=Ouro -15%)
    vipDiscountPct: [0, 5, 10, 15, 20, 25, 30], // 10.9BG: 6 tiers
  },

  // 10.6S — PACOTES DE DIAMANTES: comprados com DINHEIRO REAL (R$). UI pronta;
  // integração Google Play Billing será plugada depois (não implementada aqui).
  // Bônus VIP: quanto maior o tier, mais diamantes-bônus por pacote.
  // 10.9J — CURVA DE VALOR MODERADA (ajuste do Thiago): pacote maior rende só um POUCO mais por
  // real (bônus sobe de leve, teto ~20% — "ganhar menos não mais, 15-20% tá ótimo"). A vantagem
  // do gastador vem de COMPRAR MAIS (tiers até R$500), não de um desconto agressivo por real.
  // `bonusPct` = bônus embutido vs o pacote inicial (badge, mostrado a todos). `diamonds` = TOTAL.
  // `tag` destaca POPULAR e MELHOR VALOR. Bônus VIP (vipBonusPct) modesto: até +20% no Ouro.
  //   ~20💎/R$ no inicial → ~24💎/R$ no topo (só ~+20%)
  // 10.9K3 — Thiago: "valores acessíveis e atrativos". A escada começava em R$5,49 e usava preços
  // quebrados (5,49 / 12,99 / 26,99). Agora: entrada de R$1,99 (o "primeiro gasto" mais fácil — e
  // com a 1ª compra em dobro vira 80💎) e só preços redondos de loja (x,99). A curva continua a do
  // 10.9J (base 20💎/R$ → +20% no topo) e NENHUM pacote rende menos por real que o antigo de preço
  // parecido. `bonusPct` = bônus real vs 20💎/R$, arredondado PRA BAIXO (nunca promete a mais).
  // O "titan" (R$299,99) saiu pra grade de 2 colunas fechar em 8; o topo segue no Lendário R$499,99.
  // 10.9L10 — Thiago: "comprar 12 mil diamantes não tem que nunca dar o dobro, no máximo 30%".
  // O pacote Lendário dobrado na 1ª compra virava 24.000 💎 de uma vez (era isso que ele viu e
  // achou "muito"). `firstBonusPct` deixa o bônus da 1ª compra CONFIGURÁVEL por pacote — omitido
  // = 100 (dobro, como sempre foi pros pacotes menores); o Lendário agora é só +30%.
  diamondPacks: [
    { id: "mini",     diamonds: 40,    priceBRL: 1.99,   bonusPct: 0,  tag: "",        nameKey: "vip.pack.mini",     vipBonusPct: [0, 10, 15, 20, 25, 30, 40] },
    { id: "starter",  diamonds: 105,   priceBRL: 4.99,   bonusPct: 5,  tag: "",        nameKey: "vip.pack.starter",  vipBonusPct: [0, 10, 15, 20, 25, 30, 40] },
    { id: "small",    diamonds: 215,   priceBRL: 9.99,   bonusPct: 7,  tag: "",        nameKey: "vip.pack.small",    vipBonusPct: [0, 10, 15, 20, 25, 30, 40] },
    { id: "medium",   diamonds: 440,   priceBRL: 19.99,  bonusPct: 10, tag: "popular", nameKey: "vip.pack.medium",   vipBonusPct: [0, 10, 15, 20, 25, 30, 40] },
    { id: "large",    diamonds: 1125,  priceBRL: 49.99,  bonusPct: 12, tag: "",        nameKey: "vip.pack.large",    vipBonusPct: [0, 10, 15, 20, 25, 30, 40] },
    { id: "mega",     diamonds: 2300,  priceBRL: 99.99,  bonusPct: 15, tag: "",        nameKey: "vip.pack.mega",     vipBonusPct: [0, 10, 15, 20, 25, 30, 40] },
    { id: "ultimate", diamonds: 4700,  priceBRL: 199.99, bonusPct: 17, tag: "",        nameKey: "vip.pack.ultimate", vipBonusPct: [0, 10, 15, 20, 25, 30, 40] },
    { id: "legend",   diamonds: 12000, priceBRL: 499.99, bonusPct: 20, tag: "best",    nameKey: "vip.pack.legend",   vipBonusPct: [0, 10, 15, 20, 25, 30, 40], firstBonusPct: 30 },
  ],

  // BOSS DE EVENTO (10.4R): periodicamente um boss buffado SURGE no campo. Reusa a IA do
  // Void Alpha (arena por instância, perto do player), mais tanky e mais forte, e larga
  // loot EXCLUSIVO de boss + baú premium. Só no campo (fora da Sanctuary, sem outro boss).
  // 10.9CE — DESLIGADO. Era o "boss que nasce sozinho": 90 s depois de entrar no campo e a cada 4 min
  // um Alfa buffado caía em cima do player (Thiago: "mata 5 monstros e nasce o boss"). O boss de
  // campo agora é SÓ o Guardião (areaBoss: arena fixa, carregado por abates) + a Invasão de 4h.
  eventBoss: {
    enabled: false,
    firstDelay: 90, // s até o 1º evento (após entrar no campo)
    interval: 240, // s entre eventos
    requireAlpha: true, // só depois de derrotar o Void Alpha pela 1ª vez (jogador já sabe lutar boss)
    hpMult: 1.6, // +60% de vida (luta mais longa)
    dmgMult: 1.25, // +25% de dano
    weaponRarity: "legendary" as const, // raridade da arma exclusiva garantida
    essence: { power: 14, vitality: 10, ferocity: 8 },
    xp: 120,
  },

  // 10.6P — BOSS DE ÁREA (Guardião): cada região tem o seu, aparece a cada 1h de jogo naquela
  // região, e fica mais forte a cada kill (compounding). Reusa a IA do VoidAlpha com escala.
  // 10.9CC — ARENA DO GUARDIÃO. Thiago: "boss que aparece quando matamos monstros... essa mecânica
  // não tá boa; preciso de uma área exclusiva onde o boss nasça quando matar os monstros, e a
  // contagem tá muito pouca". O boss não nasce mais por timer em cima do player: cada região tem
  // UMA arena fixa (território abaixo); abates na região CARREGAM o guardião; cheio → desperta e
  // espera na arena (rótulo + marcador no mapa); nasce só quando o player entra e aceita.
  areaBoss: {
    enabled: true,
    arenaTerritory: { forgottenForest: "t_crest", shatteredWilds: "t_spires", tempestReach: "t_skyshelf" } as Record<string, string>,
    arenaRadius: 12,
    killsToAwaken: 80,     // abates na região pra despertar o 1º guardião
    killsPerAwaken: 25,    // +25 por guardião já derrotado na região (mais raro E mais forte)
    killsCap: 250,
    baseHpMult: 2.0,     // base: 2x o VoidAlpha padrão
    baseDmgMult: 1.5,    // base: 1.5x
    scalePerKill: 0.25,  // +25% HP/DMG por kill anterior (compounding)
    maxScale: 8.0,       // teto de multiplicador (base × maxScale)
    xp: 200,
    essence: { power: 20, vitality: 15, ferocity: 12 },
    weaponRarity: "legendary" as const, // 10.9CE: era epic — o Guardião ficou raro (80+ abates), herda o loot do boss de evento
  },

  // ===================== 10.9AC — INVASÃO DO VAZIO (evento a cada 4h) =====================
  // A cada 4h REAIS (relógio de parede, sobrevive a fechar o app) abre uma janela de evento:
  //  · monstros CORROMPIDOS (buffados) invadem ao redor do player;
  //  · CADA corrompido morto dá +dano e +defesa TEMPORÁRIOS (stacks que SÓ existem no evento);
  //  · matar corrompidos suficientes LIBERA um boss (por isso "precisa matar os monstros pra
  //    poder matar o boss"); o boss é DIFERENTE a cada aparição (variantes) e RENASCE MAIS FORTE
  //    a cada vez que morre (tier++); quando o tempo acaba, os buffs somem e tudo volta ao normal.
  worldEvent: {
    enabled: true,
    requireAlpha: true, // 10.9FO — OFICIAL: só depois de derrotar o Alpha (é conteúdo de fim de jogo)
    firstDelay: 1800,    // 10.9FO — OFICIAL: 30 min após ficar elegível (era 120s de teste)
    interval: 4 * 3600,  // 4h reais entre eventos (a partir do FIM do evento anterior)
    duration: 2 * 3600,  // 10.9BF — 2h de janela (Thiago: "ele tem 2h pra finalizar o evento"; era 5 min)
    monster: {
      hpMult: 1.7,       // corrompidos são mais tanky
      dmgMult: 1.45,     // e batem mais forte (é uma invasão)
      maxAlive: 9,       // horda simultânea viva ao redor do player
      spawnInterval: 1.0, // cadência de reposição (s)
      ringMin: 9,        // nascem num anel de 9..18 m do player
      ringMax: 18,
    },
    buff: {
      perKillDamage: 0.05,   // +5% de dano por corrompido morto
      perKillDefense: 0.025, // +2.5% de redução de dano por corrompido morto
      maxStacks: 24,         // teto de acúmulo (24 → +120% dano)
      defenseCap: 0.55,      // a redução do buff nunca passa de 55%
    },
    boss: {
      unlockKills: 40,       // 10.9CI — Thiago: "5 monstros e nasce boss é pouco; uns 40" (era 6)
      unlockKillsPerTier: 8, // +8 por renascimento (era +2) → 40, 48, 56… ~7-8 bosses nas 2h
      baseHpMult: 2.6,       // vida base do boss do evento (× VoidAlpha padrão)
      baseDmgMult: 1.2,      // dano base (10.9BF: era 1.5 — com FÚRIA e +tiers virava one-shot)
      hpPerTier: 0.35,       // 10.9BF: era 0.7 — evento de 2h: ~12-15 bosses (lutas de 7-11 min no fim), não 9-12 de 15 min
      dmgPerTier: 0.08,      // 10.9BF: era 0.18 — no tier 10 o pesado em fúria tira ~50% da vida, não 90%
      maxTier: 30,           // trava de segurança
      // "boss diferente toda vez": ciclo de variantes (nome + cor + ênfase de atributo)
      // 10.9AT — cada variante tem MORFOLOGIA própria (alphaForm/seerForm) — silhueta, não só cor
      variants: [
        { key: "wrath",  tint: 0xff4d4d, hp: 1.0,  dmg: 1.12, alphaForm: { hornLen: 1.8, plates: true }, seerForm: { rings: 3, eyeScale: 1.3 } },
        { key: "frost",  tint: 0x59d0ff, hp: 1.18, dmg: 0.95, alphaForm: { crown: 6, bodyScale: [1.1, 1.05, 1.1] }, seerForm: { shards: 9, halo: true } },
        { key: "plague", tint: 0x8bff6b, hp: 1.28, dmg: 0.9,  alphaForm: { tailBlades: 5, bodyScale: [1.15, 0.95, 1.2] }, seerForm: { tentacles: 6 } },
        { key: "storm",  tint: 0xc48bff, hp: 0.95, dmg: 1.22, alphaForm: { wings: true }, seerForm: { rings: 4, shards: 3 } },
        { key: "abyss",  tint: 0xff8a3b, hp: 1.1,  dmg: 1.05, alphaForm: { wings: true, crown: 4, hornLen: 1.4 }, seerForm: { tentacles: 8, eyeScale: 1.5, halo: true } },
      ] as { key: string; tint: number; hp: number; dmg: number; alphaForm: import("../boss/BossForms").AlphaForm; seerForm: import("../boss/BossForms").SeerForm }[],
    },
    reward: {
      diamondsPerBoss: 0,    // 10.9BK: era 12 — boss paga 🪙 (bossCoins.eventBoss)
      diamondsPerTier: 0,    // 10.9BK: era 4
      xpPerBoss: 220,
      essence: { power: 16, vitality: 12, ferocity: 10 },
      weaponRarity: "epic" as const, // chance de arma exclusiva no baú do boss
    },
  },

  // ===================== 10.9AJ — ZONA DOS BOSSES ("Fenda dos Titãs") =====================
  // Portal no Santuário teleporta pra uma grade de SALAS, cada uma com um boss e PORTA por nível.
  // Farm de itens permitido (cooldown por sala); a ARMA exclusiva do boss é quase impossível (1.5%).
  bossZone: {
    enabled: true,
    // 10.9AM — FORA DO MAPA: além dos limites de qualquer região (o maior é ±310), então NÃO dá
    // pra chegar andando — só pelo portal. Lá, o clamp do mundo usa `bounds` (quadrado da zona).
    center: { x: 0, z: 420 },
    bounds: { half: 60 },            // quadrado da zona: x∈[-60,60], z∈[360,480] (chão/borda próprios)
    portalHub: HUB.bossPortal,       // no Santuário — 10.9BP: posição vem do layout do hub (sudeste)
    arrive: { x: 0, z: 382 },        // onde o player aparece (sul da grade; salas fáceis ficam perto)
    exitPortal: { x: 0, z: 375 },    // portal de volta
    roomSize: 14, gap: 5, cols: 5,   // 10.9HU: era 4×3→10 salas; agora 5×3→15 salas (r11-r15 = PRESTÍGIO)
    arenaRadius: 6.4,                // círculo de luta dentro da sala (boss e player ficam dentro)
    respawnSeconds: 15 * 3600,       // 10.9BU — Thiago: 15h entre lutas na mesma sala (era 10 min)
    weaponDropChance: 0.015,         // "quase impossível": 1.5% por abate
    // 10.9HX — Thiago: "seria bom ter uns monstros rodando por aqui tbm". População PEQUENA e fixa
    // rondando os CORREDORES (nunca dentro das salas, pra não atrapalhar a luta de boss). Teto baixo
    // de propósito: a Fenda também roda em celular fraco.
    ambient: { maxAlive: 8, interval: 2.2, minFromPlayer: 12, ids: ["voidling", "voidRunner", "voidWisp"] as string[] },
    weaponRarity: "legendary" as const,
    rooms: [
      // 10.9AT — cada sala tem MORFOLOGIA própria (form): silhuetas diferentes, não só cor
      { id: "r1",  archetype: "alpha", nameKey: "bossZone.r1",  tint: 0xff6a3d, level: 10,  hpMult: 1.4, dmgMult: 1.15, form: { hornLen: 1.7 } },
      { id: "r2",  archetype: "seer",  nameKey: "bossZone.r2",  tint: 0x62b7ff, level: 18,  hpMult: 1.5, dmgMult: 1.2,  form: { rings: 3, shards: 7 } },
      { id: "r3",  archetype: "alpha", nameKey: "bossZone.r3",  tint: 0x8bff6b, level: 26,  hpMult: 1.9, dmgMult: 1.3,  form: { plates: true, tailBlades: 4, bodyScale: [1.15, 0.95, 1.2] } },
      { id: "r4",  archetype: "seer",  nameKey: "bossZone.r4",  tint: 0xc48bff, level: 35,  hpMult: 2.1, dmgMult: 1.4,  form: { halo: true, shards: 10, eyeScale: 0.8 } },
      { id: "r5",  archetype: "alpha", nameKey: "bossZone.r5",  tint: 0xffd23d, level: 45,  hpMult: 2.6, dmgMult: 1.55, form: { crown: 8, hornLen: 2.1, bodyScale: [1.1, 1.1, 1.1] } },
      { id: "r6",  archetype: "seer",  nameKey: "bossZone.r6",  tint: 0x3dffd2, level: 55,  hpMult: 2.9, dmgMult: 1.7,  form: { tentacles: 7, rings: 1 } },
      { id: "r7",  archetype: "alpha", nameKey: "bossZone.r7",  tint: 0xff3d7a, level: 65,  hpMult: 3.5, dmgMult: 1.9,  form: { wings: true, tailBlades: 3 } },
      { id: "r8",  archetype: "seer",  nameKey: "bossZone.r8",  tint: 0x7a3dff, level: 75,  hpMult: 4.0, dmgMult: 2.1,  form: { rings: 4, tentacles: 4, shards: 3 } },
      { id: "r9",  archetype: "alpha", nameKey: "bossZone.r9",  tint: 0xffffff, level: 85,  hpMult: 4.8, dmgMult: 2.4,  form: { wings: true, crown: 6, plates: true, hornLen: 1.5, bodyScale: [1.25, 1.2, 1.25] } },
      { id: "r10", archetype: "seer",  nameKey: "bossZone.r10", tint: 0xff2222, level: 100, hpMult: 5.8, dmgMult: 2.8,  form: { rings: 5, shards: 12, tentacles: 10, eyeScale: 1.8, halo: true } },
      // 10.9HU — SALAS DE PRESTÍGIO (Thiago: "áreas específicas com missões bem difíceis pra motivar
      // o jogador"): além do nível, exigem uma FRAÇÃO da Árvore do Void fechada (`treePct`, sobre
      // `VoidTreeState.totalMaxPoints`). SEM tier de item novo (decisão do Thiago) — a recompensa é
      // `dropChance` bem maior do Ômega (T14) já existente, não poder além dele.
      { id: "r11", archetype: "alpha", nameKey: "bossZone.r11", tint: 0xffcf5c, level: 200, hpMult: 7.2,  dmgMult: 3.1, treePct: 0.25, dropChance: 0.05, form: { wings: true, crown: 10, plates: true, hornLen: 1.8, tailBlades: 5, bodyScale: [1.3, 1.25, 1.3] } },
      { id: "r12", archetype: "seer",  nameKey: "bossZone.r12", tint: 0xe8d4ff, level: 400, hpMult: 9.0,  dmgMult: 3.4, treePct: 0.50, dropChance: 0.08, form: { rings: 6, shards: 14, tentacles: 12, eyeScale: 2.0, halo: true } },
      { id: "r13", archetype: "alpha", nameKey: "bossZone.r13", tint: 0xff0044, level: 600, hpMult: 11.5, dmgMult: 3.8, treePct: 0.70, dropChance: 0.12, form: { wings: true, crown: 12, plates: true, hornLen: 2.0, tailBlades: 6, bodyScale: [1.4, 1.3, 1.4] } },
      { id: "r14", archetype: "seer",  nameKey: "bossZone.r14", tint: 0xbdfaff, level: 800, hpMult: 14.5, dmgMult: 4.3, treePct: 0.90, dropChance: 0.16, form: { rings: 7, shards: 16, tentacles: 14, eyeScale: 2.2, halo: true } },
      { id: "r15", archetype: "alpha", nameKey: "bossZone.r15", tint: 0xfff3c4, level: 1000, hpMult: 18,   dmgMult: 4.8, treePct: 1.00, dropChance: 0.25, form: { wings: true, crown: 16, plates: true, hornLen: 2.4, tailBlades: 8, bodyScale: [1.5, 1.4, 1.5] } },
    ] as { id: string; archetype: "alpha" | "seer"; nameKey: string; tint: number; level: number; hpMult: number; dmgMult: number; treePct?: number; dropChance?: number; form?: import("../boss/BossForms").AlphaForm & import("../boss/BossForms").SeerForm }[],
    // recompensa por abate: XP (escala com o nível da sala), moedas, essência e um baú "alpha"
    // (loot normal — NÃO é dos baús premium que têm chance alta de arma de boss).
    reward: { xpBase: 260, xpPerLevel: 6, coins: 90, essence: { power: 18, vitality: 14, ferocity: 12 } },
  },

  // LOJA + MOEDA (10.4Y): "Fragmentos" (moeda GANHA jogando — NÃO é dinheiro real) gastos
  // na Loja em itens que ajudam na evolução. Preço escala por nº de compras (priceScale^n).
  potion: { healFrac: 0.5, cooldown: 8 }, // 10.9CZ — poção cura 50% da vida máx., recarga 8 s
  shop: {
    // 10.6H — MOEDA MAIS DURA. Estava fácil demais encher o bolso: monstro comum pagava 2
    // e as missões despejavam centenas, então a Loja perdia o sentido em poucos minutos.
    // Corte de ~60% no ganho por abate e nas recompensas de missão (ver missions.templates).
    // 10.9AS — "tá ganhando gold muito fácil": corte geral (~50%) nas fontes automáticas
    coinsPerKill: 1,
    coinsPerElite: 5,
    coinsPerBoss: 30,
    coinsPerEventBoss: 40,
    coinsPerChest: 8,
    // 10.5E — balance: cura menos spammável; reforços valem mais por compra (escala segura
    // porque a Ameaça Crescente impede imortalidade). Preço sobe por compra (priceScale^n).
    items: [
      // 10.9DH — organizada por CATEGORIA (cat) — a UI agrupa com títulos de seção.
      // CONSUMÍVEIS
      { id: "potion", icon: "🧪", priceBase: 40, priceScale: 1.12, kind: "potion" as const, amount: 3, cat: "consumable" as const },
      { id: "heal", icon: "❤️", priceBase: 45, priceScale: 1.0, kind: "heal" as const, amount: 0, cat: "consumable" as const },
      { id: "shards", icon: "🔷", priceBase: 45, priceScale: 1.18, kind: "shards" as const, amount: 6, cat: "consumable" as const },
      { id: "forge", icon: "🛠️", priceBase: 95, priceScale: 1.22, kind: "forge" as const, amount: 15, cat: "consumable" as const },
      // IMPULSOS (temporários)
      { id: "contract", icon: "📜", priceBase: 70, priceScale: 1.06, kind: "contract" as const, amount: 300, cat: "boost" as const },
      { id: "hunt", icon: "🎯", priceBase: 60, priceScale: 1.08, kind: "hunt" as const, amount: 1, cat: "boost" as const },
      // PERMANENTES
      { id: "hp", icon: "🛡️", priceBase: 180, priceScale: 1.55, kind: "hp" as const, amount: 10, cat: "permanent" as const }, // 10.9DK: atributo de MOEDA e fraco e caro (diamante e o forte)
      { id: "power", icon: "⚔️", priceBase: 200, priceScale: 1.55, kind: "power" as const, amount: 2, cat: "permanent" as const }, // 10.9DK
      // ANÉIS
      { id: "ringEmber", icon: "🔥", priceBase: 320, priceScale: 1.0, kind: "ring" as const, amount: 0, cat: "rings" as const },
      { id: "ringFrost", icon: "❄️", priceBase: 320, priceScale: 1.0, kind: "ring" as const, amount: 0, cat: "rings" as const },
      { id: "ringGale", icon: "🌪️", priceBase: 360, priceScale: 1.0, kind: "ring" as const, amount: 0, cat: "rings" as const },
      { id: "ringStorm", icon: "⚡", priceBase: 420, priceScale: 1.0, kind: "ring" as const, amount: 0, cat: "rings" as const },
      // COMPANHEIRO
      { id: "pet", icon: "🐾", priceBase: 220, priceScale: 1.0, kind: "pet" as const, amount: 0, cat: "pet" as const },
    ],
    contract: { duration: 300, coinMult: 1.75, essenceMult: 1.75 }, // 10.5L — Contrato de Caça
  },

  // ROUPAS / SKINS (10.4Z): cosmético — recolore o Hunter. Compradas com Fragmentos.
  // "void" é grátis (padrão). Léxico permitido (aether/tempest/ember/astral).
  outfits: [
    { id: "void", price: 0, palette: { armor: 0x6a5bd6, armorEmissive: 0x3a1d80, cloak: 0x3a2f6e, accent: 0x9d7bff, accentEmissive: 0x6a3bff, crystal: 0x8fe8ff, crystalEmissive: 0x39b8ff } },
    { id: "aether", price: 180, palette: { armor: 0xc9a94e, armorEmissive: 0x5a4210, cloak: 0x6e5a24, accent: 0xffe08a, accentEmissive: 0xffcf5a, crystal: 0xfff2c0, crystalEmissive: 0xffd76a } },
    { id: "tempest", price: 180, palette: { armor: 0x2f8f7a, armorEmissive: 0x0f4a3a, cloak: 0x1d5c4e, accent: 0x6cf0c8, accentEmissive: 0x2fe0b0, crystal: 0xbfffe8, crystalEmissive: 0x40ffd0 } },
    { id: "ember", price: 240, palette: { armor: 0xb0503a, armorEmissive: 0x5a2410, cloak: 0x6b3020, accent: 0xff9a5a, accentEmissive: 0xff6a2a, crystal: 0xffd0a0, crystalEmissive: 0xff8a40 } },
    { id: "astral", price: 300, palette: { armor: 0xb0457a, armorEmissive: 0x5a1046, cloak: 0x5e2050, accent: 0xff7bd0, accentEmissive: 0xff4dbb, crystal: 0xffc0ec, crystalEmissive: 0xff70d0 } },
    // 10.9J6 — BUG (auditoria): "gold" tinha price:0 pra funcionar como "já é sua, só equipar" DEPOIS
    // de comprada na Loja VIP (outfitGold, 500💎) — mas o Guarda-Roupa trata price:0 como "grátis"
    // igual o "void" (o padrão de todo mundo), deixando qualquer jogador equipar sem nunca ter
    // comprado. `exclusive: true` faz o Guarda-Roupa e o Game.chooseOutfit recusarem "comprar" essa
    // roupa por conta própria — só entra na posse via buyDiamondItem (Loja VIP), como sempre foi a intenção.
    { id: "gold", price: 0, exclusive: true, palette: { armor: 0xffd76a, armorEmissive: 0xa06f10, cloak: 0x8a5c0a, accent: 0xfff2b0, accentEmissive: 0xffdd60, crystal: 0xfff8d0, crystalEmissive: 0xffcf4a } },
    // 10.9J8 — 2 roupas exclusivas NOVAS da Loja de Diamantes (ver diamondShop.items acima).
    { id: "obsidian", price: 0, exclusive: true, palette: { armor: 0x1a1a24, armorEmissive: 0x0a0a14, cloak: 0x0f0f18, accent: 0x4affff, accentEmissive: 0x1adfff, crystal: 0xc0ffff, crystalEmissive: 0x4affff } },
    { id: "royal", price: 0, exclusive: true, palette: { armor: 0x5c1020, armorEmissive: 0x2a0810, cloak: 0x3a0a14, accent: 0xffb020, accentEmissive: 0xff8a10, crystal: 0xffe8b0, crystalEmissive: 0xffb020 } },
    // 10.9K3 — roupa do PASSE VIP (vipPass.outfitId): entra na posse ao ativar o passe (buyVipPass).
    // `lockKey` troca o aviso de cadeado (a padrão fala da Loja de 💎, que não vende esta).
    { id: "aurora", price: 0, exclusive: true, lockKey: "wardrobe.exclusivePass", palette: { armor: 0x243a5e, armorEmissive: 0x0c1a3a, cloak: 0x1a1438, accent: 0xb07dff, accentEmissive: 0x8a4dff, crystal: 0x9dffc8, crystalEmissive: 0x3affa0 } },
    // 10.9HB — roupas das LINHAGENS de boss (cosmético, 🪙)
    { id: "eclipse", price: 320, palette: { armor: 0x2a2030, armorEmissive: 0x3a1a08, cloak: 0x1a1220, accent: 0xff9a3c, accentEmissive: 0xff7a1a, crystal: 0xffd2a0, crystalEmissive: 0xff9a3c } },
    { id: "sideral", price: 360, palette: { armor: 0x3a5a8a, armorEmissive: 0x123a6a, cloak: 0x1e2e4e, accent: 0x9fe6ff, accentEmissive: 0x62e8ff, crystal: 0xe6fbff, crystalEmissive: 0x9fe6ff } },
    { id: "abyssal", price: 420, palette: { armor: 0x4a1a2a, armorEmissive: 0x3a0a18, cloak: 0x1e0c16, accent: 0xff4d6d, accentEmissive: 0xff2a50, crystal: 0xffc0cc, crystalEmissive: 0xff4d6d } },
    { id: "omega", price: 480, palette: { armor: 0xf0eefc, armorEmissive: 0x8a7a40, cloak: 0xd8d2ee, accent: 0xfff2c8, accentEmissive: 0xffe08a, crystal: 0xffffff, crystalEmissive: 0xfff2c8 } },
  ],

  // ABSORÇÃO (10.4O): cada monstro morto melhora o boneco. Escala com o poder do monstro
  // (maxHp/damage) e VARIA por "shape": uns curam, uns dão +vida máxima, uns dão +dano.
  // Elite/boss valem eliteMult. hp/power são PERMANENTES (salvos em GameProgress).
  absorb: {
    hpFromMaxHp: 0.006, // +maxHP permanente = 0.6% do HP do monstro (min 1)
    powerFromDamage: 0.05, // +dano permanente = 5% do dano do monstro (min 1)
    healPctOfPlayerMax: 0.005, // cura instantânea = 0.5% do HP máx do player
    eliteMult: 4,
    byShape: {
      orb: "heal", spark: "heal", flyer: "heal",
      runner: "power", beast: "power", strider: "power", stalker: "power",
      brute: "hp", heavy: "hp", bulwark: "hp", shell: "hp",
      warden: "all", sentinel: "all",
    } as Record<string, "heal" | "hp" | "power" | "all">,
  },

  // AMEAÇA CRESCENTE (10.5B): anti-imortalidade. Os inimigos escalam vida/dano conforme o
  // poder do player, mas FICAM ATRÁS (lag < 1) — o player continua evoluindo e mais forte,
  // porém o combate nunca vira trivial. baseHp/baseDamage = stats de nível 1 (referência).
  // 10.9EO — RAID (boss mundial). O BOSS e do SERVIDOR (server/raid): vida unica, agenda, ranking,
  // anti-trapaca. Aqui fica so o que o CELULAR precisa: onde fica a arena, o ritmo de envio e os
  // golpes do Tita (sequencia roteirizada pela semente do horario + relogio do servidor).
  raid: {
    // Sem URL = raid desligada (ex.: site publicado antes de subir o servidor). Em DEV usa o local.
    // 10.9GC — `||` (nao `??`): o build passa VITE_RAID_URL="" quando o segredo nao existe, e "" nao
    // dispara o `??`. Assim a URL fixa do Worker publicado vale sempre que o segredo estiver vazio.
    url: ((import.meta.env.VITE_RAID_URL as string) || (import.meta.env.DEV ? "http://127.0.0.1:8787" : "https://void-hunter-raid.thiago-mirandahs.workers.dev")),
    // 10.9IQ — HORARIOS FIXOS (hora de Brasilia). Thiago: "coloque pro boss global aparecer nas
    // horas certas pre-definidas". Estava aberto as 24 horas com janela de 58 min, que era o
    // modo de TESTE: na pratica o boss ficava o tempo todo disponivel e ninguem se encontrava.
    // Um boss MUNDIAL so funciona se todo mundo cair junto — as salas sao de 40 e a vida do
    // boss cresce com quem entra, entao poucos horarios densos valem mais que 24 vazios.
    // Thiago: "coloca pra ele aparecer a cada 2h no jogo, ficaria melhor ne" — DE 2 EM 2 HORAS,
    // nas horas pares. Ninguem espera mais que duas horas pra pegar uma, e ainda assim as janelas
    // sao poucas e curtas o bastante pra juntar gente na mesma sala.
    // PRA MUDAR: troque a lista aqui E o RAID_HOURS_BRT do server/raid/wrangler.toml — o
    // servidor e a autoridade, e um slot fora da lista dele e recusado.
    hoursBRT: [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22],
    windowMin: 20, // a luta dura ~5-8 min; 20 da tempo de entrar atrasado e ainda lutar
    lobbyMin: 3,
    // 10.9IQ — 25, era 45. O aviso foi calibrado quando a raid era de hora em hora com janela de
    // 58 min (ou seja, sempre aberta). Com janelas de 2 em 2 horas, 45 min de antecedencia + 20
    // de janela deixariam o convite na tela 57% do tempo, e ele e um botao que nunca recolhe.
    // 25 min avisa com folga pra largar o que esta fazendo e some o resto do tempo.
    announceMin: 25, // o HUD mostra a contagem regressiva nos ultimos N minutos
    arena: { x: 0, z: -1400, r: 26 }, // FORA do mapa (maior regiao e ±310; a Fenda fica em z=+420)
    sendDmgEvery: 2, // s entre pacotes de dano (1000 jogadores -> 500 pacotes/s no servidor)
    sendPosHz: 4,
    respawnDelay: 3, // morrer na raid NAO tem penalidade: volta em 3s na borda da arena
    titan: {
      radius: 3.4,
      period: 4.5, // 10.9GF — mais frequente (era 5,5s): boss global forte
      // % da VIDA MAXIMA do jogador por golpe (antes da armadura) — assusta qualquer build igual
      slam: { windup: 1.3, radius: 4.8, pct: 0.42, knockback: 10 }, // 10.9GF — Punho do Eclipse mais forte
      nova: { windup: 1.8, radius: 11, pct: 0.45, knockback: 14 }, // Explosao Solar: FUJA do Tita
      donut: { windup: 2.0, safe: 8, pct: 0.45, knockback: 10 }, // Anel do Eclipse: CHEGUE perto
      meteors: { windup: 1.5, radius: 3.4, count: 8, countP2: 13, pct: 0.35, knockback: 7 }, // 10.9GF — Chuva Estelar mais densa/forte
      phase2: { hpBelow: 0.5, windupMult: 0.8 },
    },
    reward: {
      xpFracOfLevel: 0.4, // XP = 40% do que falta pro nivel (participou e venceu)
      coinsPerLevel: 30,
      diamonds: 8,
      topDiamonds: [40, 30, 25, 20, 18, 16, 14, 12, 11, 10], // bonus do 1o ao 10o
      failFrac: 0.25, // perdeu (tempo acabou): 25% do XP/moedas, sem diamante/bau
    },
  },

  threat: {
    enabled: true,
    baseHp: 100,
    baseDamage: 10,
    dmgLag: 1.05, // 10.9FV: Thiago "monstros fracos pros players fortes" — batem um pouco ACIMA (era 0.92)
    // 10.9GP — PESO DA ABSORÇÃO DOS ABATES na Ameaça. 1 = os monstros acompanham cada abate 1:1
    // (medido no Nv44: 2000 abates = MESMO % de vida por golpe e MAIS golpes pra matar — o grind
    // não rendia nada contra bicho comum). 0.5 = só METADE do que você absorveu conta pra Ameaça:
    // os monstros ainda escalam, mas quem farma FICA mais forte. O comprado (💎/🪙) continua fora (opção A).
    absorbWeight: 0.5,
    hpLag: 0.6, // 10.9BL: era 0.38 (Thiago: "monstros com pouca vida") — comum ~3 golpes, tanque ~10
    //           10.9K: era 0.5 — a vida do inimigo cresce MENOS com o teu dano, então ficar forte
    //           SENTE (kills mais rápidas). Ainda anti-imortal (não zera a ameaça), só que o
    //           player deixa de "correr parado" — corrige o "player fraco / demora pra matar".
    // 10.9AO — era 40: tank de VIP (vida/regen comprados) + tier alto batia no teto e virava imortal.
    // Agora a ameaça é calculada pela VIDA EFETIVA (vida + regen×regenWindow, dividida pela mitigação
    // de armadura/resiliência) e o teto é alto o bastante pra acompanhar T6 + compras.
    // 10.9HM — PESO DO EQUIPAMENTO na Ameaça (Thiago: "temos level nas armaduras e armas — veja isso").
    // Medido: forjar tudo de L1 a L250 não mudava NADA contra monstro comum (8%→10% por golpe, mesmo
    // tempo pra matar), porque a Ameaça seguia o equipamento 1:1 e o teto de golpes é fixo por tier.
    // Agora só 40% do que vem do EQUIPAMENTO (vida/armadura das peças, dano base da arma) entra na
    // Ameaça — os outros 60% são vantagem real de quem forja e sobe de tier.
    gearWeight: 0.4,
    armorFactorCap: 3.3, // 10.9HL — quanto de vida efetiva a armadura pode valer na Ameaça (ver Threat.ts)
    dmgMax: 3000, // 10.9HL: era 400 — batia no teto no Nv ~100 (fator de armadura) e os monstros paravam de escalar; com o fator de armadura limitado (Threat.ts) o teto só serve de trava de segurança
    regenWindow: 20, // segundos de REGEN que contam como vida efetiva (regen comprada entra na ameaça)
    // 10.9AP — BOSSES ACOMPANHAM a ameaça (fração da escala dos monstros): vida segue o dano do
    // player (senão player forte derrete boss de campo) e o dano segue a vida efetiva (o boss já
    // tem dano por %HP, por isso a fração é menor). Vale pra TODOS os spawns de boss.
    // 10.9AY — "primeiro boss forte pra caramba": nível-acima × ameaça × chip%HP somavam 3 escalas e o
    // pesado tirava ~30% da vida de um Nv8. Boss segue MENOS a ameaça (o chip já garante ameaça no endgame).
    bossHpFollow: 0.7,
    bossDmgFollow: 0.35,
    hpMax: 1000000, // 10.7O: SEM teto de HP — monstros chegam a MILHÕES acompanhando o teu dano
    // 10.9DT — TETO DE GOLPES. A ameaca ja normaliza a vida pelo teu dano, mas tier do territorio,
    // profundidade e surge multiplicavam POR CIMA: um bicho comum de base alta chegava a ~1 MILHAO
    // de vida com o player batendo 3 mil (250+ golpes). Agora a vida final e limitada a N golpes
    // do teu dano efetivo (nunca abaixo da vida BASE do bicho, pra nao virar cosquinha cedo).
    // 10.9DY — o teto e POR TIER. Um teto unico (era 12 pra todo mundo) achatava a progressao:
    // bicho tier 4 das Terras Fraturadas morria nos mesmos 12 golpes do tier 1 da Floresta.
    // Agora cada degrau de territorio custa golpes de verdade (e as areas avancadas PESAM).
    hitsCap: { byTier: [9, 12, 16, 20, 26], eliteMult: 2, championMult: 3.2 }, // 10.9FV: +~30% de golpes (era [7,9,12,16,20]) — monstro dura pra ameacar o player forte
    // 10.9DU — mesmo teto para BOSS. O boss soma ameaca (x0.7) + escala de nivel (ate x3.2) +
    // tier de sala, e chegava a 1.200.000 de vida com o player batendo 3.2K (375 golpes).
    // A luta continua LONGA (e o numero mais alto do jogo), mas deixa de ser impossivel.
    bossHitsCap: 60,
    // piso: o teto nunca deixa um boss abaixo disso (no comeco do jogo o teu dano e baixo e o
    // teto ficaria MENOR que a vida base do boss — o boss viraria cosquinha).
    bossHpFloor: 2500,
  },

  // CORRENTE DE ARMAS (10.5H): até N armas da BOLSA ficam presas ao Hunter por elos de
  // energia, orbitando; cada uma ATACA sozinha (no seu ritmo) qualquer inimigo até `reach`
  // metros — a corrente "estica" a arma até o alvo (acerta voadores também). Dano = arma
  // × dmgMult (um pouco menor que a mão principal, p/ não dobrar o DPS de graça).
  chain: {
    maxWeapons: 4,
    reach: 5.5, // distância X até onde a arma presa alcança
    orbitRadius: 1.75,
    orbitSpeed: 0.8, // rad/s
    height: 1.35,
    // 10.9L24 — Thiago: "quero que o mesmo dano da arma da mão esteja nas armas da corrente".
    // Era 0.3× (cada presa batia só 30% do que a mão bateria com aquela arma) — agora é 1:1: a
    // fórmula em ChainWeapons.damageFor fica idêntica à do dano da mão (flatSemArma + baseDaArma),
    // então uma arma presa bate EXATAMENTE o que bateria se estivesse equipada na mão.
    dmgMult: 1,
    // pesoDaArma: 1 = soma o baseDamage da arma presa uma vez só (igual a mão) — não amplifica.
    weaponWeight: 1,
    strikeTime: 0.28, // s do "estalo" (ida e volta) visual
    links: 6, // elos por corrente
  },

  hook: {
    range: 10,
    projectileSpeed: 20,
    pullSpeed: 14,
    cooldown: 2.5,
    missCooldown: 1.0,
    minPullDistance: 1.8,
    headRadius: 0.22,
    hitRadius: 0.9, // tolerância pra "grudar" no inimigo em voo
    groundDuration: 2.0 as number, // Fase 8: Aetherwing derrubado fica no chão vulnerável ~2s
  },

  enemies: {
    voidling: {
      id: "voidling",
      weakTo: "sword", // 10.6G
      maxHp: 40, // 10.9BL: era 22 — morria em 1 golpe
      damage: 9,
      moveSpeed: 3.4,
      aggroRange: 10,
      attackRange: 1.5,
      attackCooldown: 1.0,
      attackWindup: 0.28,
      xpReward: 10,
      radius: 0.5,
      separation: 1.15,
      knockbackResist: 0.55,
      color: 0x8b3bd6,
      scale: 1,
      shape: "orb",
      drops: [
        { type: "power", min: 1, max: 2, chance: 1 },
        { type: "vitality", min: 1, max: 1, chance: 0.3 },
      ],
    } as EnemyDef,

    voidRunner: {
      id: "voidRunner",
      weakTo: "sword", // 10.6G
      maxHp: 30, // 10.9BL: era 14
      damage: 8,
      moveSpeed: 5.8,
      aggroRange: 12,
      attackRange: 1.4,
      attackCooldown: 0.6,
      attackWindup: 0.14,
      xpReward: 8,
      radius: 0.42,
      separation: 0.95,
      knockbackResist: 0.4,
      color: 0x33e0c0,
      scale: 0.85,
      shape: "runner",
      drops: [
        { type: "agility", min: 1, max: 2, chance: 1 },
        { type: "ferocity", min: 1, max: 1, chance: 0.35 },
      ],
    } as EnemyDef,

    voidBrute: {
      id: "voidBrute",
      maxHp: 80, // 10.9BL: era 50
      damage: 18,
      moveSpeed: 2.4,
      aggroRange: 9,
      attackRange: 2.0,
      attackCooldown: 1.6,
      attackWindup: 0.45,
      xpReward: 25,
      radius: 0.9,
      separation: 1.7,
      knockbackResist: 0.75,
      color: 0xff5a2b,
      scale: 1.6,
      shape: "brute",
      // 10.6F — bate de perto E arremessa lasca de longe: não dá mais para ficar em pé
      // a 5 m dele batendo de arco sem tomar nada.
      behavior: "hybrid",
      ranged: { cooldown: 3.4, windup: 0.7, projectileSpeed: 13, projectileColor: 0xff8a5a, damage: 8, range: 13 },
      drops: [
        { type: "vitality", min: 2, max: 3, chance: 1 },
        { type: "fortitude", min: 1, max: 1, chance: 0.5 },
      ],
    } as EnemyDef,

    corruptedBrute: {
      id: "corruptedBrute",
      maxHp: 160,
      damage: 28, // Heavy Slam (ataque normal, telegraph longo)
      moveSpeed: 2.6,
      aggroRange: 12,
      attackRange: 2.6,
      attackCooldown: 2.2,
      attackWindup: 0.8, // telegraph claro do Heavy Slam
      xpReward: 50,
      radius: 1.1,
      separation: 2.2,
      knockbackResist: 0.9,
      color: 0xff3355,
      scale: 2.0,
      shape: "brute",
      armor: 10,
      behavior: "hybrid", // 10.6F
      ranged: { cooldown: 3.0, windup: 0.8, projectileSpeed: 14, projectileColor: 0xff5a7a, damage: 14, range: 15 },
      drops: [
        { type: "vitality", min: 3, max: 5, chance: 1 },
        { type: "fortitude", min: 1, max: 2, chance: 1 },
        { type: "power", min: 1, max: 2, chance: 0.6 },
      ],
      elite: {
        aura: 0xff2244,
        hookStagger: 0.7,
        groundSmash: { telegraph: 1.0, radius: 4.0, damage: 22, cooldown: 5, knockback: 8 },
      },
    } as EnemyDef,


    // ================= 10.6I: MONSTROS EXCLUSIVOS POR ÁREA =================
    // Cada área ganha 2 bichos que só existem nela. As fraquezas respeitam a escada de
    // armas: na Área 1 (só espada) ninguém resiste a nada — resistir sem dar alternativa
    // é punição, não desafio. Da Área 2 em diante, cada um tem verde/laranja/vermelho.

    // --- ÁREA 1 · Forgotten Forest ---
    // Void Spitter: fica atrás da gangue e cospe. Ensina que nem todo bicho vem até você.
    voidSpitter: {
      id: "voidSpitter",
      weakTo: "sword",
      maxHp: 42, // 10.9BL: era 24
      damage: 11,
      moveSpeed: 2.8,
      aggroRange: 14,
      attackRange: 9.5,
      attackCooldown: 2.6,
      attackWindup: 0.55,
      xpReward: 16,
      radius: 0.52,
      separation: 1.3,
      knockbackResist: 0.25,
      color: 0x6fbf5a,
      crystalColor: 0xc7ff9e,
      scale: 1.05,
      shape: "orb",
      behavior: "ranged",
      tier: 1,
      ranged: { cooldown: 2.6, windup: 0.55, projectileSpeed: 12, projectileColor: 0xc7ff9e, damage: 6 },
      drops: [
        { type: "power", min: 1, max: 2, chance: 1 },
        { type: "agility", min: 1, max: 1, chance: 0.4 },
      ],
    } as EnemyDef,

    // Void Husk: casca lenta e dura que bate perto E arremessa. O primeiro "muro" da Área 1.
    voidHusk: {
      id: "voidHusk",
      weakTo: "sword",
      maxHp: 100, // 10.9BL: era 75
      damage: 22,
      moveSpeed: 2.0,
      aggroRange: 10,
      attackRange: 2.3,
      attackCooldown: 2.4,
      attackWindup: 0.75,
      xpReward: 34,
      radius: 1.0,
      separation: 1.9,
      knockbackResist: 0.85,
      color: 0x7a6a4a,
      crystalColor: 0xd6c48a,
      scale: 1.7,
      shape: "heavy",
      behavior: "hybrid",
      tier: 1,
      armor: 6,
      ranged: { cooldown: 4.0, windup: 0.85, projectileSpeed: 12, projectileColor: 0xd6c48a, damage: 9, range: 12 },
      drops: [
        { type: "vitality", min: 2, max: 4, chance: 1 },
        { type: "fortitude", min: 1, max: 2, chance: 0.6 },
      ],
    } as EnemyDef,

    // --- ÁREA 2 · Shattered Wilds ---
    // Shard Crawler: rasteiro e rápido, pula em cima. Corte escorrega, machado abre.
    shardCrawler: {
      id: "shardCrawler",
      weakTo: "axe",
      tough: "sword",
      resists: "bow",
      maxHp: 65,
      damage: 28,
      moveSpeed: 5.6,
      aggroRange: 14,
      attackRange: 1.9,
      attackCooldown: 1.3,
      attackWindup: 0.28,
      xpReward: 42,
      radius: 0.62,
      separation: 1.25,
      knockbackResist: 0.3,
      color: 0x4fb3c9,
      crystalColor: 0xbdf3ff,
      scale: 1.05,
      shape: "beast",
      behavior: "lunge",
      tier: 2,
      charge: { telegraph: 0.5, speed: 17, damage: 17, maxDist: 9, cooldown: 4.2, knockback: 6, recovery: 0.45 },
      drops: [
        { type: "agility", min: 2, max: 3, chance: 1 },
        { type: "power", min: 1, max: 2, chance: 0.6 },
      ],
    } as EnemyDef,

    // Vein Lurker: some e reaparece do lado. Lança alcança antes dele chegar; machado não.
    veinLurker: {
      id: "veinLurker",
      weakTo: "bow",
      tough: "sword",
      resists: "axe",
      maxHp: 80,
      damage: 34,
      moveSpeed: 4.6,
      aggroRange: 15,
      attackRange: 2.1,
      attackCooldown: 1.5,
      attackWindup: 0.35,
      xpReward: 52,
      radius: 0.7,
      separation: 1.5,
      knockbackResist: 0.45,
      color: 0x6a5aa8,
      crystalColor: 0xd0c2ff,
      scale: 1.25,
      shape: "stalker",
      behavior: "ambush",
      tier: 2,
      ambush: { stalkRange: 10, fadeOpacity: 0.34, telegraph: 0.5, surgeSpeed: 16, surgeDamage: 21, cooldown: 5.5, knockback: 6 },
      drops: [
        { type: "ferocity", min: 1, max: 3, chance: 1 },
        { type: "agility", min: 1, max: 2, chance: 0.6 },
      ],
    } as EnemyDef,

    // --- ÁREA 3 · Tempest Reach ---
    // Storm Lance: atira de MUITO longe e recua. É o motivo de existir arco e maça.
    stormLance: {
      id: "stormLance",
      weakTo: "mace",
      tough: "bow",
      resists: "bow",
      maxHp: 90, // 10.9BL: era 140
      damage: 45,
      moveSpeed: 4.0,
      aggroRange: 18,
      attackRange: 13,
      attackCooldown: 2.0,
      attackWindup: 0.5,
      xpReward: 96,
      radius: 0.75,
      separation: 1.6,
      knockbackResist: 0.4,
      color: 0x7fb0ff,
      crystalColor: 0xdcecff,
      scale: 1.35,
      shape: "strider",
      behavior: "ranged",
      tier: 3,
      ranged: { cooldown: 2.4, windup: 0.6, projectileSpeed: 20, projectileColor: 0xdcecff, damage: 26, burst: 2, burstGap: 0.22 },
      drops: [
        { type: "power", min: 3, max: 5, chance: 1 },
        { type: "agility", min: 2, max: 3, chance: 0.7 },
      ],
    } as EnemyDef,

    // Ruin Sentinel: parede que ergue guarda. Só o arco fura de longe; espada quica.
    ruinSentinel: {
      id: "ruinSentinel",
      weakTo: "bow",
      tough: "mace",
      resists: "sword",
      maxHp: 120, // 10.9BL: era 220
      damage: 52,
      moveSpeed: 2.4,
      aggroRange: 13,
      attackRange: 2.8,
      attackCooldown: 2.2,
      attackWindup: 0.7,
      xpReward: 130,
      radius: 1.15,
      separation: 2.2,
      knockbackResist: 0.95,
      color: 0x9aa8bc,
      crystalColor: 0xeaf3ff,
      scale: 1.9,
      shape: "sentinel",
      behavior: "guard",
      tier: 3,
      armor: 10, // 10.9BL: era 18
      guard: { reduction: 0.5, duration: 2.2, cooldown: 6.5 },
      drops: [
        { type: "fortitude", min: 3, max: 5, chance: 1 },
        { type: "vitality", min: 2, max: 4, chance: 0.8 },
      ],
    } as EnemyDef,

    // ===================== FASE 8: SHATTERED WILDS (Tier 2) =====================
    // Shardfang — predador quadrúpede ágil: circula, aproxima, LUNGE, morde, recua.
    shardfang: {
      id: "shardfang",
      tough: "axe", // 10.6G nível intermediário
      weakTo: "bow", // 10.6G
      resists: "sword",
      maxHp: 60,
      damage: 22,
      moveSpeed: 5.2,
      aggroRange: 13,
      attackRange: 1.8,
      attackCooldown: 1.4,
      attackWindup: 0.28,
      xpReward: 22,
      radius: 0.6,
      separation: 1.2,
      knockbackResist: 0.25,
      color: 0x2f6bd6,
      crystalColor: 0x62e8ff,
      scale: 1.0,
      shape: "beast",
      behavior: "lunge",
      tier: 2,
      charge: { telegraph: 0.4, speed: 15, damage: 12, maxDist: 6, cooldown: 3.2, knockback: 5, recovery: 0.5 },
      drops: [
        { type: "ferocity", min: 2, max: 3, chance: 1 },
        { type: "agility", min: 1, max: 2, chance: 0.5 },
      ],
    } as EnemyDef,

    // Craghorn — quadrúpede pesado com chifres e placas de pedra: CHARGE telegrafado.
    craghorn: {
      id: "craghorn",
      weakTo: "axe", // 10.6G
      resists: "bow",
      maxHp: 90, // 10.9BL: era 130
      damage: 38,
      moveSpeed: 2.8,
      aggroRange: 12,
      attackRange: 2.4,
      attackCooldown: 2.4,
      attackWindup: 0.7,
      xpReward: 45,
      radius: 1.05,
      separation: 2.1,
      knockbackResist: 0.85,
      hookPullResist: 0.6,
      armor: 10, // 10.9BL: era 18
      color: 0x6b7280,
      crystalColor: 0x8ea2ff,
      scale: 1.7,
      shape: "heavy",
      behavior: "charge",
      tier: 2,
      charge: { telegraph: 0.85, speed: 20, damage: 22, maxDist: 14, cooldown: 5.5, knockback: 10, recovery: 0.7 },
      drops: [
        { type: "fortitude", min: 2, max: 3, chance: 1 },
        { type: "vitality", min: 2, max: 3, chance: 0.7 },
      ],
    } as EnemyDef,

    // Aetherwing — criatura flutuante: mantém distância e dispara AETHER BOLT.
    aetherwing: {
      id: "aetherwing",
      tough: "sword", // 10.6G nível intermediário
      weakTo: "bow", // 10.6G
      resists: "axe",
      maxHp: 45,
      damage: 16,
      moveSpeed: 4.0,
      aggroRange: 15,
      attackRange: 10,
      attackCooldown: 2.0,
      attackWindup: 0.5,
      xpReward: 20,
      radius: 0.55,
      separation: 1.3,
      knockbackResist: 0.2,
      color: 0x35c9ff,
      crystalColor: 0xa9f0ff,
      scale: 1.0,
      shape: "flyer",
      behavior: "ranged",
      flying: true,
      tier: 2,
      ranged: { cooldown: 2.0, windup: 0.5, projectileSpeed: 12, projectileColor: 0x8fe8ff },
      drops: [
        { type: "agility", min: 2, max: 3, chance: 1 },
        { type: "power", min: 1, max: 2, chance: 0.5 },
      ],
    } as EnemyDef,

    // Void Wisp (10.5H) — VOADOR da Área 1: orbe alado frágil que cospe bolts de longe.
    // Melee normal não acerta voando — é a corrente (e o arco) que resolve.
    voidWisp: {
      id: "voidWisp",
      weakTo: "sword", // 10.6G
      maxHp: 40, // 10.9BL: era 26
      damage: 4,
      moveSpeed: 3.2,
      aggroRange: 13,
      attackRange: 8,
      attackCooldown: 2.3,
      attackWindup: 0.45,
      xpReward: 12,
      radius: 0.42,
      separation: 1.2,
      knockbackResist: 0.1,
      color: 0x8b5cf6,
      crystalColor: 0xd9c6ff,
      scale: 0.85,
      shape: "flyer",
      behavior: "ranged",
      flying: true,
      tier: 1,
      ranged: { cooldown: 2.3, windup: 0.45, projectileSpeed: 11, projectileColor: 0xc9b6ff },
      drops: [
        { type: "agility", min: 1, max: 2, chance: 1 },
        { type: "power", min: 1, max: 1, chance: 0.35 },
      ],
    } as EnemyDef,

    // Glintback — criatura defensiva de carapaça cristalina: CRYSTAL GUARD (reduz dano).
    glintback: {
      id: "glintback",
      tough: "bow", // 10.6G nível intermediário
      weakTo: "axe", // 10.6G
      resists: "sword",
      maxHp: 85,
      damage: 18,
      moveSpeed: 2.6,
      aggroRange: 10,
      attackRange: 1.8,
      attackCooldown: 1.8,
      attackWindup: 0.55,
      xpReward: 30,
      radius: 0.8,
      separation: 1.6,
      knockbackResist: 0.7,
      armor: 15, // 10.9BL: era 30
      color: 0x3a7d68,
      crystalColor: 0x76ffd0,
      scale: 1.2,
      shape: "shell",
      behavior: "guard",
      tier: 2,
      guard: { reduction: 0.7, duration: 3.0, cooldown: 6.0 },
      drops: [
        { type: "fortitude", min: 2, max: 4, chance: 1 },
        { type: "vitality", min: 1, max: 2, chance: 0.6 },
      ],
    } as EnemyDef,

    // Shard Warden — Elite exclusivo da Região 2: CRYSTAL CHARGE + SHARD BURST.
    shardWarden: {
      id: "shardWarden",
      weakTo: "bow", // 10.6G
      resists: "sword",
      maxHp: 420,
      damage: 48,
      moveSpeed: 3.0,
      aggroRange: 13,
      attackRange: 2.8,
      attackCooldown: 2.4,
      attackWindup: 0.8,
      xpReward: 90,
      radius: 1.25,
      separation: 2.4,
      knockbackResist: 0.9,
      hookPullResist: 0.7,
      armor: 25,
      color: 0x2b5fd6,
      crystalColor: 0x7cf0ff,
      scale: 2.2,
      shape: "warden",
      behavior: "warden",
      tier: 2,
      charge: { telegraph: 0.9, speed: 18, damage: 28, maxDist: 12, cooldown: 6.5, knockback: 10, recovery: 0.8 },
      ranged: { cooldown: 5.0, windup: 0.9, projectileSpeed: 11, projectileColor: 0x8fe8ff, damage: 20 },
      elite: {
        aura: 0x4c8cff,
        hookStagger: 0.6,
        groundSmash: { telegraph: 1.0, radius: 4.5, damage: 30, cooldown: 6, knockback: 9 },
      },
      drops: [
        { type: "power", min: 3, max: 5, chance: 1 },
        { type: "fortitude", min: 2, max: 3, chance: 1 },
        { type: "ferocity", min: 2, max: 3, chance: 0.8 },
      ],
    } as EnemyDef,

    // ===================== FASE 10: TEMPEST REACH (Tier 3) =====================
    // Galestrider — bípede leve e veloz: circula, DASH lateral, aproxima, ataca, reposiciona.
    galestrider: {
      id: "galestrider",
      tough: "bow", // 10.6G nível intermediário
      weakTo: "bow", // 10.6G
      resists: "sword",
      maxHp: 65, // 10.9BL: era 80
      damage: 28,
      moveSpeed: 6.0,
      aggroRange: 15,
      attackRange: 1.8,
      attackCooldown: 1.2,
      attackWindup: 0.24,
      xpReward: 28,
      radius: 0.6,
      separation: 1.25,
      knockbackResist: 0.2,
      color: 0x4a78c4,
      crystalColor: 0x9fe6ff,
      scale: 1.0,
      shape: "strider",
      behavior: "strafe",
      tier: 3,
      charge: { telegraph: 0.35, speed: 18, damage: 15, maxDist: 8, cooldown: 3.0, knockback: 5, recovery: 0.45 },
      drops: [
        { type: "agility", min: 2, max: 4, chance: 1 },
        { type: "ferocity", min: 1, max: 2, chance: 0.55 },
      ],
    } as EnemyDef,

    // Arcbeak — criatura aérea: mantém distância e dispara ARC BOLT (2 projéteis). Hook derruba.
    arcbeak: {
      id: "arcbeak",
      tough: "axe", // 10.6G nível intermediário
      weakTo: "bow", // 10.6G
      resists: "bow",
      maxHp: 48,
      damage: 22,
      moveSpeed: 4.2,
      aggroRange: 16,
      attackRange: 11,
      attackCooldown: 2.0,
      attackWindup: 0.5,
      xpReward: 24,
      radius: 0.55,
      separation: 1.3,
      knockbackResist: 0.2,
      color: 0x3aa0e6,
      crystalColor: 0xbfefff,
      scale: 1.0,
      shape: "spark",
      behavior: "ranged",
      flying: true,
      tier: 3,
      groundedDuration: 1.3, // recupera um pouco mais rápido que o Aetherwing (§16)
      ranged: { cooldown: 2.4, windup: 0.5, projectileSpeed: 13, projectileColor: 0x9fe6ff, burst: 2, burstGap: 0.16 },
      drops: [
        { type: "agility", min: 2, max: 3, chance: 1 },
        { type: "power", min: 1, max: 2, chance: 0.55 },
      ],
    } as EnemyDef,

    // Stormback — pesado com carapaça: STATIC SHIELD (-35%) c/ ponto exposto; Hook quebra o escudo.
    stormback: {
      id: "stormback",
      tough: "sword", // 10.6G nível intermediário
      weakTo: "mace", // 10.6G
      resists: "bow",
      maxHp: 100, // 10.9BL: era 170
      damage: 32,
      moveSpeed: 2.6,
      aggroRange: 12,
      attackRange: 2.2,
      attackCooldown: 2.0,
      attackWindup: 0.65,
      xpReward: 52,
      radius: 1.1,
      separation: 2.1,
      knockbackResist: 0.9,
      hookPullResist: 0.8,
      armor: 12, // 10.9BL: era 26
      color: 0x566b86,
      crystalColor: 0x8fd8ff,
      scale: 1.5,
      shape: "bulwark",
      behavior: "shield",
      tier: 3,
      guard: { reduction: 0.35, duration: 4.0, cooldown: 7.0, hookBreaks: true, stagger: 1.6 },
      drops: [
        { type: "fortitude", min: 3, max: 4, chance: 1 },
        { type: "vitality", min: 2, max: 3, chance: 0.7 },
      ],
    } as EnemyDef,

    // Cloudstalker — predador de emboscada: fade parcial -> telegraph -> SURGE -> golpe.
    cloudstalker: {
      id: "cloudstalker",
      weakTo: "bow", // 10.6G
      resists: "axe",
      maxHp: 70,
      damage: 26,
      moveSpeed: 4.6,
      aggroRange: 14,
      attackRange: 1.9,
      attackCooldown: 1.5,
      attackWindup: 0.28,
      xpReward: 30,
      radius: 0.6,
      separation: 1.2,
      knockbackResist: 0.25,
      color: 0x6a7fb0,
      crystalColor: 0xbfe0ff,
      scale: 1.05,
      shape: "stalker",
      behavior: "ambush",
      tier: 3,
      ambush: { stalkRange: 9, fadeOpacity: 0.32, telegraph: 0.55, surgeSpeed: 17, surgeDamage: 20, cooldown: 4.0, knockback: 6 },
      drops: [
        { type: "agility", min: 2, max: 3, chance: 1 },
        { type: "ferocity", min: 2, max: 3, chance: 0.6 },
      ],
    } as EnemyDef,

    // Tempest Warden — Elite da Região 3: ARC DASH (linha de energia) + STORM FIELD (zonas).
    tempestWarden: {
      id: "tempestWarden",
      weakTo: "mace", // 10.6G
      resists: "sword",
      maxHp: 500,
      damage: 52,
      moveSpeed: 3.2,
      aggroRange: 14,
      attackRange: 2.8,
      attackCooldown: 2.2,
      attackWindup: 0.8,
      xpReward: 100,
      radius: 1.3,
      separation: 2.5,
      knockbackResist: 0.9,
      hookPullResist: 0.75,
      armor: 28,
      color: 0x4a6b96,
      crystalColor: 0x9fe0ff,
      scale: 2.1,
      shape: "sentinel",
      behavior: "tempest",
      tier: 3,
      charge: { telegraph: 0.7, speed: 24, damage: 30, maxDist: 16, cooldown: 6.5, knockback: 11, recovery: 0.7 },
      stormField: { zones: 3, radius: 3.0, telegraph: 1.0, damage: 26, cooldown: 8.0 },
      elite: {
        aura: 0x6fb8ff,
        hookStagger: 0.7,
        groundSmash: { telegraph: 1.0, radius: 4.5, damage: 32, cooldown: 7, knockback: 10 },
      },
      drops: [
        { type: "power", min: 3, max: 5, chance: 1 },
        { type: "agility", min: 2, max: 3, chance: 1 },
        { type: "fortitude", min: 2, max: 3, chance: 0.8 },
      ],
    } as EnemyDef,
  },

  essence: {
    magnetRadius: 3,
    magnetSpeed: 10,
    collectRadius: 0.9,
    orbLifetime: 30,
    orbFloatHeight: 0.6,
  },

  /** 10.9L21 — Thiago: "o pet não da atributo nenhum, não da pra evoluir ele e ele não vai atrás
   *  dos recursos que caem no chão". Era só cosmético + alcance FIXO de coleta. Agora ganha XP
   *  por abate (enquanto ativo), sobe de nível, e cada nível aumenta um pouco o alcance de coleta
   *  de essência E o dano do Caçador — além de o pet ir voando buscar a essência mais próxima em
   *  vez de só ficar orbitando o jogador esperando ela chegar perto. */
  pet: {
    xpPerKill: 1,
    // 10.9L22 — Thiago: "o level de pet pode ser infinito mas com dificuldade igual o level do
    // personagem". Era linear (15+lvl×10, nunca ficava MAIS difícil por kill) e tinha teto no Nv20.
    // Curva de potência igual ao estilo das Perícias (mesmo expoente 1.5) — sem teto de nível.
    xpForLevel: (lvl: number): number => Math.floor(15 * Math.pow(lvl, 1.5)),
    magnetMultBase: 1.5, // alcance de coleta no Nv1 (era fixo 1.8 sem nunca subir)
    magnetMultPerLevel: 0.05, // +5%/nível, sem teto (Nv20 = 2.45×, Nv50 = 3.95×...)
    dmgMultPerLevel: 0.005, // +0.5% de dano do Caçador por nível, sem teto
    chaseRadius: 7, // o pet só sai voando atrás de essência dentro deste raio do jogador
  },

  altar: {
    position: HUB.altar, // 10.9BP — posição vem do layout do hub (norte)
    interactRadius: 3.2,
    cost: { base: 5, growth: 1.35 }, // custo = round(base × growth^nivelAtual)
    upgrades: [
      { key: "power", stat: "baseDamage", amount: 1, descKey: "altar.effect.power" },
      { key: "vitality", stat: "maxHp", amount: 5, descKey: "altar.effect.vitality" },
      { key: "agility", stat: "moveSpeed", amount: 0.05, descKey: "altar.effect.agility" },
      { key: "fortitude", stat: "armor", amount: 1, descKey: "altar.effect.fortitude" },
      { key: "ferocity", stat: "critChance", amount: 0.005, descKey: "altar.effect.ferocity" },
    ] as AltarUpgradeDef[],
  },

  // 10.9T — CLASSES: o jogador escolhe ao entrar (Guerreiro/Arqueiro/Mago; extensível).
  // A classe MODIFICA os stats base (via mods no recalc) + define a ARMA INICIAL. Não trava
  // tipos de arma na Fase 1 (loot/forja seguem iguais). Magias por classe vêm na Fase 2.
  classes: [
    // 10.9EN — PALETA POR CLASSE (Thiago: "cores proprias"). Cada classe tem UMA familia de cor e
    // nenhuma magia encosta na de outra classe. Antes: arqueiro e necromante eram dois verdes quase
    // iguais, a Nova do necromante era o mesmo roxo da Nova do mago, o Meteoro do mago era o mesmo
    // laranja do guerreiro, e (erro meu no 10.9EM) o dourado do arqueiro batia com o do guerreiro.
    //   GUERREIRO  laranja/vermelho   #ff8a5a  #ff5a3c  #ff4d6d
    //   ARQUEIRO   dourado            #ffd24a  #ffe89a  #fff07a   (cor da classe tambem vira dourada)
    //   MAGO       roxo/violeta       #b48bff  #d15bff  #7f8bff
    //   NECROMANTE verde espectral    #7fe0b0  #2fd6b0  #5aff8a
    {
      id: "warrior", nameKey: "class.warrior.name", descKey: "class.warrior.desc", icon: "⚔️", color: "#ff8a5a",
      // 10.9EU — GUERREIRO REFORCADO (Thiago: "ta muito fraca"). Medido no Nv30: menor DPS do jogo (298 vs 401
      // do arqueiro), 25,4s pra matar um T4 (arqueiro 15,1s), alcance 2,6m contra 9m, e a vida extra ANULADA
      // pela Ameaca (todas as classes morriam com 2 golpes: 51% da vida por golpe). Ver Game.recalcPlayer.
      // atk 1.25: com a Ameaca normalizando o dano por golpe, so VELOCIDADE muda o tempo pra matar 1 alvo
      // (boss) — o machado vai de 0,8 pra 1 golpe/s.
      hpMult: 1.35, damageMult: 1.05, attackSpeedMult: 1.25, moveSpeedMult: 0.96, armorFlat: 4, critChance: 0, critDamageMult: 0,
      // 10.9EW — Thiago: "ainda ta fraco, toma muito dano, ta desvantajoso ser melee". Colado no monstro ele
      // apanha de VARIOS ao mesmo tempo. Defesas de CLASSE que a Ameaca NAO enxerga (senao ela anula):
      blockChance: 0.2, // bloqueio nato (era 12%): golpe bloqueado = metade do dano
      cleaveTargets: 3, // golpe em AREA: acerta ate 3 monstros no arco (antes: 1, igual ao arqueiro)
      damageReduction: 0.35, // PELE DE ACO: recebe 35% menos dano de tudo
      lifeSteal: 0.06, // roubo de vida nato: com o golpe em area, se cura lutando
      starterWeaponId: "voidCleaver", // machado (corpo a corpo pesado)
      allowedWeapons: ["sword", "axe", "mace"] as const, // 10.9Z — só corpo a corpo
      // 10.9U — magia: GOLPE SÍSMICO (AoE em volta + empurrão forte)
      spell: { kind: "slam", nameKey: "spell.warrior", cooldown: 8, dmgMult: 3.4, radius: 5.5, knockback: 12, color: 0xff8a5a, damageType: "blunt" as const },
      // 10.9CX — +2 magias: INVESTIDA (avanço com dano) e GRITO DE GUERRA (buff defensivo/ofensivo)
      spell2: { kind: "dash" as const, nameKey: "spell.warrior2", descKey: "spell.warrior2.d", cooldown: 6, dmgMult: 2.0, radius: 2.4, knockback: 9, color: 0xff5a3c, damageType: "blunt" as const, dashRange: 8 },
      spell3: { kind: "buff" as const, nameKey: "spell.warrior3", descKey: "spell.warrior3.d", cooldown: 18, dmgMult: 1, radius: 0, knockback: 0, color: 0xff4d6d, damageType: "blunt" as const, buff: { seconds: 6, dmgMult: 0.30, dr: 0.40, healFrac: 0.15, moveMult: 0, atkSpeedMult: 0, critChance: 0 } },
      // 10.9FC — GRIMORIO: magias NOVAS pra escolher (1 buff + 2 ataque), liberadas por nivel
      extraSpells: [
        { id: "whirl", unlockLevel: 10, kind: "whirl" as const, nameKey: "spell.x.whirl", descKey: "spell.x.whirl.d", cooldown: 10, dmgMult: 1.1, radius: 3.8, knockback: 3, color: 0xff7a3c, damageType: "slashing" as const, field: { seconds: 2.5, tickEvery: 0.25 } },
        { id: "shockwave", unlockLevel: 25, kind: "wave" as const, nameKey: "spell.x.shockwave", descKey: "spell.x.shockwave.d", cooldown: 7, dmgMult: 4.2, radius: 2.2, knockback: 14, color: 0xffb04a, damageType: "blunt" as const, waveLength: 12 },
        { id: "battlecry", unlockLevel: 40, kind: "buff" as const, nameKey: "spell.x.battlecry", descKey: "spell.x.battlecry.d", cooldown: 20, dmgMult: 1, radius: 0, knockback: 0, color: 0xff3d3d, damageType: "blunt" as const, buff: { seconds: 8, dmgMult: 0.5, atkSpeedMult: 0.4, healFrac: 0.25 } },
      ],
    },
    {
      id: "archer", nameKey: "class.archer.name", descKey: "class.archer.desc", icon: "🏹", color: "#ffd24a",
      hpMult: 0.85, damageMult: 1.0, attackSpeedMult: 1.15, moveSpeedMult: 1.1, armorFlat: 0, critChance: 0.08, critDamageMult: 0.2,
      blockChance: 0, cleaveTargets: 1, damageReduction: 0, lifeSteal: 0,
      starterWeaponId: "voidBow", // arco (à distância ágil)
      allowedWeapons: ["bow", "crossbow"] as const, // 10.9L25 — besta: 2º tipo, pra ter variedade na corrente igual o guerreiro
      // 10.9U — magia: CHUVA DE FLECHAS (cone à frente, perfura vários)
      spell: { kind: "volley", nameKey: "spell.archer", cooldown: 7, dmgMult: 1.5, radius: 12, knockback: 3, color: 0xffd24a, damageType: "piercing" as const },
      // 10.9EM — magias do arqueiro em DOURADO (flechas de luz). Eram verde 0x8fe86a/0xbfff6a — quase
      // o mesmo verde do necromante (0x7fe0b0/0x7fef9f): Chuva de Flechas e Praga viravam o MESMO
      // anel verde no chao, e o Thiago via "a magia do arqueiro saindo igual a do necromante".
      // 10.9CX — +2 magias: FOCO DO CAÇADOR (buff crítico/velocidade) e SALTO EVASIVO (dash com dano)
      spell2: { kind: "buff" as const, nameKey: "spell.archer2", descKey: "spell.archer2.d", cooldown: 14, dmgMult: 1, radius: 0, knockback: 0, color: 0xffe89a, damageType: "piercing" as const, buff: { seconds: 6, dmgMult: 0.25, dr: 0, healFrac: 0, moveMult: 0, atkSpeedMult: 0.30, critChance: 0.15 } },
      spell3: { kind: "dash" as const, nameKey: "spell.archer3", descKey: "spell.archer3.d", cooldown: 6, dmgMult: 1.3, radius: 2.0, knockback: 4, color: 0xfff07a, damageType: "piercing" as const, dashRange: 7 },
      // 10.9FC — GRIMORIO
      extraSpells: [
        { id: "pierce", unlockLevel: 10, kind: "wave" as const, nameKey: "spell.x.pierce", descKey: "spell.x.pierce.d", cooldown: 6, dmgMult: 4.5, radius: 1.4, knockback: 5, color: 0xfff27a, damageType: "piercing" as const, waveLength: 22 },
        { id: "trap", unlockLevel: 25, kind: "trap" as const, nameKey: "spell.x.trap", descKey: "spell.x.trap.d", cooldown: 9, dmgMult: 7.0, radius: 4, knockback: 10, color: 0xff9a3c, damageType: "piercing" as const, trap: { seconds: 12, trigger: 2.2 } },
        { id: "eagle", unlockLevel: 40, kind: "buff" as const, nameKey: "spell.x.eagle", descKey: "spell.x.eagle.d", cooldown: 18, dmgMult: 1, radius: 0, knockback: 0, color: 0xffe066, damageType: "piercing" as const, buff: { seconds: 8, critChance: 0.35, dmgMult: 0.3 } },
      ],
    },
    {
      id: "mage", nameKey: "class.mage.name", descKey: "class.mage.desc", icon: "🔮", color: "#b48bff",
      // 10.9CC — Thiago pediu pra rever o dano do mago: 1.4→1.5 (vidro-canhão de verdade; Nova 3.4→4.0)
      // 10.9IZ — Thiago: "as skills do mago estao fracas... fortalece um pouco". O MAIOR problema
      // era mira (ver spellAimPoint/spellAimDir: Meteoro/Nevasca saiam pro lado que o corpo estava
      // virado, nao pro monstro) — corrigido separadamente. Em cima disso, +7% de vida (a unica
      // classe SEM nenhuma mitigacao: sem armadura, bloqueio, reducao de dano OU lifesteal —
      // Guerreiro tem os 4; um erro de posicionamento custa mais caro no vidro-canhao do que nas
      // outras classes, entao a rede de seguranca ajuda mais aqui do que subir ainda mais o dano).
      hpMult: 0.75, damageMult: 1.5, attackSpeedMult: 0.9, moveSpeedMult: 1.0, armorFlat: 0, critChance: 0.03, critDamageMult: 0.5,
      blockChance: 0, cleaveTargets: 1, damageReduction: 0, lifeSteal: 0,
      starterWeaponId: "voidStaff", // 10.9AD — CAJADO próprio (não é mais o arco do arqueiro)
      allowedWeapons: ["staff", "tome"] as const, // 10.9L25 — grimório: 2º tipo, pra ter variedade na corrente igual o guerreiro
      // 10.9U — magia: NOVA ARCANA (explosão mágica grande em volta, dano altíssimo)
      // 10.9IZ — cooldown 9→8 (mais chances de conjurar agora que Meteoro/Nevasca acertam de verdade)
      spell: { kind: "nova", nameKey: "spell.mage", cooldown: 8, dmgMult: 4.0, radius: 6.5, knockback: 6, color: 0xb48bff, damageType: "arcane" as const }, // 10.9CC
      // 10.9CX — +2 magias: METEORO (AoE grande com atraso, à frente) e BARREIRA ARCANA (buff defensivo)
      // 10.9IZ — dmgMult 6.0→6.6 e cooldown 12→10: era a magia MAIS forte no papel (maior dmgMult
      // do jogo depois da Armadilha do Guerreiro) e a que mais errava por causa da mira antiga.
      spell2: { kind: "meteor" as const, nameKey: "spell.mage2", descKey: "spell.mage2.d", cooldown: 10, dmgMult: 6.6, radius: 3.6, knockback: 6, color: 0xd15bff, damageType: "arcane" as const, meteorDelay: 0.9, meteorRange: 8 },
      spell3: { kind: "buff" as const, nameKey: "spell.mage3", descKey: "spell.mage3.d", cooldown: 18, dmgMult: 1, radius: 0, knockback: 0, color: 0x7f8bff, damageType: "arcane" as const, buff: { seconds: 6, dmgMult: 0, dr: 0.50, healFrac: 0.10, moveMult: 0, atkSpeedMult: 0, critChance: 0 } },
      // 10.9FC — GRIMORIO
      extraSpells: [
        { id: "chain", unlockLevel: 10, kind: "chain" as const, nameKey: "spell.x.chain", descKey: "spell.x.chain.d", cooldown: 6, dmgMult: 3.5, radius: 12, knockback: 2, color: 0x8fd8ff, damageType: "arcane" as const, chain: { jumps: 5, jumpRange: 7 } },
        // 10.9IZ — dmgMult 1.0→1.15 por tick (10 ticks em 5s: 10.0→11.5 no total) — era a que MAIS
        // sofria com a mira antiga (nascia 6m à frente do rosto, o alcance inteiro da magia).
        { id: "blizzard", unlockLevel: 25, kind: "curse" as const, nameKey: "spell.x.blizzard", descKey: "spell.x.blizzard.d", cooldown: 12, dmgMult: 1.15, radius: 6, knockback: 0, color: 0xaee8ff, damageType: "arcane" as const, field: { seconds: 5, tickEvery: 0.5 }, slow: 0.5, ahead: 6 },
        { id: "arcshield", unlockLevel: 40, kind: "buff" as const, nameKey: "spell.x.arcshield", descKey: "spell.x.arcshield.d", cooldown: 20, dmgMult: 1, radius: 0, knockback: 0, color: 0x9a8cff, damageType: "arcane" as const, buff: { seconds: 6, dr: 0.5, healFrac: 0.1 } },
      ],
    },
    {
      // 10.9AD — NECROMANTE: caçador de cajado que INVOCA servos que atacam os monstros (a magia
      // é a invocação). Frágil, mas os servos seguram a linha e distraem enquanto ele castiga de longe.
      id: "necromancer", nameKey: "class.necromancer.name", descKey: "class.necromancer.desc", icon: "💀", color: "#7fe0b0",
      hpMult: 0.78, damageMult: 1.15, attackSpeedMult: 0.95, moveSpeedMult: 1.0, armorFlat: 0, critChance: 0.04, critDamageMult: 0.3,
      blockChance: 0, cleaveTargets: 1, damageReduction: 0, lifeSteal: 0,
      starterWeaponId: "voidStaff",
      allowedWeapons: ["staff", "tome"] as const, // 10.9L25 — grimório: 2º tipo, pra ter variedade na corrente igual o guerreiro
      // magia: EXÉRCITO DO VAZIO — invoca servos temporários que caçam e batem nos monstros.
      // 10.9CF — cooldown 14→10 s (-30%, pedido do Thiago)
      spell: { kind: "summon", nameKey: "spell.necromancer", cooldown: 10, dmgMult: 1.0, radius: 6, knockback: 0, color: 0x7fe0b0, damageType: "arcane" as const,
        // 10.9BZ — Thiago: os servos NÃO têm vida nem levam dano (são espectros). Então o limite deles
        // é o TEMPO: 5 s de vida (era 16). `hp` fica só de registro — nada consome.
        // 10.9L7 — dmgMult 0.6→0.85: cada golpe do servo era 60% de um tiro do cajado (o número que
        // pula na tela saía pequeno). 0.85 = quase um tiro inteiro, mas o Necromante continua sendo
        // quem bate mais forte. Invocação cheia: 3 servos × 6 s × 1,2 golpes/s × 0.85 = 18 tiros de cajado.
        // 10.9L13 — Thiago: "aumenta o dano desses sumons". 0.85→1.0: cada golpe do servo agora É um
        // tiro inteiro do cajado (igual ao próprio Necromante bate à distância). Invocação cheia:
        // 3 servos × 6 s × 1,2 golpes/s × 1.0 = 21,6 tiros de cajado (era 18).
        summon: { count: 3, lifetime: 6, dmgMult: 1.0, hp: 55, moveSpeed: 6.4, attackRange: 1.0, attacksPerSecond: 1.2, maxAlive: 6 } },
      // 10.9CX — +2 magias: EXPLOSÃO DE ALMAS (AoE arcano em volta) e PRAGA DO VAZIO (zona de dano no tempo)
      // 10.9L7 — Thiago: "as magias do necromante e os monstros invocados precisam bater bem". Régua:
      // ~80% da magia equivalente do MAGO (o vidro-canhão puro; o necro tem os servos por cima).
      // Contas em dano de arma W já com a classe (mago ×1.5, necro ×1.15):
      //   Explosão 3.2→4.2: 3.2×1.15 = 3.7 W era 61% da Nova do mago (4.0×1.5 = 6.0 W); 4.2×1.15 = 4.8 W = 80%.
      //   Praga 0.8→1.2/tick: 0.92 W/tick era 53% da Nevasca (1.15×1.5 = 1.73 W/tick); 1.2×1.15 = 1.38 = 80%.
      spell2: { kind: "nova" as const, nameKey: "spell.necromancer2", descKey: "spell.necromancer2.d", cooldown: 9, dmgMult: 4.2, radius: 6, knockback: 5, color: 0x2fd6b0, damageType: "arcane" as const },
      spell3: { kind: "curse" as const, nameKey: "spell.necromancer3", descKey: "spell.necromancer3.d", cooldown: 12, dmgMult: 1.2, radius: 5, knockback: 0, color: 0x5aff8a, damageType: "arcane" as const, field: { seconds: 4, tickEvery: 0.5 } },
      // 10.9FC — GRIMORIO
      // 10.9L7 — Ossos 4.0→4.5: 4.5×1.15 = 5.2 W = o 1º alvo da Corrente do mago (3.5×1.5 = 5.25 W), mesmo nível (10).
      //          Drenar 0.9→1.2/tick: mesma régua da Praga (80% da Nevasca por tick) + a cura que já tinha.
      extraSpells: [
        { id: "bones", unlockLevel: 10, kind: "wave" as const, nameKey: "spell.x.bones", descKey: "spell.x.bones.d", cooldown: 6, dmgMult: 4.5, radius: 2.0, knockback: 6, color: 0xe8f0d8, damageType: "arcane" as const, waveLength: 14 },
        { id: "drain", unlockLevel: 25, kind: "curse" as const, nameKey: "spell.x.drain", descKey: "spell.x.drain.d", cooldown: 12, dmgMult: 1.2, radius: 5.5, knockback: 0, color: 0xc23a5a, damageType: "arcane" as const, field: { seconds: 5, tickEvery: 0.5 }, drainPct: 0.015 },
        { id: "bonearmor", unlockLevel: 40, kind: "buff" as const, nameKey: "spell.x.bonearmor", descKey: "spell.x.bonearmor.d", cooldown: 20, dmgMult: 1, radius: 0, knockback: 0, color: 0xd8e0c0, damageType: "arcane" as const, buff: { seconds: 8, dr: 0.4, dmgMult: 0.25, healFrac: 0.15 } },
      ],
    },
  ],

  progression: {
    startLevel: 1,
    // 10.7H — CURVA DE RETENÇÃO. 10.7D (60×lvl^1.15) era suave DEMAIS: Thiago chegou ao lv 237
    // e zerou tudo em poucos dias. Agora 54×lvl^1.35: early continua acessível, endgame ESCALA.
    // Combinado com nós infinitos + XP do Threat menor = progressão de semanas, não dias.
    // 10.9J6 — comentário tinha "55×" e os números de exemplo (lv10=1231…lv200=78430) do multiplicador
    // ANTIGO (55) e de ANTES da curva em dois trechos (10.9EE): removidos daqui pra não confundir
    // ajuste futuro — os valores medidos de VERDADE com a fórmula atual estão logo abaixo, na 10.9EE.
    // 10.9EE — CURVA EM DOIS TRECHOS.
    //   Ate o Nv32 (fim do conteudo desenhado: saida da Area 3) a curva e EXATAMENTE a de antes,
    //   54·L^1.35 — o PLANO DE ETAPAS ali em cima (Area 1 sai Nv10 ~1h, Area 2 Nv20, Area 3 Nv32)
    //   foi ajustado a mao e continua valendo NO SEGUNDO.
    //   Depois do Nv32 e Profundidade em laco, e com 1.35 ficava impossivel: o Nv500 pedia 841h e
    //   UM nivel no Nv1000 custava DEZ HORAS de caca sem parar — o jogo parava de recompensar.
    //   Dai pra frente o expoente cai pra 0.713, colado no valor do Nv32 (sem degrau na virada).
    // Medido (a 60k XP/h): Nv100 19h -> 12h · Nv300 253h -> 83h · Nv500 841h -> 200h ·
    //   Nv1000 4292h -> 657h, e um nivel no Nv1000 passa de 10h para 1,1h.
    xpLateFrom: 32,
    xpLateExp: 0.713,
    xpForLevel: (level: number): number => {
      const k = GameConfig.progression.xpLateFrom;
      if (level <= k) return Math.floor(54 * Math.pow(level, 1.35));
      return Math.floor(54 * Math.pow(k, 1.35) * Math.pow(level / k, GameConfig.progression.xpLateExp));
    },
    levelUp: { maxHp: 9, baseDamage: 2 }, // 10.5N: progressão de dano/vida por nível mais visível
    // 10.9Y — reset da Árvore Void: 1º GRÁTIS, depois custa 💎 dobrando (50→100→200→400…, teto 800)
    // 10.9K4 — Thiago: 10💎 e sobe 1% a cada reset (arredondado: 10 até o 5º, 11 do 6º ao 15º…)
    treeReset: { base: 10, growth: 0.01 },
    xpMultCap: 4, // 10.9BO: era 3 — Mestre+Passe já saturava e o booster "não fazia nada". Tier × Passe × área VIP × booster ≤ 4
    // 10.9K2 — AUDITORIA DE JUSTIÇA: a 🪙 não tinha teto (contrato 1,75 × tier 2,2 × passe 1,5 × booster 2
    // = 11,55×) e 🪙 compra atributo PERMANENTE na Loja de Atributos. Mesmo teto do XP.
    coinMultCap: 4,
    // 10.9BM — o XP por abate cresce com a vida do monstro (ameaça×tier×profundidade), mas com TETO:
    // sem isso, na Profundidade 2 um Nv31 pulava pro Nv106 numa volta (a ameaça não tem teto de vida).
    xpKillScaleCap: 2.5,
  },

  spawner: {
    // 10.6X — RITMO CERTO DE FARM: 32 era demais (Thiago demoraria pra chegar no boss).
    // 22 = 2x mais que o original (10), suficiente pra farmar sem virar zoo cansativo.
    maxAlive: 22,
    spawnInterval: 1.8,
    radiusMin: 20,
    radiusMax: 30,
    minDistanceFromPlayer: 12,
    nearRadiusMin: 10,
    nearRadiusMax: 15,
    initialNear: 5,
    initialFar: 5,
    // distribuição por tipo (pesos)
    distribution: [
      { id: "voidling", weight: 60 },
      { id: "voidRunner", weight: 25 },
      { id: "voidBrute", weight: 15 },
    ],
  },

  arena: {
    // 10.5M — MUNDO GRANDE: 210×210 (era 120×120). A arena inteira é dividida em
    // territórios (ver `territories`), cada um com bioma e gangue próprios.
    halfSize: 105,
    wallHeight: 3,
  },

  // Forgotten Forest dividida por faixas de Z (player nasce em z=0 = Void Clearing)
  regions: [
    { id: "forestEntrance", zMin: 21, zMax: 999 },
    { id: "voidClearing", zMin: -21, zMax: 21 },
    { id: "corruptedGrove", zMin: -63, zMax: -21 },
    { id: "alphaDen", zMin: -999, zMax: -63 },
  ],

  /**
   * 10.6H — CONTRATO DE CONCLUSÃO DA ÁREA. O portão fica TRANCADO até tudo isto estar
   * cumprido. Cada área pede mais que a anterior, e os alvos crescem com a Profundidade.
   */
  // 10.9AX — XP por CONCLUIR a área (auditoria de progressão): os objetivos rendiam ~3-5k XP mas a
  // área seguinte exigia Nv14 (~10.5k) / Nv30 (~57k) → o jogador terminava a área e ficava moendo.
  // Escala com a Profundidade (depth.rewardPerDepth).
  // 10.9BM — NORMALIZAÇÃO do dano por região. A Ameaça Crescente já deixa o dano do monstro
  // proporcional à vida efetiva do player (referência = Área 1, base ~9-10). Os bichos das
  // Áreas 2/3 têm base 22-52 e isso EMPILHAVA: entrar na Área 2 no nível certo = morrer em 2
  // golpes. Fator aplicado ao dano no spawn (tier e ameaça continuam por cima).
  // 10.9DY — Thiago: "os monstros aqui precisam ser mais fortes". O corte de 0.55/0.45 derrubava
  // o dano das areas 2/3 quase ao nivel da Floresta (craghorn 38 x 0.55 = 21, contra voidBrute 18
  // da area 1). Subindo pra 0.85/0.75 a area avancada bate MAIS FORTE de verdade, sem voltar ao
  // dano cru dos defs (que atropelaria quem acabou de entrar).
  regionDmgNorm: { forgottenForest: 1, shatteredWilds: 0.85, tempestReach: 0.75 } as Record<string, number>,
  // 10.9DY — VIDA por area. Faltava o par do regionDmgNorm: as bases da area 2 (45-90) sao ATE
  // MENORES que as da Floresta (o corruptedBrute tem 160), entao o bicho da area avancada morria
  // mais rapido que o da area inicial. Com isso a area 2/3 encosta no teto de golpes do tier dela.
  regionHpNorm: { forgottenForest: 1, shatteredWilds: 1.8, tempestReach: 2.6 } as Record<string, number>,
  // 10.9BE — anúncios recompensados (fase de testes: vídeo simulado). Limites diários em src/ads/Ads.ts.
  // 10.9K2 — Thiago: "dar uns diamantes em anúncio". Era 3 por vídeo (5/dia = 15💎); agora 5 (25💎/dia).
  ads: { freeDiamonds: 5, xpBoostSeconds: 900 },
  // PLANO DE ETAPAS (3 áreas + Profundidade):
  //   Área 1 Floresta  → sai Nv 10 (~1h)      · portão Área 2 = Nv 10
  //   Área 2 Partidas  → sai Nv 20 (~2-2,5h)  · portão Área 3 = Nv 20
  //   Área 3 Tempestade→ sai Nv ~32 (~3,5-4h) · fecha → Profundidade 2 (alvos +50%, XP +60%)
  // Conta (XP dos objetivos + missões + boss + este bônus) validada em simulação, ver 10.9AX.
  areaCompleteXp: { forgottenForest: 1500, shatteredWilds: 5000, tempestReach: 12000 } as Record<string, number>,
  // 10.9HS — AUDITORIA: 8/12/16 de 15/16/20 territórios abria o Portal Rift com metade do mapa
  // ainda trancado, e o abate já vinha de brinde só conquistando os territórios pedidos. Agora
  // pede TODOS os campeões de verdade e mais território/abate/baú — o Portal fecha o que sobrou.
  areaGoals: {
    forgottenForest: [
      { kind: "territories" as const, target: 12 },
      { kind: "champions" as const, target: 7 }, // todos os 7 campeões da Floresta
      { kind: "kills" as const, target: 250 },
      { kind: "chests" as const, target: 5 },
      { kind: "boss" as const, target: 1 },
    ],
    shatteredWilds: [
      { kind: "territories" as const, target: 14 },
      { kind: "champions" as const, target: 6 }, // todos os 6 campeões das Terras
      { kind: "kills" as const, target: 500 },
      { kind: "chests" as const, target: 8 },
      { kind: "boss" as const, target: 1 },
    ],
    tempestReach: [
      { kind: "territories" as const, target: 18 },
      { kind: "champions" as const, target: 7 }, // todos os 7 campeões da Tempestade
      { kind: "kills" as const, target: 800 },
      { kind: "chests" as const, target: 10 },
      { kind: "boss" as const, target: 1 },
    ],
  },
  // 10.9L3 — QUAL boss conta no objetivo "boss" de cada área. Antes só o Apex Fang contava (Floresta):
  // o Colosso não marcava as Terras e nada marcava a Tempestade — essas áreas NUNCA fechavam (a
  // Profundidade não avançava), e a viagem dependia do atalho "matou o boss → região liberada".
  // "guardian" = o Guardião da área (boss de área), pra região sem boss principal.
  areaGoalBoss: { forgottenForest: "voidAlpha", shatteredWilds: "aetherColossus", tempestReach: "guardian" } as Record<string, "voidAlpha" | "aetherColossus" | "guardian">,

  /**
   * 10.6H — PROFUNDIDADE (conteúdo sem fim). Fechar a Área 3 não termina o jogo: o mundo
   * reinicia na Área 1 com Profundidade +1. Monstros mais fortes, loot melhor e objetivos
   * de área maiores, para sempre. É o laço que impede o jogo de ser "zerado".
   */
  depth: {
    // 10.9BM — auditoria do endgame: a Ameaça JÁ escala vida/dano com o player; a Profundidade
    // multiplicava POR CIMA (+30%/+55% por volta) e na volta 4 o monstro comum matava em 1 golpe
    // e levava 16+ golpes. Agora a volta adiciona pressão moderada (+12% vida, +15% dano).
    hpPerDepth: 0.12, // era 0.3
    dmgPerDepth: 0.15, // era 0.55
    maxHpMult: 1.8, // teto do multiplicador de PROFUNDIDADE (a ameaça continua escalando por cima)
    maxDmgMult: 1.8,
    goalGrowth: 0.5, // +50% nos alvos dos objetivos de área por volta
    rewardPerDepth: 0.6, // +60% em Fragmentos e XP
    maxDepth: 1_000_000, // 10.9BN — Thiago: "impossível de zerar, tem que ser infinito" (era 99 — tinha fim)
  },

  /**
   * 10.6M — MISSÃO DIÁRIA. Uma por dia, trocada à meia-noite do aparelho. Prêmio bem acima
   * das rotativas: é o motivo de voltar amanhã. Não é energia nem espera — é um objetivo
   * a mais por cima do jogo normal.
   */
  daily: {
    targetPerLevel: 0.06, // alvo cresce 6% por nível do Caçador
    targetMaxMult: 2.5, // teto do crescimento (senão em nível alto vira inalcançável)
    coinsPerLevel: 6, // 10.9AS: era 14 — a diária escalava moeda rápido demais
    xpPerLevel: 26,
    templates: [ // 10.9AS — moedas ~40% menores
      { id: "d_kill", kind: "kill" as const, target: 60, coins: 85, xp: 320, shards: 3 },
      { id: "d_elite", kind: "elite" as const, target: 4, coins: 110, xp: 380, shards: 4 },
      // baú NÃO renasce no save: alvo baixo de propósito, senão a diária vira impossível
      { id: "d_chest", kind: "chest" as const, target: 2, coins: 90, xp: 300, shards: 3 },
      { id: "d_territory", kind: "territory" as const, target: 3, coins: 95, xp: 340, shards: 3 },
      { id: "d_hunt", kind: "kill" as const, target: 110, coins: 130, xp: 480, shards: 5 },
      { id: "d_boss", kind: "boss" as const, target: 1, coins: 160, xp: 560, shards: 6 },
      { id: "d_elite2", kind: "elite" as const, target: 7, coins: 145, xp: 500, shards: 5 },
    ],
  },

  /** 10.6N — dano do perigo ambiental (fração da vida MÁXIMA por segundo, sem o anel). */
  hazard: {
    dpsFraction: 0.055,
    graceSeconds: 2.5, // tempo de aviso antes de começar a queimar/congelar
    // 10.9L8 — BUG: a Loja vende o anel de proteção ÉPICO (Nv20 pra equipar), mas os 1ºs territórios
    // com perigo (T4, Terras/Tempestade) abrem no Nv16 (gateLevel na Profundidade 1). Quem comprava
    // no 16–19 pagava e não conseguia usar. Anel que PROTEGE equipa a partir deste nível.
    ringMinLevel: 16,
  },

  gate: {
    requiredElites: 3,
    barrierZ: -21, // Void Barrier atravessa o mapa aqui (10.5M: na divisa do Covil do Alfa)
    barrierWidth: 444, // 10.6Q: atravessa o mapa inteiro (mundo até 440+620 de largura)
    barrierHeight: 7,
    eliteMaxAlive: 3, // 10.9HS: era 2
    eliteSpawn: { zMin: -19, zMax: -6, xSpread: 34 },
  },

  boss: {
    // 10.5Z — BOSS escala com o nível do Caçador (estava fixo: em nível alto virava alvo parado)
    levelScale: { hpPerLevel: 0.09, dmgPerLevel: 0.06, maxHp: 3.2, maxDmg: 2.2 },
    // 10.9BF — FÚRIA: abaixo de 30% de vida o dano do boss sobe até ×1.7 em 0% (todos os bosses)
    fury: { below: 0.3, maxMult: 1.7 },
    // 10.9CK — COLEIRA do boss (Thiago: "ao desafiar o boss ficamos presos; libera pra andar pelo
    // mapa"). O player anda livre; o BOSS é quem fica na arena. Player a mais de `margin` m da borda
    // da arena por `seconds` s → o boss perde o alvo e a luta reseta (sem punição, desafia de novo).
    leash: { margin: 14, seconds: 6 },
    // 10.9DR — REI DO VAZIO (boss humanoide / comandante): invoca tropas + golpe pesado + onda de choque na fúria
    voidKing: {
      maxHp: 4200, radius: 1.5, armor: 6, moveSpeed: 3.0, introDuration: 1.6, restBetween: 0.7,
      arena: { center: { x: 0, z: 0 }, radius: 9 },
      slam: { range: 4.5, windup: 0.7, damage: 26, radius: 3.4, knockback: 10, cooldown: 3.2, recovery: 0.6 },
      summon: { count: 3, countFury: 5, windup: 0.8, cooldown: 9 },
      shockwave: { windup: 0.9, radius: 7, damage: 30, knockback: 12, cooldown: 8 },
      phase2: { hpBelow: 0.5, speedMult: 1.3, dmgMult: 1.2 },
    },
    voidAlpha: {
      // Rebalanceado na Fase 5: as armas da Fase 4 ~dobraram o DPS. Alvo: ~2min de combate
      // real para um player APROPRIADO (com esquivas), punindo o low-gear. Valor ajustável.
      maxHp: 2500,
      moveSpeed: 4,
      armor: 15,
      radius: 1.7,
      xpReward: 300,
      // 10.9AC — cada golpe leva TAMBÉM esta fatia da vida MÁX do player. O dano fixo virava
      // cosquinha em quem tem muita vida/gear; assim o boss continua ameaçador em qualquer nível.
      hpChipPct: 0.03, // 10.9AY: era 0.05 — é o PRIMEIRO boss; o chip alto somava com a ameaça
      phase2At: 0.5, // 50% HP -> Void Rage
      rageAt: 0.2, // 20% HP -> pressão final
      phase2SpeedMult: 1.15,
      phase2AttackScale: 0.85, // multiplica windups/cooldowns (menor = mais rápido)
      hookReactStagger: 0.25, // reação leve ao Hook
      interruptStagger: 1.5, // stagger ao interromper o Howl
      maxAdds: 4,
      introDuration: 2.0,
      restBetweenAttacks: 0.9, // respiro base (anti-chain)
      arena: { center: { x: 0, z: -42 }, radius: 15 },
      clawCombo: { approachRange: 5, range: 3.2, windup: 0.4, swipeGap: 0.32, swipeDamage: 14, heavyWindup: 0.5, heavyDamage: 24, recovery: 0.95, arc: Math.PI * 0.55, knockback: 4, cooldown: 2 },
      // 10.9AF — ATAQUE À DISTÂNCIA (telegrafado): erupções no chão na posição PREVISTA do player.
      // Sem isso o boss só machucava no contato (quem ficava longe não apanhava). Evitável (círculo de aviso).
      spikes: { windup: 0.9, count: 3, radius: 2.6, damage: 16, recovery: 0.5, cooldown: 4.5, spread: 3.6 },
      charge: { windup: 0.7, speed: 22, damage: 26, maxDist: 16, wallStagger: 1.5, recovery: 0.6, cooldown: 6, knockback: 10, width: 2.4 },
      howl: { windup: 1.2, radius: 7, damage: 18, knockback: 12, summonCount: 2, recovery: 1.0, cooldown: 10, interruptStart: 0.2, interruptEnd: 1.0 },
      leap: { windup: 0.7, airTime: 0.55, radius: 4.5, radiusRage: 5.5, damage: 28, recovery: 0.7, cooldown: 7, knockback: 8 },
      rewards: { xp: 300, essence: { power: 20, vitality: 15, ferocity: 12 } },
      refight: { xp: 80, essence: { power: 6, vitality: 5, ferocity: 4 } },
    },

    // ===================== FASE 9: AETHER COLOSSUS (Boss da Região 2) =====================
    aetherColossus: {
      maxHp: 7000,
      moveSpeed: 3.2,
      radius: 2.6,
      xpReward: 600,
      hpChipPct: 0.055, // 10.9AC — golpe leva também esta fatia da vida máx (ameaça em qualquer gear)
      phase2At: 0.55, // 55% -> Aether Core exposto
      finalAt: 0.2, // 20% -> pressão final
      introDuration: 2.6,
      restBetweenAttacks: 1.0,
      restRage: 0.7,
      staggerDuration: 2.2, // charge na parede / todas as plates quebradas
      phase2SpeedMult: 1.25,
      phase2AttackScale: 0.85, // menor = mais rápido
      arena: { center: { x: 0, z: -50 }, radius: 16 },
      // Crystal Armor: 3 placas. Redução de dano por nº de placas VIVAS [3,2,1,0].
      plates: {
        count: 3,
        hp: 780, // dano normal necessário p/ quebrar uma placa
        hookRip: 620, // dano que um Hook na janela EXPOSED causa à placa
        reductionByAlive: [0.45, 0.3, 0.16, 0] as number[], // 3,2,1,0 placas vivas
        exposeWindow: 1.6, // s de janela EXPOSED (charge na parede)
      },
      attacks: {
        stoneSweep: { windup: 0.7, arc: Math.PI * 0.9, range: 6.5, damage: 26, recovery: 0.9, cooldown: 4.5, knockback: 7 },
        fractureCharge: { telegraph: 0.9, speed: 22, damage: 30, maxDist: 20, recovery: 0.8, cooldown: 6.5, knockback: 11, wallStagger: 2.2 },
        shardRain: { windup: 1.0, zonesMin: 4, zonesMax: 7, radius: 2.6, damage: 22, zoneDelay: 0.9, cooldown: 7, spread: 11 },
        corePulse: { windup: 1.0, waveSpeed: 12, maxRadius: 12, damage: 24, recovery: 0.9, cooldown: 8, knockback: 8 },
        fractureLeap: { windup: 0.8, airTime: 0.6, radius: 5, damage: 30, recovery: 0.8, cooldown: 7, knockback: 9 },
        prismaticLine: { windup: 1.1, length: 30, width: 3, damage: 28, sweep: 1.2, cooldown: 8 },
        coreOverload: { channel: 3.2, zones: 6, radius: 2.6, damage: 26, zoneInterval: 0.5, cooldown: 12, anchorHookWindow: 3.0, interruptStagger: 2.0 },
      },
      rewards: { xp: 900, essence: { power: 40, vitality: 30, ferocity: 24, fortitude: 20 } },
      refight: { xp: 220, essence: { power: 12, vitality: 9, ferocity: 8 } },
    },

    // ===================== 10.9AG — RIFT SEER (boss CONJURADOR, Fase 4) =====================
    // Boss À DISTÂNCIA que kita e telegrafa TUDO: barragem no chão, feixe em linha, blink+nova
    // (janela de HOOK pra interromper) e anel expansivo. Exige leitura/posicionamento/timing —
    // não é "inimigo comum com HP alto". Usado como variante do evento (tiers ímpares) e refight.
    riftSeer: {
      maxHp: 3200,
      radius: 1.5,
      armor: 8,
      xpReward: 340,
      hpChipPct: 0.05, // golpe leva fatia da vida máx (ameaça em qualquer gear)
      introDuration: 2.0,
      phase2At: 0.5,          // 50% HP -> Sobrecarga (mais rápido + telegrafa em dobro)
      restBetweenAttacks: 1.1,
      restRage: 0.75,
      phase2AttackScale: 0.85, // menor = mais rápido
      moveSpeed: 3.6,
      hover: 2.3,             // altura visual (a LÓGICA fica no chão, y=0)
      keepDist: 8,           // distância que tenta manter do player (kita)
      arena: { radius: 15 }, // raio relativo ao ponto de spawn
      attacks: {
        // barragem: N erupções telegrafadas na posição PREVISTA do player (força movimento)
        barrage: { windup: 0.5, count: 5, radius: 2.2, damage: 15, delay: 0.7, cooldown: 5.5, spread: 5 },
        // feixe: linha reta telegrafada que dispara ao fim do windup
        beam: { windup: 0.95, length: 26, width: 3.4, damage: 28, recovery: 0.6, cooldown: 7 },
        // blink+nova: teleporta pra perto do player, canaliza (JANELA DE HOOK) e estoura AoE
        blinkNova: { windup: 0.8, channel: 1.2, radius: 6, damage: 32, recovery: 0.7, cooldown: 9,
          hookStart: 0.2, hookEnd: 1.0, interruptStagger: 1.8 },
        // anel: onda expansiva do boss pra fora (esquiva pela distância, como o corePulse)
        ring: { windup: 0.9, waveSpeed: 11, maxRadius: 13, damage: 24, recovery: 0.8, cooldown: 8 },
      },
      rewards: { xp: 420, essence: { power: 22, vitality: 16, ferocity: 14 } },
      refight: { xp: 110, essence: { power: 8, vitality: 6, ferocity: 5 } },
    },
  },

  // Colossus Gate + desbloqueio (progressão ambiental: derrotar Shard Wardens + descobrir sub-áreas)
  // 10.9L15 — BUG "o boss que libera a bota não existe" (Thiago). As 4 chaves de `discoveries`
  // eram os ids das SUB-ÁREAS de Terras Fraturadas (regions[].subAreas: crystalFlats/brokenPass/
  // aetherCliffs/wardenGrounds — existem de verdade, ver GameConfig.world.regions), só que nada no
  // jogo chama `region.discover()` pra elas: essa chamada só existe pra Floresta (altar/alphaDen) e
  // pros TERRITÓRIOS (`terr:<id>`, em Game.updateRegionAndGate). `colossusUnlocked()` exigia
  // `disc[k] === true` pras 4 sub-áreas — sempre falso, nunca setado — então o Colosso NUNCA ficava
  // desafiável e o slot de BOTAS (só ele libera, `equipment.slotUnlock.boots`) nunca abria pra
  // ninguém. Corrigido usando o mecanismo de território que JÁ FUNCIONA, com o território que fica
  // dentro de cada sub-área homônima: t_flats (Planície de Cristal), t_brokenpass (Passagem
  // Quebrada), t_cliffs (Falésias de Éter) e t_wardens (Terreno dos Wardens, onde os 3 Shard
  // Wardens rondam).
  colossus: {
    gate: { x: 0, z: -46, interactRadius: 3.2 },
    unlock: { wardens: 3, discoveries: ["terr:t_flats", "terr:t_brokenpass", "terr:t_cliffs", "terr:t_wardens"] as string[] },
  },

  // ===================== FASE 10: REGIONAL MASTERY (§30-44) =====================
  // Maestria POR REGIÃO, obtida JOGANDO na região (kills/elites/boss/discoveries/chests/
  // surges). Nunca comprada. Recompensas play-to-progress; perks region-scoped MODERADOS
  // (§42/§43) — nada de +100% global. Desbloqueia tiers de Rift Surge (§37).
  regionMastery: {
    maxLevel: 20,
    // XP necessária para ir do nível (n-1) -> n (crescente)
    xpPerLevel: (n: number): number => Math.floor(140 * Math.pow(n, 1.25)),
    xp: { perKill: 6, perElite: 32, perBoss: 140, perDiscovery: 30, perChest: 24 },
    surgeUnlock: { tier2: 4, tier3: 8 }, // Surge I = região concluída; II/III por maestria (§37)
    // perks acumulados aplicados SÓ nesta região (moderados)
    perks: { essenceAtLv2: 0.05, essenceAtLv20: 0.05, lootAtLv6: 0.02, lootAtLv20: 0.02 },
    // trilha de recompensas (§41) — kind dirige o efeito/ícone na UI
    rewards: [
      { level: 2, kind: "essence" },
      { level: 4, kind: "chest" },
      { level: 6, kind: "loot" },
      { level: 8, kind: "surge2" },
      { level: 10, kind: "title" },
      { level: 12, kind: "chest" },
      { level: 15, kind: "surge3" },
      { level: 20, kind: "mastered" },
    ] as { level: number; kind: string }[],
  },

  // ===================== FASE 10: RIFT SURGES (§33-40, §48) =====================
  // Dificuldade OPCIONAL para regiões concluídas. NÃO é daily/energia/timer (§34, §47).
  // Modificadores LEVES (nunca HP×10 §36); recompensa maestria/essência/loot.
  riftSurge: {
    tiers: [
      { tier: 1, hpMult: 1.25, dmgMult: 1.2, eliteChance: 0.15, masteryMult: 1.5, lootMult: 1.3, essenceMult: 1.4, objKills: 12, objElites: 1 },
      { tier: 2, hpMult: 1.55, dmgMult: 1.38, eliteChance: 0.28, masteryMult: 2.0, lootMult: 1.6, essenceMult: 1.8, objKills: 16, objElites: 2 },
      { tier: 3, hpMult: 1.9, dmgMult: 1.6, eliteChance: 0.42, masteryMult: 2.6, lootMult: 2.0, essenceMult: 2.2, objKills: 20, objElites: 3 },
    ],
  },

  audio: {
    defaults: { master: 0.8, music: 0.6, sfx: 0.8 },
  },

  // 10.9IC — LOGIN SOCIAL (Google/Facebook). O código está pronto e testado, mas os botões só
  // aparecem quando `social: true`, porque eles dependem de configuração no PAINEL (Supabase +
  // console do Google + app do Facebook). Sem isso o jogador clicaria e tomaria erro.
  // PRA LIGAR (Thiago): 1) criar as credenciais no Google Cloud e no Facebook Developers;
  // 2) colar as duas no Supabase > Authentication > Providers; 3) em URL Configuration, pôr
  // https://void-hunter.pages.dev em Site URL e em Redirect URLs; 4) ligar "Manual linking"
  // (Auth > Settings) pra conta de convidado virar conta de verdade SEM perder o progresso;
  // 5) trocar este campo pra true. O "ID do Void" (e-mail+senha) já funciona hoje, sem nada disso.
  // 10.9ID — Google LIGADO e validado (provedor ativo no Supabase, Site/Redirect URLs corretas,
  // Manual linking ativo; o fluxo real sai pra accounts.google.com sem erro). Facebook ainda não
  // foi configurado no painel, então o botão dele segue escondido por `facebook: false`.
  auth: { social: true, google: true, facebook: false },

  // Presets de qualidade (mobile-first). Arquitetura pronta; default MEDIUM.
  // Foco: smartphones intermediários — cap de pixelRatio, sombras e partículas moduláveis.
  // 10.9HY — Thiago: "otimize pra celulares low, eles estão sofrendo... adaptável a qualquer tipo
  // de celular". Antes o preset só mexia em 6 coisas (pixelRatio/sombra/partícula/bloom/normal) e
  // TUDO que pesa de verdade era fixo: 220-260 inimigos vivos, 10 territórios ativos, corte de
  // cenário a 150 m, barra de vida de todo inimigo, minimapa a 12 Hz. Medido no desktop com 7
  // inimigos: 395 draw calls / 120k triângulos / 6.711 meshes visíveis por quadro — num aparelho
  // fraco isso é 15-25 fps. Agora cada preset controla também a CARGA DE CENA, e `low` é de fato
  // baixo. Ver `Quality.ts` (detecção automática do aparelho) e o governador de fps no Game.
  quality: {
    default: "medium",
    presets: {
      low: {
        pixelRatioCap: 1.0, shadows: false, shadowMapSize: 1024, particleScale: 0.35, bloom: false, normalMaps: false,
        enemyScale: 0.35,      // teto de inimigos vivos e por território (220 -> 77, 50 -> 17)
        activeTerritories: 4,  // territórios que geram horda ao mesmo tempo (era fixo 10)
        cullDist: 110,         // corte do cenário instanciado em metros (era fixo 150)
        fogBoost: 1.5,         // névoa mais densa: esconde o corte mais curto (ver Quality.ts)
        renderScale: 0.8,      // desenha 36% menos pixels (o gargalo do celular é fill rate)
        groundTexSize: 256,    // textura de chão gera 4× mais rápido (trava menos ao entrar na área)
        enemyBars: 8,          // barras de vida na tela ao mesmo tempo (era ilimitado)
        minimapHz: 6,          // redesenho do minimapa (era fixo 12)
        decorShadows: false,   // cenário não projeta sombra (metade das draw calls da sombra)
      },
      medium: {
        pixelRatioCap: 1.5, shadows: true, shadowMapSize: 1024, particleScale: 0.8, bloom: true, normalMaps: true,
        enemyScale: 0.7, activeTerritories: 7, cullDist: 130, enemyBars: 16, minimapHz: 10, decorShadows: false, fogBoost: 1.15, renderScale: 1.0, groundTexSize: 512,
      },
      high: {
        pixelRatioCap: 2.0, shadows: true, shadowMapSize: 2048, particleScale: 1.0, bloom: true, normalMaps: true,
        enemyScale: 1.0, activeTerritories: 10, cullDist: 150, enemyBars: 28, minimapHz: 12, decorShadows: true, fogBoost: 1.0, renderScale: 1.0, groundTexSize: 512,
      },
    },
  },

  camera: {
    // Fase 8: mais perto e com tilt ARPG (~55°) — personagem maior/presente, boa leitura
    // do entorno e dos telegraphs (referência: câmera action-RPG mobile).
    offset: { x: 0, y: 18, z: 12.5 },
    positionDamping: 7,
    lookDamping: 9,
  },

  respawn: {
    delay: 2, // s de "morto" antes de teleportar pra Sanctuary
    hpFraction: 0.3, // reaparece com 30% do MaxHP (a base regenera o resto)
    invuln: 2.0, // proteção pós-respawn
  },

  // ===================== FASE 4: EQUIPAMENTO / LOOT / MAESTRIA =====================
  rarity: {
    order: ["common", "uncommon", "rare", "epic", "legendary", "mythic"] as const,
    // multiplicador do atributo base + quantidade de atributos secundários (ajustável)
    mult: { common: 1.0, uncommon: 1.08, rare: 1.18, epic: 1.32, legendary: 1.5, mythic: 1.75 },
    // 10.9L16 — Thiago: "quero que os itens deem mais atributos pra os players". +1 em toda
    // raridade (comum SAI DO ZERO: até o item mais fraco agora vem com um bônus, não só o dano/
    // armadura base). Os pools de cabeça e bota cresceram junto (eram os únicos menores que o
    // novo teto do mítico, 6 — ver `pools` logo abaixo) pra não capar o mítico nessas peças.
    secondary: { common: 1, uncommon: 2, rare: 3, epic: 4, legendary: 5, mythic: 6 },
    // pesos de sorteio por FONTE (relativos; MYTHIC quase inexistente nesta região)
    weights: {
      normal: { common: 62, uncommon: 26, rare: 9, epic: 2.6, legendary: 0.4, mythic: 0 },
      elite: { common: 30, uncommon: 34, rare: 24, epic: 9, legendary: 2.5, mythic: 0.5 },
      boss: { common: 0, uncommon: 20, rare: 45, epic: 25, legendary: 9, mythic: 1 },
      wooden: { common: 70, uncommon: 30, rare: 0, epic: 0, legendary: 0, mythic: 0 },
      void: { common: 20, uncommon: 45, rare: 30, epic: 5, legendary: 0, mythic: 0 },
      // 10.7F — DROPS MAIS DIFÍCEIS: ~40-60% menos legendary/mythic (Thiago pediu)
      alpha: { common: 5, uncommon: 25, rare: 48, epic: 18, legendary: 3.5, mythic: 0.5 },
      wilds: { common: 45, uncommon: 32, rare: 17, epic: 4.5, legendary: 1.2, mythic: 0.3 },
      warden: { common: 15, uncommon: 32, rare: 36, epic: 13, legendary: 3.5, mythic: 0.5 },
      ruined: { common: 50, uncommon: 38, rare: 10, epic: 2, legendary: 0, mythic: 0 },
      shard: { common: 18, uncommon: 40, rare: 32, epic: 8, legendary: 1.7, mythic: 0.3 },
      colossus: { common: 3, uncommon: 20, rare: 46, epic: 22, legendary: 7.5, mythic: 1.5 },
      tempest: { common: 30, uncommon: 36, rare: 24, epic: 7.5, legendary: 2, mythic: 0.5 },
      stormWarden: { common: 8, uncommon: 26, rare: 40, epic: 18, legendary: 6.5, mythic: 1.5 },
      skyruin: { common: 34, uncommon: 42, rare: 18, epic: 4.5, legendary: 1.2, mythic: 0.3 },
      storm: { common: 8, uncommon: 30, rare: 42, epic: 15, legendary: 4, mythic: 1 },
    },
  },

  equipment: {
    capacity: 30,
    // 10.9HA — MOCHILA MAIOR (loja de 💎): +step por compra até max; preço = base + priceStep × compras
    bagUpgrade: { step: 10, max: 100, priceStep: 20 },
    // desbloqueio de slots por PROGRESSÃO (nunca por dinheiro)
    slotUnlock: {
      weapon: "start", // sempre liberado
      head: "elite", // após 1º Elite
      chest: "gate", // após abrir Alpha Den
      accessory: "boss", // após derrotar Void Alpha
      gloves: "boss", // Fase 10: após Void Alpha (junto com accessory)
      boots: "colossus", // Fase 10: após Aether Colossus
    },
    // Slots de ARMA (loadout da barra inferior). Slot 1 já liberado; os demais desbloqueiam
    // por progressão (nível); o último é VIP (teremos VIP). Só informa/desbloqueia — sem billing.
    // 10.9J7 — o 5º slot ("vip") dava +1 arma na Corrente SÓ pra quem tinha VIP, desde o Nv1: poder
    // de combate real vendido por 💎, contradizendo até o comentário do slotUnlock logo acima
    // ("nunca por dinheiro"). Removido — volta a ser só por NÍVEL, igual pra todo mundo, como esta
    // lista sempre disse que era a regra.
    weaponSlots: [
      { unlock: "start" },
      { unlock: "level", level: 10 },
      { unlock: "level", level: 20 },
      { unlock: "level", level: 30 },
      // 10.9L0 — Thiago: "o slot da arma seria um diferencial pra fazer o personagem bater mais, uma
      // vantagem pra quem é VIP". VOLTOU (tinha saído na 10.9J7 pela regra "VIP = só conteúdo"): +1
      // arma na Corrente pra quem tem QUALQUER tier VIP ou o Passe VIP ativo (Vip.weaponSlotUnlocked).
      { unlock: "vip" },
    ] as ReadonlyArray<{ unlock: "start" } | { unlock: "level"; level: number } | { unlock: "vip" }>,
    // 10.9P — FORJA UNIFICADA: cada item sobe de 1→250 gastando SHARDS, com CHANCE DE QUEBRA
    // (falha = cai 1 nível) acima do nível seguro; o ELIXIR de Proteção impede a queda. É o
    // trilho ÚNICO de evolução (o "weapon level" por cópias foi aposentado — menos confusão).
    upgradeMaxLevel: 250,
    // custo (shards) para ir de `level`→`level+1` (cresce devagar; endgame é grind longo de propósito)
    upgradeCostFn: (level: number): number => 2 + Math.floor(level * 0.8),
    // 10.9BI — a forja também custa FRAGMENTOS 🪙 (Thiago: "pra ter o que gastar fragmentos").
    // Cresce com o nível e com a raridade (comum ×1 … mítico ×3.5). L1 comum 13🪙 · L50 raro 320🪙 · L150 épico 1.4k🪙
    upgradeCoinsFn: (level: number, rarityIdx: number): number => Math.round((10 + level * 3) * (1 + rarityIdx * 0.5)),
    forgeSafeLevel: 15, // sem risco de quebra até aqui (early tranquilo)
    forgeBreakPerLevel: 0.004, // +0,4% de chance de quebra por nível acima do seguro
    // 10.9K2 — AUDITORIA DE JUSTIÇA: era 0.5. Com 50% de quebra (−1) e 50% de acerto (+1), acima do
    // Nv~140 o progresso ESPERADO sem Elixir era ZERO — um muro que só o Elixir (vendido por 💎)
    // atravessava. Com 40%: sem Elixir +0,2 nível/tentativa (lento, mas anda); com Elixir +0,6
    // (continua 3× mais rápido — acelera, não é mais a única saída). Elixir também sai jogando agora
    // (baú semanal do login).
    forgeBreakMax: 0.4,
    // 10.9K9 — EVOLUÇÃO DE ARMA (Thiago; substitui a fusão de 5 iguais da 10.9K5): a arma repetida
    // (MESMA arma e MESMA raridade de uma que o jogador já tem) entra DIRETO na arma — +1 numa barra
    // de evolução — e continua dando os fragmentos da forja de sempre. Com `copies` na barra, a arma
    // EVOLUI pra raridade seguinte (comum → … → mítico) mantendo o nível de forja — só se o Caçador
    // já puder equipar a raridade nova (senão a barra espera cheia, nada se perde). Raridade
    // diferente não conta: entra na bolsa como arma própria (com a barra dela). Armadura não evolui.
    evolution: { copies: 100 },
    // Fase 10 (§15/§16): WEAPON LEVEL por DEFINIÇÃO (copies de jogar). Auto level-up;
    // sobra preservada. Poder MODERADO; separado da Weapon Mastery. Milestones passivos.
    weaponLevel: {
      maxLevel: 30,
      // cópias para ir do nível (n) -> (n+1)
      copiesForLevel: (n: number): number => 2 + Math.floor(n * 1.4),
      damagePerLevel: 0.09, // 10.7O: +9% por Weapon Level (L30 = +261%) — escala forte
      milestones: [
        { level: 5, key: "wm.crit" },
        { level: 10, key: "wm.speed" },
        { level: 15, key: "wm.damage" },
        { level: 20, key: "wm.crit2" },
        { level: 30, key: "wm.master" },
      ] as { level: number; key: string }[],
      // bônus passivos por milestone (folded no recalc; sem novos botões)
      milestoneBonus: { critChance: 0.03, attackSpeed: 0.05, damageMult: 0.05 },
    },
    upgradeDamagePerLevel: 0.09, // 10.9P: +9% do baseDamage por nível de forja (L250 ≈ ×23,4) — era 0.22 c/ cap 10
    upgradeArmorPerLevel: 0.06,  // 10.9P: +6% por nível (L250 ≈ ×15,9)
    // 10.9L23 — Thiago viu uma arma Nv51 e os atributos secundários dela (crítico, vel. ataque,
    // dano arcano...) continuavam EXATAMENTE o valor do drop — só o dano/armadura base (acima)
    // escalava com a forja. Mais suave que o dano base pra não explodir % (crítico, bloqueio...):
    // L51 ≈ ×1,5 · L250 ≈ ×3,49.
    upgradeRollPerLevel: 0.01,
    // 10.9M — VENDA de itens por 🪙 (esvaziar a bolsa). Valor por raridade + leve bônus por
    // nível de forja do item. Usado na venda manual (selecionar vários) e na auto-venda.
    sell: {
      byRarity: { common: 8, uncommon: 18, rare: 45, epic: 110, legendary: 280, mythic: 700 } as Record<string, number>,
      perLevelPct: 0.12, // +12% por nível de forja do item
    },
    // pools de atributos secundários por slot: [min, max] (antes da raridade)
    pools: {
      weapon: [
        { key: "damage", min: 2, max: 6 },
        { key: "attackSpeed", min: 0.03, max: 0.08 },
        { key: "critChance", min: 0.02, max: 0.05 },
        { key: "critDamage", min: 0.05, max: 0.15 },
        { key: "attackRange", min: 0.2, max: 0.5 },
        // 10.5T — armas passam a rolar dano por TIPO (combina com a Árvore do Void)
        { key: "dmgSlash", min: 0.03, max: 0.09 },
        { key: "dmgPierce", min: 0.03, max: 0.09 },
        { key: "dmgBlunt", min: 0.03, max: 0.09 },
        { key: "lifesteal", min: 0.01, max: 0.03 }, // 10.9EQ — ROUBO DE VIDA (armas)
      ],
      head: [
        { key: "maxHp", min: 8, max: 20 },
        { key: "armor", min: 2, max: 6 },
        { key: "moveSpeed", min: 0.01, max: 0.03 },
        { key: "defPierce", min: 0.03, max: 0.08 }, // 10.5T
        { key: "regen", min: 0.002, max: 0.005 },
        { key: "critChance", min: 0.015, max: 0.04 }, // 10.9L16 — pool tinha só 5 chaves, o mítico (6) capava sem essa
      ],
      chest: [
        { key: "maxHp", min: 14, max: 30 },
        { key: "maxHpPct", min: 0.03, max: 0.08 }, // 10.9FR — % de vida (peito forte)
        { key: "armor", min: 3, max: 8 },
        { key: "moveSpeed", min: 0.01, max: 0.02 },
        { key: "defSlash", min: 0.03, max: 0.09 }, // 10.5T
        { key: "defBlunt", min: 0.03, max: 0.09 },
        { key: "block", min: 0.02, max: 0.06 },
      ],
      accessory: [
        { key: "critChance", min: 0.02, max: 0.05 },
        { key: "maxHpPct", min: 0.03, max: 0.07 }, // 10.9FR
        { key: "damagePct", min: 0.03, max: 0.07 }, // 10.9FR
        { key: "attackSpeed", min: 0.03, max: 0.06 },
        { key: "moveSpeed", min: 0.02, max: 0.04 },
        { key: "critDamage", min: 0.05, max: 0.12 },
        { key: "regen", min: 0.002, max: 0.006 }, // 10.5T
        { key: "lifesteal", min: 0.01, max: 0.025 }, // 10.9EQ — ROUBO DE VIDA (aneis)
        { key: "dmgSlash", min: 0.03, max: 0.08 },
        { key: "dmgPierce", min: 0.03, max: 0.08 },
      ],
      // Fase 10 — luvas (ataque) e botas (mobilidade/defesa), §14
      gloves: [
        { key: "attackSpeed", min: 0.03, max: 0.07 },
        { key: "damagePct", min: 0.03, max: 0.08 }, // 10.9FR — % de dano (luvas fortes)
        { key: "critChance", min: 0.02, max: 0.05 },
        { key: "damage", min: 2, max: 5 },
        { key: "block", min: 0.02, max: 0.05 }, // 10.5T
        { key: "dmgBlunt", min: 0.03, max: 0.07 },
      ],
      boots: [
        { key: "moveSpeed", min: 0.02, max: 0.05 },
        { key: "armor", min: 2, max: 6 },
        { key: "maxHp", min: 6, max: 16 },
        { key: "defPierce", min: 0.03, max: 0.07 }, // 10.5T
        // 10.9L16 — pool tinha só 4 chaves, o mítico (6) capava em 4 mesmo. +2 pra caber.
        { key: "block", min: 0.015, max: 0.04 },
        { key: "regen", min: 0.0015, max: 0.004 },
      ],
    },
    // catálogo de ARMAS (4 arquétipos)
    weapons: [
      { id: "voidBlade", nameKey: "weapon.sword", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 11, attackRange: 2.4, attacksPerSecond: 1.4, arc: Math.PI * 0.7, knockback: 4, projectileSpeed: 0, visualId: "sword" },
      { id: "voidCleaver", nameKey: "weapon.axe", slot: "weapon", weaponType: "axe", attackType: "melee", damageType: "slashing", baseDamage: 21, attackRange: 2.6, attacksPerSecond: 0.8, arc: Math.PI * 0.85, knockback: 7, projectileSpeed: 0, visualId: "axe" },
      { id: "voidBow", nameKey: "weapon.bow", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 12, attackRange: 9, attacksPerSecond: 1.35, arc: 0, knockback: 2, projectileSpeed: 30, visualId: "bow" },
      { id: "voidMace", nameKey: "weapon.mace", slot: "weapon", weaponType: "mace", attackType: "melee", damageType: "blunt", baseDamage: 25, attackRange: 2.5, attacksPerSecond: 0.72, arc: Math.PI * 0.6, knockback: 9, projectileSpeed: 0, visualId: "mace" },
      // 10.9AD — CAJADO (arma do Mago/Necromante): à distância, bolt arcano. Alcance e cadência
      // próprios (mais dano por tiro que o arco, um tico mais lento).
      { id: "voidStaff", nameKey: "weapon.staff", slot: "weapon", weaponType: "staff", attackType: "ranged", damageType: "arcane", baseDamage: 15, attackRange: 9.5, attacksPerSecond: 1.15, arc: 0, knockback: 3, projectileSpeed: 30, visualId: "staff" },
      // Fase 8 — armas Tier 2 (Shattered Wilds): identidade visual própria (visualId)
      { id: "shardSaber", nameKey: "weapon.shardSaber", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 17, attackRange: 2.5, attacksPerSecond: 1.5, arc: Math.PI * 0.7, knockback: 4, projectileSpeed: 0, visualId: "shardSaber" },
      // Fase 9 — drops únicos do Aether Colossus (identidade própria, sem power creep enorme)
      { id: "colossusEdge", bossOnly: true, nameKey: "weapon.colossusEdge", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 23, attackRange: 2.6, attacksPerSecond: 1.45, arc: Math.PI * 0.72, knockback: 6, projectileSpeed: 0, visualId: "shardSaber" },
      { id: "aetherLongbow", bossOnly: true, nameKey: "weapon.aetherLongbow", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 18, attackRange: 10, attacksPerSecond: 1.4, arc: 0, knockback: 3, projectileSpeed: 32, visualId: "bow" },
      // Fase 10 — armas Tier 3 (Tempest Reach): identidade própria (visualId), sem power creep enorme
      { id: "galeBlade", bossOnly: true, nameKey: "weapon.galeBlade", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 22, attackRange: 2.5, attacksPerSecond: 1.5, arc: Math.PI * 0.7, knockback: 4, projectileSpeed: 0, visualId: "galeBlade" },
      { id: "tempestCleaver", bossOnly: true, nameKey: "weapon.tempestCleaver", slot: "weapon", weaponType: "axe", attackType: "melee", damageType: "slashing", baseDamage: 35, attackRange: 2.7, attacksPerSecond: 0.82, arc: Math.PI * 0.85, knockback: 8, projectileSpeed: 0, visualId: "tempestCleaver" },
      { id: "stormMaul", bossOnly: true, nameKey: "weapon.stormMaul", slot: "weapon", weaponType: "mace", attackType: "melee", damageType: "blunt", baseDamage: 42, attackRange: 2.6, attacksPerSecond: 0.7, arc: Math.PI * 0.62, knockback: 12, projectileSpeed: 0, visualId: "mace" },
      { id: "arcLongbow", bossOnly: true, nameKey: "weapon.arcLongbow", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 20, attackRange: 10, attacksPerSecond: 1.42, arc: 0, knockback: 3, projectileSpeed: 33, visualId: "arcLongbow" },
      { id: "aetherScepter", bossOnly: true, nameKey: "weapon.aetherScepter", slot: "weapon", weaponType: "staff", attackType: "ranged", damageType: "arcane", baseDamage: 24, attackRange: 10, attacksPerSecond: 1.2, arc: 0, knockback: 4, projectileSpeed: 32, visualId: "staff" },
      // 10.9FX — ECLIPSE (Tier 4 · Nv150): armas de fim de jogo que caem do BOSS GLOBAL (e de bosses).
      // Base ALTA + rolam % de dano (pools) — "baseado no dano e uma porcentagem" (pedido do Thiago).
      { id: "eclipseBlade", bossOnly: true, tier: 7, nameKey: "weapon.eclipseBlade", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 40, attackRange: 2.6, attacksPerSecond: 1.5, arc: Math.PI * 0.72, knockback: 5, projectileSpeed: 0, visualId: "galeBlade" },
      { id: "eclipseCleaver", bossOnly: true, tier: 7, nameKey: "weapon.eclipseCleaver", slot: "weapon", weaponType: "axe", attackType: "melee", damageType: "slashing", baseDamage: 58, attackRange: 2.7, attacksPerSecond: 0.84, arc: Math.PI * 0.85, knockback: 9, projectileSpeed: 0, visualId: "tempestCleaver" },
      { id: "eclipseMaul", bossOnly: true, tier: 7, nameKey: "weapon.eclipseMaul", slot: "weapon", weaponType: "mace", attackType: "melee", damageType: "blunt", baseDamage: 70, attackRange: 2.6, attacksPerSecond: 0.72, arc: Math.PI * 0.62, knockback: 13, projectileSpeed: 0, visualId: "mace" },
      { id: "eclipseBow", bossOnly: true, tier: 7, nameKey: "weapon.eclipseBow", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 34, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "arcLongbow" },
      { id: "eclipseStaff", bossOnly: true, tier: 7, nameKey: "weapon.eclipseStaff", slot: "weapon", weaponType: "staff", attackType: "ranged", damageType: "arcane", baseDamage: 42, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "staff" },
      // 10.9GG — ÚNICOS do BOSS GLOBAL (só caem na raid). Fortes e com visual próprio.
      { id: "reaperEclipse", bossOnly: true, raidOnly: true, tier: 6, nameKey: "weapon.reaperEclipse", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 50, attackRange: 2.8, attacksPerSecond: 1.55, arc: Math.PI * 0.78, knockback: 6, projectileSpeed: 0, visualId: "galeBlade" },
      { id: "ruinEclipse", bossOnly: true, raidOnly: true, tier: 6, nameKey: "weapon.ruinEclipse", slot: "weapon", weaponType: "axe", attackType: "melee", damageType: "slashing", baseDamage: 72, attackRange: 2.9, attacksPerSecond: 0.86, arc: Math.PI * 0.9, knockback: 11, projectileSpeed: 0, visualId: "tempestCleaver" },
      { id: "collapseEclipse", bossOnly: true, raidOnly: true, tier: 6, nameKey: "weapon.collapseEclipse", slot: "weapon", weaponType: "mace", attackType: "melee", damageType: "blunt", baseDamage: 88, attackRange: 2.7, attacksPerSecond: 0.74, arc: Math.PI * 0.66, knockback: 15, projectileSpeed: 0, visualId: "mace" },
      { id: "omenEclipse", bossOnly: true, raidOnly: true, tier: 6, nameKey: "weapon.omenEclipse", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 42, attackRange: 11, attacksPerSecond: 1.5, arc: 0, knockback: 4, projectileSpeed: 36, visualId: "arcLongbow" },
      { id: "voidEclipse", bossOnly: true, raidOnly: true, tier: 6, nameKey: "weapon.voidEclipse", slot: "weapon", weaponType: "staff", attackType: "ranged", damageType: "arcane", baseDamage: 52, attackRange: 11, attacksPerSecond: 1.28, arc: 0, knockback: 5, projectileSpeed: 36, visualId: "staff" },
      // 10.9FZ — TIERS DE FIM DE JOGO (Nv 200..1000): so caem de bosses (gate por nivel).
      { id: "umbralSword", bossOnly: true, tier: 8, nameKey: "weapon.umbralSword", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 56, attackRange: 2.6, attacksPerSecond: 1.5, arc: Math.PI * 0.72, knockback: 5, projectileSpeed: 0, visualId: "galeBlade" },
      { id: "umbralAxe", bossOnly: true, tier: 8, nameKey: "weapon.umbralAxe", slot: "weapon", weaponType: "axe", attackType: "melee", damageType: "slashing", baseDamage: 81, attackRange: 2.7, attacksPerSecond: 0.84, arc: Math.PI * 0.85, knockback: 9, projectileSpeed: 0, visualId: "tempestCleaver" },
      { id: "umbralMace", bossOnly: true, tier: 8, nameKey: "weapon.umbralMace", slot: "weapon", weaponType: "mace", attackType: "melee", damageType: "blunt", baseDamage: 98, attackRange: 2.6, attacksPerSecond: 0.72, arc: Math.PI * 0.62, knockback: 13, projectileSpeed: 0, visualId: "mace" },
      { id: "umbralBow", bossOnly: true, tier: 8, nameKey: "weapon.umbralBow", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 48, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "arcLongbow" },
      { id: "umbralStaff", bossOnly: true, tier: 8, nameKey: "weapon.umbralStaff", slot: "weapon", weaponType: "staff", attackType: "ranged", damageType: "arcane", baseDamage: 59, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "staff" },
      { id: "astralSword", bossOnly: true, tier: 9, nameKey: "weapon.astralSword", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 72, attackRange: 2.6, attacksPerSecond: 1.5, arc: Math.PI * 0.72, knockback: 5, projectileSpeed: 0, visualId: "galeBlade" },
      { id: "astralAxe", bossOnly: true, tier: 9, nameKey: "weapon.astralAxe", slot: "weapon", weaponType: "axe", attackType: "melee", damageType: "slashing", baseDamage: 104, attackRange: 2.7, attacksPerSecond: 0.84, arc: Math.PI * 0.85, knockback: 9, projectileSpeed: 0, visualId: "tempestCleaver" },
      { id: "astralMace", bossOnly: true, tier: 9, nameKey: "weapon.astralMace", slot: "weapon", weaponType: "mace", attackType: "melee", damageType: "blunt", baseDamage: 126, attackRange: 2.6, attacksPerSecond: 0.72, arc: Math.PI * 0.62, knockback: 13, projectileSpeed: 0, visualId: "mace" },
      { id: "astralBow", bossOnly: true, tier: 9, nameKey: "weapon.astralBow", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 61, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "arcLongbow" },
      { id: "astralStaff", bossOnly: true, tier: 9, nameKey: "weapon.astralStaff", slot: "weapon", weaponType: "staff", attackType: "ranged", damageType: "arcane", baseDamage: 76, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "staff" },
      { id: "sideralSword", bossOnly: true, tier: 10, nameKey: "weapon.sideralSword", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 92, attackRange: 2.6, attacksPerSecond: 1.5, arc: Math.PI * 0.72, knockback: 5, projectileSpeed: 0, visualId: "galeBlade" },
      { id: "sideralAxe", bossOnly: true, tier: 10, nameKey: "weapon.sideralAxe", slot: "weapon", weaponType: "axe", attackType: "melee", damageType: "slashing", baseDamage: 133, attackRange: 2.7, attacksPerSecond: 0.84, arc: Math.PI * 0.85, knockback: 9, projectileSpeed: 0, visualId: "tempestCleaver" },
      { id: "sideralMace", bossOnly: true, tier: 10, nameKey: "weapon.sideralMace", slot: "weapon", weaponType: "mace", attackType: "melee", damageType: "blunt", baseDamage: 161, attackRange: 2.6, attacksPerSecond: 0.72, arc: Math.PI * 0.62, knockback: 13, projectileSpeed: 0, visualId: "mace" },
      { id: "sideralBow", bossOnly: true, tier: 10, nameKey: "weapon.sideralBow", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 78, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "arcLongbow" },
      { id: "sideralStaff", bossOnly: true, tier: 10, nameKey: "weapon.sideralStaff", slot: "weapon", weaponType: "staff", attackType: "ranged", damageType: "arcane", baseDamage: 97, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "staff" },
      { id: "stellarSword", bossOnly: true, tier: 11, nameKey: "weapon.stellarSword", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 128, attackRange: 2.6, attacksPerSecond: 1.5, arc: Math.PI * 0.72, knockback: 5, projectileSpeed: 0, visualId: "galeBlade" },
      { id: "stellarAxe", bossOnly: true, tier: 11, nameKey: "weapon.stellarAxe", slot: "weapon", weaponType: "axe", attackType: "melee", damageType: "slashing", baseDamage: 186, attackRange: 2.7, attacksPerSecond: 0.84, arc: Math.PI * 0.85, knockback: 9, projectileSpeed: 0, visualId: "tempestCleaver" },
      { id: "stellarMace", bossOnly: true, tier: 11, nameKey: "weapon.stellarMace", slot: "weapon", weaponType: "mace", attackType: "melee", damageType: "blunt", baseDamage: 224, attackRange: 2.6, attacksPerSecond: 0.72, arc: Math.PI * 0.62, knockback: 13, projectileSpeed: 0, visualId: "mace" },
      { id: "stellarBow", bossOnly: true, tier: 11, nameKey: "weapon.stellarBow", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 109, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "arcLongbow" },
      { id: "stellarStaff", bossOnly: true, tier: 11, nameKey: "weapon.stellarStaff", slot: "weapon", weaponType: "staff", attackType: "ranged", damageType: "arcane", baseDamage: 134, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "staff" },
      { id: "nebularSword", bossOnly: true, tier: 12, nameKey: "weapon.nebularSword", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 172, attackRange: 2.6, attacksPerSecond: 1.5, arc: Math.PI * 0.72, knockback: 5, projectileSpeed: 0, visualId: "galeBlade" },
      { id: "nebularAxe", bossOnly: true, tier: 12, nameKey: "weapon.nebularAxe", slot: "weapon", weaponType: "axe", attackType: "melee", damageType: "slashing", baseDamage: 249, attackRange: 2.7, attacksPerSecond: 0.84, arc: Math.PI * 0.85, knockback: 9, projectileSpeed: 0, visualId: "tempestCleaver" },
      { id: "nebularMace", bossOnly: true, tier: 12, nameKey: "weapon.nebularMace", slot: "weapon", weaponType: "mace", attackType: "melee", damageType: "blunt", baseDamage: 301, attackRange: 2.6, attacksPerSecond: 0.72, arc: Math.PI * 0.62, knockback: 13, projectileSpeed: 0, visualId: "mace" },
      { id: "nebularBow", bossOnly: true, tier: 12, nameKey: "weapon.nebularBow", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 146, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "arcLongbow" },
      { id: "nebularStaff", bossOnly: true, tier: 12, nameKey: "weapon.nebularStaff", slot: "weapon", weaponType: "staff", attackType: "ranged", damageType: "arcane", baseDamage: 181, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "staff" },
      { id: "abyssalSword", bossOnly: true, tier: 13, nameKey: "weapon.abyssalSword", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 280, attackRange: 2.6, attacksPerSecond: 1.5, arc: Math.PI * 0.72, knockback: 5, projectileSpeed: 0, visualId: "galeBlade" },
      { id: "abyssalAxe", bossOnly: true, tier: 13, nameKey: "weapon.abyssalAxe", slot: "weapon", weaponType: "axe", attackType: "melee", damageType: "slashing", baseDamage: 406, attackRange: 2.7, attacksPerSecond: 0.84, arc: Math.PI * 0.85, knockback: 9, projectileSpeed: 0, visualId: "tempestCleaver" },
      { id: "abyssalMace", bossOnly: true, tier: 13, nameKey: "weapon.abyssalMace", slot: "weapon", weaponType: "mace", attackType: "melee", damageType: "blunt", baseDamage: 490, attackRange: 2.6, attacksPerSecond: 0.72, arc: Math.PI * 0.62, knockback: 13, projectileSpeed: 0, visualId: "mace" },
      { id: "abyssalBow", bossOnly: true, tier: 13, nameKey: "weapon.abyssalBow", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 238, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "arcLongbow" },
      { id: "abyssalStaff", bossOnly: true, tier: 13, nameKey: "weapon.abyssalStaff", slot: "weapon", weaponType: "staff", attackType: "ranged", damageType: "arcane", baseDamage: 294, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "staff" },
      { id: "omegaSword", bossOnly: true, tier: 14, nameKey: "weapon.omegaSword", slot: "weapon", weaponType: "sword", attackType: "melee", damageType: "slashing", baseDamage: 480, attackRange: 2.6, attacksPerSecond: 1.5, arc: Math.PI * 0.72, knockback: 5, projectileSpeed: 0, visualId: "galeBlade" },
      { id: "omegaAxe", bossOnly: true, tier: 14, nameKey: "weapon.omegaAxe", slot: "weapon", weaponType: "axe", attackType: "melee", damageType: "slashing", baseDamage: 696, attackRange: 2.7, attacksPerSecond: 0.84, arc: Math.PI * 0.85, knockback: 9, projectileSpeed: 0, visualId: "tempestCleaver" },
      { id: "omegaMace", bossOnly: true, tier: 14, nameKey: "weapon.omegaMace", slot: "weapon", weaponType: "mace", attackType: "melee", damageType: "blunt", baseDamage: 840, attackRange: 2.6, attacksPerSecond: 0.72, arc: Math.PI * 0.62, knockback: 13, projectileSpeed: 0, visualId: "mace" },
      { id: "omegaBow", bossOnly: true, tier: 14, nameKey: "weapon.omegaBow", slot: "weapon", weaponType: "bow", attackType: "ranged", damageType: "piercing", baseDamage: 408, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "arcLongbow" },
      { id: "omegaStaff", bossOnly: true, tier: 14, nameKey: "weapon.omegaStaff", slot: "weapon", weaponType: "staff", attackType: "ranged", damageType: "arcane", baseDamage: 504, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "staff" },

      // 10.9L25 — CROSSBOW (Besta, arqueiro) e TOME (Grimório, mago/necro): 2º tipo de arma da
      // classe, MESMOS números do bow/staff irmão em cada tier (nada muda de balance — só dá pra
      // ter os dois tipos distintos na bolsa/corrente ao mesmo tempo sem virar shard na hora).
      { id: "crossbow", nameKey: "weapon.crossbow", slot: "weapon", weaponType: "crossbow", attackType: "ranged", damageType: "piercing", baseDamage: 12, attackRange: 9, attacksPerSecond: 1.35, arc: 0, knockback: 2, projectileSpeed: 30, visualId: "crossbow" },
      { id: "tome", nameKey: "weapon.tome", slot: "weapon", weaponType: "tome", attackType: "ranged", damageType: "arcane", baseDamage: 15, attackRange: 9.5, attacksPerSecond: 1.15, arc: 0, knockback: 3, projectileSpeed: 30, visualId: "tome" },
      { id: "arcCrossbow", bossOnly: true, nameKey: "weapon.arcCrossbow", slot: "weapon", weaponType: "crossbow", attackType: "ranged", damageType: "piercing", baseDamage: 20, attackRange: 10, attacksPerSecond: 1.42, arc: 0, knockback: 3, projectileSpeed: 33, visualId: "crossbow" },
      { id: "arcaneTome", bossOnly: true, nameKey: "weapon.arcaneTome", slot: "weapon", weaponType: "tome", attackType: "ranged", damageType: "arcane", baseDamage: 24, attackRange: 10, attacksPerSecond: 1.2, arc: 0, knockback: 4, projectileSpeed: 32, visualId: "tome" },
      { id: "boltEclipse", bossOnly: true, raidOnly: true, tier: 6, nameKey: "weapon.boltEclipse", slot: "weapon", weaponType: "crossbow", attackType: "ranged", damageType: "piercing", baseDamage: 42, attackRange: 11, attacksPerSecond: 1.5, arc: 0, knockback: 4, projectileSpeed: 36, visualId: "crossbow" },
      { id: "runeEclipse", bossOnly: true, raidOnly: true, tier: 6, nameKey: "weapon.runeEclipse", slot: "weapon", weaponType: "tome", attackType: "ranged", damageType: "arcane", baseDamage: 52, attackRange: 11, attacksPerSecond: 1.28, arc: 0, knockback: 5, projectileSpeed: 36, visualId: "tome" },
      { id: "eclipseCrossbow", bossOnly: true, tier: 7, nameKey: "weapon.eclipseCrossbow", slot: "weapon", weaponType: "crossbow", attackType: "ranged", damageType: "piercing", baseDamage: 34, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "crossbow" },
      { id: "eclipseTome", bossOnly: true, tier: 7, nameKey: "weapon.eclipseTome", slot: "weapon", weaponType: "tome", attackType: "ranged", damageType: "arcane", baseDamage: 42, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "tome" },
      { id: "umbralCrossbow", bossOnly: true, tier: 8, nameKey: "weapon.umbralCrossbow", slot: "weapon", weaponType: "crossbow", attackType: "ranged", damageType: "piercing", baseDamage: 48, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "crossbow" },
      { id: "umbralTome", bossOnly: true, tier: 8, nameKey: "weapon.umbralTome", slot: "weapon", weaponType: "tome", attackType: "ranged", damageType: "arcane", baseDamage: 59, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "tome" },
      { id: "astralCrossbow", bossOnly: true, tier: 9, nameKey: "weapon.astralCrossbow", slot: "weapon", weaponType: "crossbow", attackType: "ranged", damageType: "piercing", baseDamage: 61, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "crossbow" },
      { id: "astralTome", bossOnly: true, tier: 9, nameKey: "weapon.astralTome", slot: "weapon", weaponType: "tome", attackType: "ranged", damageType: "arcane", baseDamage: 76, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "tome" },
      { id: "sideralCrossbow", bossOnly: true, tier: 10, nameKey: "weapon.sideralCrossbow", slot: "weapon", weaponType: "crossbow", attackType: "ranged", damageType: "piercing", baseDamage: 78, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "crossbow" },
      { id: "sideralTome", bossOnly: true, tier: 10, nameKey: "weapon.sideralTome", slot: "weapon", weaponType: "tome", attackType: "ranged", damageType: "arcane", baseDamage: 97, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "tome" },
      { id: "stellarCrossbow", bossOnly: true, tier: 11, nameKey: "weapon.stellarCrossbow", slot: "weapon", weaponType: "crossbow", attackType: "ranged", damageType: "piercing", baseDamage: 109, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "crossbow" },
      { id: "stellarTome", bossOnly: true, tier: 11, nameKey: "weapon.stellarTome", slot: "weapon", weaponType: "tome", attackType: "ranged", damageType: "arcane", baseDamage: 134, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "tome" },
      { id: "nebularCrossbow", bossOnly: true, tier: 12, nameKey: "weapon.nebularCrossbow", slot: "weapon", weaponType: "crossbow", attackType: "ranged", damageType: "piercing", baseDamage: 146, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "crossbow" },
      { id: "nebularTome", bossOnly: true, tier: 12, nameKey: "weapon.nebularTome", slot: "weapon", weaponType: "tome", attackType: "ranged", damageType: "arcane", baseDamage: 181, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "tome" },
      { id: "abyssalCrossbow", bossOnly: true, tier: 13, nameKey: "weapon.abyssalCrossbow", slot: "weapon", weaponType: "crossbow", attackType: "ranged", damageType: "piercing", baseDamage: 238, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "crossbow" },
      { id: "abyssalTome", bossOnly: true, tier: 13, nameKey: "weapon.abyssalTome", slot: "weapon", weaponType: "tome", attackType: "ranged", damageType: "arcane", baseDamage: 294, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "tome" },
      { id: "omegaCrossbow", bossOnly: true, tier: 14, nameKey: "weapon.omegaCrossbow", slot: "weapon", weaponType: "crossbow", attackType: "ranged", damageType: "piercing", baseDamage: 408, attackRange: 10.5, attacksPerSecond: 1.45, arc: 0, knockback: 3, projectileSpeed: 34, visualId: "crossbow" },
      { id: "omegaTome", bossOnly: true, tier: 14, nameKey: "weapon.omegaTome", slot: "weapon", weaponType: "tome", attackType: "ranged", damageType: "arcane", baseDamage: 504, attackRange: 10.5, attacksPerSecond: 1.22, arc: 0, knockback: 4, projectileSpeed: 34, visualId: "tome" },
    ],
    // catálogo de ARMADURA/ACESSÓRIO
    armor: [
      // 10.9AN — `tier` = escada de PODER por slot (1 sem gate · 2 = Nv20 · 3 = Nv35), não o tema:
      // T1 só tem elmo/peito/amuleto, então as luvas/botas fracas ficam T1 pra nv baixo ter o slot.
      { id: "voidHelm", nameKey: "armor.head", slot: "head", visualId: "head", baseArmor: 3, baseHp: 10, tier: 1 },
      { id: "voidPlate", nameKey: "armor.chest", slot: "chest", visualId: "chest", baseArmor: 6, baseHp: 25, tier: 1 },
      { id: "voidCharm", nameKey: "armor.accessory", slot: "accessory", visualId: "accessory", baseArmor: 0, baseHp: 5, tier: 1 },
      // Fase 10 — armadura temática (T2/T3) para os 6 slots (§14). Sem set bonus/copies.
      { id: "shardHelm", nameKey: "armor.shardHelm", slot: "head", visualId: "head", baseArmor: 6, baseHp: 18, tier: 2 },
      { id: "tempestVisor", nameKey: "armor.tempestVisor", slot: "head", visualId: "head", baseArmor: 9, baseHp: 26, tier: 3 },
      { id: "wardenPlate", nameKey: "armor.wardenPlate", slot: "chest", visualId: "chest", baseArmor: 10, baseHp: 40, tier: 2 },
      { id: "tempestMail", nameKey: "armor.tempestMail", slot: "chest", visualId: "chest", baseArmor: 14, baseHp: 55, tier: 3 },
      { id: "shardGrips", nameKey: "armor.shardGrips", slot: "gloves", visualId: "gloves", baseArmor: 3, baseHp: 8, tier: 1 },
      { id: "galeWraps", nameKey: "armor.galeWraps", slot: "gloves", visualId: "gloves", baseArmor: 2, baseHp: 6, tier: 1 },
      { id: "aetherGauntlets", nameKey: "armor.aetherGauntlets", slot: "gloves", visualId: "gloves", baseArmor: 5, baseHp: 14, tier: 2 },
      // 10.9J7 — AUDITORIA: luvas e acessório pulavam do T2 direto pro T4 (Nv20→Nv50, 15 níveis
      // sem upgrade nesses 2 slots, e nem boss dropava nada pra eles nesse intervalo — rollBossArmor
      // exige tier EXATO). Números interpolados entre o T2 e o T4 do mesmo slot (mesma média
      // geométrica usada nos outros T2→T3→T4 já existentes).
      { id: "tempestGrips", nameKey: "armor.tempestGrips", slot: "gloves", visualId: "gloves", baseArmor: 6, baseHp: 18, tier: 3 },
      { id: "riftwalkBoots", nameKey: "armor.riftwalkBoots", slot: "boots", visualId: "boots", baseArmor: 3, baseHp: 10, tier: 1 },
      { id: "galestepBoots", nameKey: "armor.galestepBoots", slot: "boots", visualId: "boots", baseArmor: 4, baseHp: 12, tier: 2 },
      { id: "stonepathGreaves", nameKey: "armor.stonepathGreaves", slot: "boots", visualId: "boots", baseArmor: 7, baseHp: 20, tier: 3 },
      { id: "aetherRing", nameKey: "armor.aetherRing", slot: "accessory", visualId: "accessory", baseArmor: 0, baseHp: 8, tier: 1 },
      { id: "prismCharm", nameKey: "armor.prismCharm", slot: "accessory", visualId: "accessory", baseArmor: 1, baseHp: 6, tier: 1 },
      { id: "stormSigil", nameKey: "armor.stormSigil", slot: "accessory", visualId: "accessory", baseArmor: 2, baseHp: 12, tier: 2 },
      { id: "tempestSigil", nameKey: "armor.tempestSigil", slot: "accessory", visualId: "accessory", baseArmor: 3, baseHp: 15, tier: 3 }, // 10.9J7 — fecha o mesmo buraco do T3 (ver tempestGrips acima)
      // 10.9AO — TIERS 4/5/6 (Nv50/70/90): ~×1.4 por tier em cada slot. Os monstros acompanham via
      // Ameaça Crescente (vida EFETIVA: vida + regen + armadura + resiliência), então tank de tier
      // alto + VIP (vida/regen/dano comprados) continua tendo ameaça.
      { id: "abyssalHelm", nameKey: "armor.abyssalHelm", slot: "head", visualId: "head", baseArmor: 13, baseHp: 36, tier: 4 },
      { id: "abyssalPlate", nameKey: "armor.abyssalPlate", slot: "chest", visualId: "chest", baseArmor: 19, baseHp: 76, tier: 4 },
      { id: "abyssalGrips", nameKey: "armor.abyssalGrips", slot: "gloves", visualId: "gloves", baseArmor: 8, baseHp: 22, tier: 4 },
      { id: "abyssalGreaves", nameKey: "armor.abyssalGreaves", slot: "boots", visualId: "boots", baseArmor: 10, baseHp: 28, tier: 4 },
      { id: "abyssalSigil", nameKey: "armor.abyssalSigil", slot: "accessory", visualId: "accessory", baseArmor: 4, baseHp: 18, tier: 4 },
      { id: "titanCrown", nameKey: "armor.titanCrown", slot: "head", visualId: "head", baseArmor: 18, baseHp: 50, tier: 5 },
      { id: "titanBulwark", nameKey: "armor.titanBulwark", slot: "chest", visualId: "chest", baseArmor: 27, baseHp: 106, tier: 5 },
      { id: "titanGauntlets", nameKey: "armor.titanGauntlets", slot: "gloves", visualId: "gloves", baseArmor: 11, baseHp: 31, tier: 5 },
      { id: "titanTreads", nameKey: "armor.titanTreads", slot: "boots", visualId: "boots", baseArmor: 14, baseHp: 39, tier: 5 },
      { id: "titanSeal", nameKey: "armor.titanSeal", slot: "accessory", visualId: "accessory", baseArmor: 6, baseHp: 26, tier: 5 },
      { id: "primordialCrown", nameKey: "armor.primordialCrown", slot: "head", visualId: "head", baseArmor: 25, baseHp: 70, tier: 6 },
      { id: "primordialAegis", nameKey: "armor.primordialAegis", slot: "chest", visualId: "chest", baseArmor: 38, baseHp: 148, tier: 6 },
      { id: "primordialFists", nameKey: "armor.primordialFists", slot: "gloves", visualId: "gloves", baseArmor: 16, baseHp: 44, tier: 6 },
      { id: "primordialStriders", nameKey: "armor.primordialStriders", slot: "boots", visualId: "boots", baseArmor: 20, baseHp: 55, tier: 6 },
      { id: "primordialHeart", nameKey: "armor.primordialHeart", slot: "accessory", visualId: "accessory", baseArmor: 9, baseHp: 36, tier: 6 },
      // 10.9FX — ECLIPSE (Tier 7 · Nv150): roupa de fim de jogo (cai do boss global e de bosses). Rola % de vida.
      { id: "eclipseCrown", nameKey: "armor.eclipseCrown", slot: "head", visualId: "head", baseArmor: 38, baseHp: 110, tier: 7 },
      { id: "eclipseAegis", nameKey: "armor.eclipseAegis", slot: "chest", visualId: "chest", baseArmor: 58, baseHp: 230, tier: 7 },
      { id: "eclipseGrips", nameKey: "armor.eclipseGrips", slot: "gloves", visualId: "gloves", baseArmor: 24, baseHp: 68, tier: 7 },
      { id: "eclipseTreads", nameKey: "armor.eclipseTreads", slot: "boots", visualId: "boots", baseArmor: 30, baseHp: 86, tier: 7 },
      { id: "eclipseSeal", nameKey: "armor.eclipseSeal", slot: "accessory", visualId: "accessory", baseArmor: 14, baseHp: 56, tier: 7 },
      // 10.9GG — ACESSÓRIO ÚNICO do boss global (só cai na raid)
      { id: "titanHeart", raidOnly: true, tier: 6, nameKey: "armor.titanHeart", slot: "accessory", visualId: "accessory", baseArmor: 22, baseHp: 100 },
      // 10.9FZ — armadura de fim de jogo (Nv 200..1000)
      { id: "umbralHead", nameKey: "armor.umbralHead", slot: "head", visualId: "head", baseArmor: 53, baseHp: 154, tier: 8 },
      { id: "umbralChest", nameKey: "armor.umbralChest", slot: "chest", visualId: "chest", baseArmor: 81, baseHp: 322, tier: 8 },
      { id: "umbralGloves", nameKey: "armor.umbralGloves", slot: "gloves", visualId: "gloves", baseArmor: 34, baseHp: 95, tier: 8 },
      { id: "umbralBoots", nameKey: "armor.umbralBoots", slot: "boots", visualId: "boots", baseArmor: 42, baseHp: 120, tier: 8 },
      { id: "umbralAccessory", nameKey: "armor.umbralAccessory", slot: "accessory", visualId: "accessory", baseArmor: 20, baseHp: 78, tier: 8 },
      { id: "astralHead", nameKey: "armor.astralHead", slot: "head", visualId: "head", baseArmor: 68, baseHp: 198, tier: 9 },
      { id: "astralChest", nameKey: "armor.astralChest", slot: "chest", visualId: "chest", baseArmor: 104, baseHp: 414, tier: 9 },
      { id: "astralGloves", nameKey: "armor.astralGloves", slot: "gloves", visualId: "gloves", baseArmor: 43, baseHp: 122, tier: 9 },
      { id: "astralBoots", nameKey: "armor.astralBoots", slot: "boots", visualId: "boots", baseArmor: 54, baseHp: 155, tier: 9 },
      { id: "astralAccessory", nameKey: "armor.astralAccessory", slot: "accessory", visualId: "accessory", baseArmor: 25, baseHp: 101, tier: 9 },
      // 10.9J7 — AUDITORIA: T9→T10 crescia só ~×1.28 (abaixo do ~×1.34-1.4 que T4-T9 E o próprio
      // T10→T11 já seguem — dá pra conferir: T11 bate quase exato com T10-novo × a mesma razão).
      // Buffado pra fechar o afundamento (nunca reduzido — ninguém perde poder). Calculado como
      // média geométrica entre o T9 (68/198 etc.) e o T12 (163/473 etc.), que ficam INTOCADOS.
      // NÃO mexi em T12→T13→T14 (a disparada de lá): pode ser proposital — são as "salas de
      // prestígio" que também exigem % da Árvore do Void, não só nível. Fica pra confirmar antes.
      { id: "sideralHead", nameKey: "armor.sideralHead", slot: "head", visualId: "head", baseArmor: 91, baseHp: 265, tier: 10 },
      { id: "sideralChest", nameKey: "armor.sideralChest", slot: "chest", visualId: "chest", baseArmor: 139, baseHp: 553, tier: 10 },
      { id: "sideralGloves", nameKey: "armor.sideralGloves", slot: "gloves", visualId: "gloves", baseArmor: 58, baseHp: 163, tier: 10 },
      { id: "sideralBoots", nameKey: "armor.sideralBoots", slot: "boots", visualId: "boots", baseArmor: 72, baseHp: 207, tier: 10 },
      { id: "sideralAccessory", nameKey: "armor.sideralAccessory", slot: "accessory", visualId: "accessory", baseArmor: 34, baseHp: 135, tier: 10 },
      { id: "stellarHead", nameKey: "armor.stellarHead", slot: "head", visualId: "head", baseArmor: 122, baseHp: 352, tier: 11 },
      { id: "stellarChest", nameKey: "armor.stellarChest", slot: "chest", visualId: "chest", baseArmor: 186, baseHp: 736, tier: 11 },
      { id: "stellarGloves", nameKey: "armor.stellarGloves", slot: "gloves", visualId: "gloves", baseArmor: 77, baseHp: 218, tier: 11 },
      { id: "stellarBoots", nameKey: "armor.stellarBoots", slot: "boots", visualId: "boots", baseArmor: 96, baseHp: 275, tier: 11 },
      { id: "stellarAccessory", nameKey: "armor.stellarAccessory", slot: "accessory", visualId: "accessory", baseArmor: 45, baseHp: 179, tier: 11 },
      { id: "nebularHead", nameKey: "armor.nebularHead", slot: "head", visualId: "head", baseArmor: 163, baseHp: 473, tier: 12 },
      { id: "nebularChest", nameKey: "armor.nebularChest", slot: "chest", visualId: "chest", baseArmor: 249, baseHp: 989, tier: 12 },
      { id: "nebularGloves", nameKey: "armor.nebularGloves", slot: "gloves", visualId: "gloves", baseArmor: 103, baseHp: 292, tier: 12 },
      { id: "nebularBoots", nameKey: "armor.nebularBoots", slot: "boots", visualId: "boots", baseArmor: 129, baseHp: 370, tier: 12 },
      { id: "nebularAccessory", nameKey: "armor.nebularAccessory", slot: "accessory", visualId: "accessory", baseArmor: 60, baseHp: 241, tier: 12 },
      { id: "abyssalHead", nameKey: "armor.abyssalHead", slot: "head", visualId: "head", baseArmor: 266, baseHp: 770, tier: 13 },
      { id: "abyssalChest", nameKey: "armor.abyssalChest", slot: "chest", visualId: "chest", baseArmor: 406, baseHp: 1610, tier: 13 },
      { id: "abyssalGloves", nameKey: "armor.abyssalGloves", slot: "gloves", visualId: "gloves", baseArmor: 168, baseHp: 476, tier: 13 },
      { id: "abyssalBoots", nameKey: "armor.abyssalBoots", slot: "boots", visualId: "boots", baseArmor: 210, baseHp: 602, tier: 13 },
      { id: "abyssalAccessory", nameKey: "armor.abyssalAccessory", slot: "accessory", visualId: "accessory", baseArmor: 98, baseHp: 392, tier: 13 },
      { id: "omegaHead", nameKey: "armor.omegaHead", slot: "head", visualId: "head", baseArmor: 456, baseHp: 1320, tier: 14 },
      { id: "omegaChest", nameKey: "armor.omegaChest", slot: "chest", visualId: "chest", baseArmor: 696, baseHp: 2760, tier: 14 },
      { id: "omegaGloves", nameKey: "armor.omegaGloves", slot: "gloves", visualId: "gloves", baseArmor: 288, baseHp: 816, tier: 14 },
      { id: "omegaBoots", nameKey: "armor.omegaBoots", slot: "boots", visualId: "boots", baseArmor: 360, baseHp: 1032, tier: 14 },
      { id: "omegaAccessory", nameKey: "armor.omegaAccessory", slot: "accessory", visualId: "accessory", baseArmor: 168, baseHp: 672, tier: 14 },
      // 10.6N — ANÉIS DE PROTEÇÃO. Cada um anula um perigo ambiental. Sem o anel do lugar,
      // o território drena vida: é o que dá identidade e exigência a cada região.
      { id: "ringEmber", nameKey: "armor.ringEmber", slot: "accessory", visualId: "accessory", baseArmor: 5, baseHp: 40, protects: "frost" }, // 10.9GJ — anel util (base melhor)
      { id: "ringFrost", nameKey: "armor.ringFrost", slot: "accessory", visualId: "accessory", baseArmor: 5, baseHp: 40, protects: "ember" },
      { id: "ringGale", nameKey: "armor.ringGale", slot: "accessory", visualId: "accessory", baseArmor: 5, baseHp: 40, protects: "gale" },
      { id: "ringStorm", nameKey: "armor.ringStorm", slot: "accessory", visualId: "accessory", baseArmor: 6, baseHp: 46, protects: "storm" },
    ],
  },

  loot: {
    // fonte de raridade por inimigo (chave em rarity.weights E em dropTable)
    raritySource: {
      voidling: "normal", voidRunner: "normal", voidBrute: "normal", voidWisp: "normal", corruptedBrute: "elite",
      shardfang: "wilds", craghorn: "wilds", aetherwing: "wilds", glintback: "wilds", shardWarden: "warden",
      galestrider: "tempest", arcbeak: "tempest", stormback: "tempest", cloudstalker: "tempest", tempestWarden: "stormWarden",
    } as Record<string, string>,
    // TABELA DE DROP por fonte (pesos relativos). Cada kill sorteia UM resultado:
    // "none" = só dinheiro (essência), nenhum item; senão dropa 1 item DAQUELA raridade.
    // Ajuste livre — a % efetiva de cada raridade = peso / soma dos pesos da linha.
    // (mob comum: ~metade vira item comum; lendária rara; a maior fatia costuma ser "none".)
    // 10.5A — REBALANCE do farm: Área 1 (normal) dropa MENOS e SEM raridade alta
    // (epic+ = 0). Raridade alta (epic/legendary/mythic) só a partir da Área 2 (wilds/warden)
    // e além. Elite da Área 1 chega no máx a epic (raro), sem legendary/mythic.
    // 10.5F — drops MAIS DIFÍCEIS (mais "none"); Área 1 sem raridade alta; epic+ só Área 2+.
    dropTable: {
      //             none  common uncommon rare  epic legendary mythic
      normal:      { none: 60, common: 27, uncommon: 9, rare: 3.2, epic: 0.8, legendary: 0, mythic: 0 }, // 10.9G: mais loot
      // 10.9HS — Thiago: "elite dropar itens bons" — menos "none", mais épico+ nas 3 tabelas de elite/campeão
      elite:       { none: 24, common: 30, uncommon: 24, rare: 14, epic: 6.5, legendary: 1.4, mythic: 0.1 },
      wilds:       { none: 42, common: 30, uncommon: 18, rare: 7, epic: 2.5, legendary: 0.5, mythic: 0.1 },
      warden:      { none: 18, common: 20, uncommon: 27, rare: 20, epic: 10, legendary: 4, mythic: 1 },
      tempest:     { none: 40, common: 30, uncommon: 19, rare: 8, epic: 2.5, legendary: 0.5, mythic: 0.1 }, // 10.9G: mais loot
      stormWarden: { none: 14, common: 16, uncommon: 26, rare: 24, epic: 12, legendary: 6.5, mythic: 1.5 },
    } as Record<string, Record<string, number>>,
    // 10.5F — TIPOS de arma liberados por REGIÃO (progressivo). Fora da lista, aquele tipo
    // não cai naquela área (armaduras não são afetadas). Boss libera arma forte da próxima.
    /**
     * 10.6G — ESCADA DE ARMAS POR ÁREA. Cada área libera uma arma nova e ela é a chave
     * daquela área (os monstros de lá são fracos a ela). Só cai o que a área libera:
     *   Área 1 — Espada.  ·  Área 2 — + Machado.  ·  Área 3 — + Arco e Maça.
     * Voltar para uma área antiga volta a cair só o que aquela área libera: o lugar tem
     * identidade própria, e a arma nova é sempre a recompensa de avançar.
     */
    regionWeaponTypes: {
      forgottenForest: ["sword"],
      shatteredWilds: ["sword", "axe"],
      tempestReach: ["sword", "axe", "bow", "mace"],
    } as Record<string, string[]>,
    pickupRadius: 1.5, // auto-pickup curto
    magnetRadius: 3.5,
    // 10.9O — baús PREMIUM têm CHANCE de largar uma ARMA DE BOSS (em vez de vender arma direto
    // na loja de 💎). Só nos baús grandes; raridade da arma escala com o baú.
    chestBossWeapon: {
      colossus: { chance: 0.35, rarity: "legendary" as const },
      tempest: { chance: 0.22, rarity: "epic" as const },
      skyruin: { chance: 0.12, rarity: "epic" as const },
    } as Record<string, { chance: number; rarity: import("../items/EquipmentTypes").Rarity }>,
  },

  // 10.9BY — SKILLS (à parte da maestria): 7 perícias que sobem por TREINO online/offline.
  skills: {
    maxLevel: 100,
    xpBase: 40, xpExp: 1.5, // xpFor(l) = 40 × l^1.5
    // treino ONLINE (lutando): XP na skill da arma em uso / na Defesa ao tomar dano
    online: { perHit: 1, perKill: 6, perElite: 30, perBoss: 150, perHitTaken: 1, perBlock: 3, defenseCooldown: 0.5 },
    // efeito: arma +0.8% dano/nível (máx +80%) + 2% vel. ataque a cada 10; defesa +0.4%/nível (máx 40%)
    bonus: { damagePerLevel: 0.008, damageCap: 0.8, attackSpeedEvery: 10, attackSpeedStep: 0.02, defensePerLevel: 0.004, defenseCap: 0.4 },
    // treino OFFLINE (Boneco de Treino no hub, tempo real)
    training: {
      interactRadius: 2.8,
      hours: [1, 4, 8, 12] as number[], // durações; VIP ativo libera também vipHours
      vipHours: 24,
      costBase: 20, costPerLevel: 4, // 🪙/h = 20 + nível×4
      coinsPerDiamond: 100, // "Pagar com 💎" = ceil(🪙/100), mín 1
      rateBase: 2, rateEvery: 10, // XP/min = 2 + floor(nível/10)
      finishMinsPerDiamond: 30, // "Terminar agora" = ceil(min restantes/30) 💎
    },
  },

  // maestria de arma: ganha JOGANDO (dano validado, kills).
  // 10.9L22 — Thiago: "as maestrias e level de pet podem ser infinitos mas com dificuldade igual
  // o level do personagem". Removido o teto de Nv20 (era `maxLevel`): os 4 marcos abaixo (5/10/15/20)
  // continuam os mesmos de sempre, MAS a partir do Nv20 cada nível seguinte soma um pouco de dano,
  // pra sempre — sem teto, igual o nível do Caçador (`progression.xpForLevel`, que também nunca para).
  mastery: {
    xpForLevel: (level: number): number => Math.floor(60 * Math.pow(level, 1.6)),
    xp: { perHit: 1, perHitCap: 3, perKill: 12, perElite: 60, perBoss: 300 },
    // bônus por arquétipo em marcos; trait = efeito especial (Lv20)
    bonuses: {
      sword: { 5: { attackSpeedMult: 0.03 }, 10: { damageMult: 0.05 }, 15: { critChance: 0.05 }, 20: { trait: "voidEdge" } },
      axe: { 5: { damageMult: 0.05 }, 10: { critDamageMult: 0.08 }, 15: { trait: "cleave" }, 20: { trait: "executioner" } },
      bow: { 5: { attackSpeedMult: 0.05 }, 10: { projectileSpeedMult: 0.05 }, 15: { critChance: 0.05 }, 20: { trait: "voidShot" } },
      mace: { 5: { damageMult: 0.05 }, 10: { critDamageMult: 0.08 }, 15: { damageMult: 0.05 }, 20: { trait: "cleave" } }, // clava: impacto pesado
      staff: { 5: { damageMult: 0.05 }, 10: { critDamageMult: 0.08 }, 15: { attackSpeedMult: 0.05 }, 20: { trait: "voidShot" } }, // cajado: bolt arcano perfurante no Lv20
      // 10.9L25 — crossbow/tome espelham bow/staff (2º tipo de arma da classe, mesmos marcos)
      crossbow: { 5: { attackSpeedMult: 0.05 }, 10: { projectileSpeedMult: 0.05 }, 15: { critChance: 0.05 }, 20: { trait: "voidShot" } },
      tome: { 5: { damageMult: 0.05 }, 10: { critDamageMult: 0.08 }, 15: { attackSpeedMult: 0.05 }, 20: { trait: "voidShot" } },
    },
    // 10.9L22 — sem teto: acima do Lv20, cada nível soma +0.25% de dano (sem limite).
    pastCapLevel: 20,
    pastCapDamagePerLevel: 0.0025,
    voidEdgeEvery: 4, // sword: a cada N ataques, próximo golpe reforçado
    voidEdgeBonus: 0.6,
    executionerThreshold: 0.3, // axe: alvo abaixo de 30% HP
    executionerBonus: 0.5,
    voidShotEvery: 4, // bow: a cada N disparos, projétil forte
    voidShotMult: 2.0,
  },

  projectile: {
    radius: 0.18,
    lifetime: 2.5,
    aimAssist: 0.15, // homing leve (0 = reto)
  },

  chests: {
    // conteúdo por tipo: fonte de raridade (rarity.weights) + essência
    wooden: { raritySource: "wooden", essence: { min: 2, max: 5 } },
    void: { raritySource: "void", essence: { min: 5, max: 12 } },
    alpha: { raritySource: "alpha", essence: { min: 12, max: 24 } },
    // Fase 8 — baús Tier 2 (Shattered Wilds)
    ruined: { raritySource: "ruined", essence: { min: 6, max: 14 } },
    shard: { raritySource: "shard", essence: { min: 14, max: 28 } },
    colossus: { raritySource: "colossus", essence: { min: 30, max: 60 } }, // Colossus Chest (boss R2)
    // Fase 10 — baús Tier 3 (Tempest Reach)
    skyruin: { raritySource: "skyruin", essence: { min: 10, max: 22 } }, // Skyruin Chest (exploração)
    tempest: { raritySource: "storm", essence: { min: 20, max: 40 } }, // Tempest Chest (grande)
    // baús únicos de exploração por REGIÃO (salvam abertos). region = id da WorldRegion.
    world: [
      { id: "chest_clearing_1", type: "wooden", x: -24, z: 14, region: "forgottenForest" }, // 10.9CC: era (-8,6) — dentro do hub, em cima do portal PvP
      { id: "chest_clearing_2", type: "wooden", x: 26, z: 16, region: "forgottenForest" }, // 10.9CC: era (10,10) — na parede do hub
      { id: "chest_grove_1", type: "void", x: -14, z: -14, region: "forgottenForest" },
      { id: "chest_grove_2", type: "wooden", x: 16, z: -12, region: "forgottenForest" },
      { id: "chest_entrance_1", type: "wooden", x: 4, z: 24, region: "forgottenForest" },
      // 10.5M — baús espalhados pelos 15 territórios (o mapa triplicou; explorar tem que pagar)
      { id: "chest_whisper", type: "wooden", x: -66, z: 4, region: "forgottenForest" },
      { id: "chest_garden", type: "void", x: 66, z: -4, region: "forgottenForest" },
      { id: "chest_marsh", type: "wooden", x: -68, z: 44, region: "forgottenForest" },
      { id: "chest_hills", type: "wooden", x: 68, z: 40, region: "forgottenForest" },
      { id: "chest_roots", type: "void", x: -66, z: 86, region: "forgottenForest" },
      { id: "chest_sleepgate", type: "void", x: 2, z: 88, region: "forgottenForest" },
      { id: "chest_southmist", type: "void", x: 70, z: 84, region: "forgottenForest" },
      { id: "chest_grovefar", type: "void", x: -68, z: -44, region: "forgottenForest" },
      { id: "chest_fangtrail", type: "wooden", x: 68, z: -40, region: "forgottenForest" },
      { id: "chest_ravine", type: "void", x: -66, z: -86, region: "forgottenForest" },
      { id: "chest_shardfield", type: "void", x: 68, z: -84, region: "forgottenForest" },
      // Shattered Wilds
      { id: "chest_wilds_1", type: "ruined", x: -24, z: 16, region: "shatteredWilds" }, // 10.9CC: era (-10,8) — dentro do hub
      { id: "chest_wilds_2", type: "ruined", x: 24, z: -14, region: "shatteredWilds" }, // 10.9CC: era (14,-6) — na parede
      { id: "chest_wilds_3", type: "shard", x: -16, z: -16, region: "shatteredWilds" },
      { id: "chest_wilds_4", type: "shard", x: 8, z: -22, region: "shatteredWilds" },
      { id: "chest_wilds_5", type: "shard", x: -68, z: -68, region: "shatteredWilds" },
      { id: "chest_wilds_6", type: "shard", x: 68, z: -70, region: "shatteredWilds" },
      { id: "chest_wilds_7", type: "ruined", x: -70, z: 68, region: "shatteredWilds" },
      { id: "chest_wilds_8", type: "ruined", x: 70, z: 70, region: "shatteredWilds" },
      { id: "chest_wilds_9", type: "shard", x: 2, z: -72, region: "shatteredWilds" },
      { id: "chest_wilds_10", type: "ruined", x: -4, z: 70, region: "shatteredWilds" },
      // 10.6J — a Área 2 dobrou de tamanho e os baús ficaram todos no miolo antigo
      { id: "chest_wilds_11", type: "shard", x: 101, z: 0, region: "shatteredWilds" },
      { id: "chest_wilds_12", type: "ruined", x: 27, z: 29, region: "shatteredWilds" },
      { id: "chest_wilds_13", type: "shard", x: 63, z: 79, region: "shatteredWilds" },
      { id: "chest_wilds_14", type: "ruined", x: -6, z: 40, region: "shatteredWilds" },
      { id: "chest_wilds_15", type: "shard", x: -23, z: 99, region: "shatteredWilds" },
      { id: "chest_wilds_16", type: "ruined", x: -35, z: 20, region: "shatteredWilds" },
      { id: "chest_wilds_17", type: "shard", x: -91, z: 44, region: "shatteredWilds" },
      { id: "chest_wilds_18", type: "ruined", x: -37, z: -15, region: "shatteredWilds" },
      { id: "chest_wilds_19", type: "shard", x: -91, z: -44, region: "shatteredWilds" },
      { id: "chest_wilds_20", type: "ruined", x: -12, z: -38, region: "shatteredWilds" },
      { id: "chest_wilds_21", type: "shard", x: -23, z: -99, region: "shatteredWilds" },
      { id: "chest_wilds_22", type: "ruined", x: 22, z: -33, region: "shatteredWilds" },
      { id: "chest_wilds_23", type: "shard", x: 63, z: -79, region: "shatteredWilds" },
      { id: "chest_wilds_24", type: "ruined", x: 40, z: -3, region: "shatteredWilds" },
      // Tempest Reach
      { id: "chest_tempest_1", type: "skyruin", x: -24, z: 16, region: "tempestReach" }, // 10.9CC: era (-12,8) — dentro do hub
      { id: "chest_tempest_2", type: "skyruin", x: 26, z: -14, region: "tempestReach" }, // 10.9CC: era (16,-8) — na parede
      { id: "chest_tempest_3", type: "tempest", x: -14, z: -30, region: "tempestReach" },
      { id: "chest_tempest_4", type: "tempest", x: 10, z: -46, region: "tempestReach" },
      { id: "chest_tempest_5", type: "tempest", x: -68, z: -54, region: "tempestReach" },
      { id: "chest_tempest_6", type: "tempest", x: 70, z: -50, region: "tempestReach" },
      { id: "chest_tempest_7", type: "skyruin", x: -70, z: 54, region: "tempestReach" },
      { id: "chest_tempest_8", type: "skyruin", x: 68, z: 52, region: "tempestReach" },
      // 10.6J — a Área 3 é a maior do jogo e tinha 8 baús: agora cobre o mapa inteiro
      { id: "chest_tempest_9", type: "tempest", x: 120, z: 0, region: "tempestReach" },
      { id: "chest_tempest_10", type: "skyruin", x: 38, z: 29, region: "tempestReach" },
      { id: "chest_tempest_11", type: "tempest", x: 101, z: 65, region: "tempestReach" },
      { id: "chest_tempest_12", type: "skyruin", x: 16, z: 45, region: "tempestReach" },
      { id: "chest_tempest_13", type: "tempest", x: 50, z: 110, region: "tempestReach" },
      { id: "chest_tempest_14", type: "skyruin", x: -11, z: 46, region: "tempestReach" },
      { id: "chest_tempest_15", type: "tempest", x: -17, z: 119, region: "tempestReach" },
      { id: "chest_tempest_16", type: "skyruin", x: -34, z: 33, region: "tempestReach" },
      { id: "chest_tempest_17", type: "tempest", x: -79, z: 91, region: "tempestReach" },
      { id: "chest_tempest_18", type: "skyruin", x: -47, z: 10, region: "tempestReach" },
      { id: "chest_tempest_19", type: "tempest", x: -116, z: 34, region: "tempestReach" },
      { id: "chest_tempest_20", type: "skyruin", x: -44, z: -17, region: "tempestReach" },
      { id: "chest_tempest_21", type: "tempest", x: -116, z: -34, region: "tempestReach" },
      { id: "chest_tempest_22", type: "skyruin", x: -28, z: -38, region: "tempestReach" },
      { id: "chest_tempest_23", type: "tempest", x: -79, z: -91, region: "tempestReach" },
      { id: "chest_tempest_24", type: "skyruin", x: -3, z: -48, region: "tempestReach" },
      { id: "chest_tempest_25", type: "tempest", x: -17, z: -119, region: "tempestReach" },
      { id: "chest_tempest_26", type: "skyruin", x: 23, z: -41, region: "tempestReach" },
      { id: "chest_tempest_27", type: "tempest", x: 50, z: -110, region: "tempestReach" },
      { id: "chest_tempest_28", type: "skyruin", x: 42, z: -22, region: "tempestReach" },
      { id: "chest_tempest_29", type: "tempest", x: 101, z: -65, region: "tempestReach" },
      { id: "chest_tempest_30", type: "skyruin", x: 47, z: 4, region: "tempestReach" },
    ],
    interactRadius: 2.4,
  },

  // ===================== FASE 5: MINIMAPA / FOG OF WAR =====================
  fog: {
    // 10.5M — mapa 3× maior: célula maior mantém o grid leve (23×23 células).
    cellSize: 10, // metros por célula (grid leve, mobile)
    worldExtent: 112, // 10.9HQ — LEGADO: só a migração do save usa; o grid real é por região (arenaHalfSize + 8)
    revealRadius: 16, // raio de revelação ao redor do player
  },
  minimap: {
    rangeWorld: 44, // quanto do mundo (raio) o minimapa mostra ao redor do player (10.5M: mapa maior)
    updateHz: 12, // 10.9HZ — NÃO é mais lido: a taxa vem de `quality.values.minimapHz` (por preset) // atualização em baixa frequência (perf)
    discoverRadius: 6, // distância para "descobrir" um POI
  },

  // acessibilidade (persistido)
  accessibility: {
    uiScales: { small: 0.85, default: 1.0, large: 1.18 },
  },

  // ===================== FASE 6: VOID TREE =====================
  voidTree: {
    // 10.9AQ — 1 Void Point a cada N níveis (era 1 por nível: "a pessoa fica forte rápido demais").
    // Só o ritmo do NÍVEL muda; pontos de boss e os comprados na Loja continuam valendo 1 cada.
    levelsPerPoint: 2,
    bossBonus: 1, // +1 na 1ª derrota do Void Alpha (derivado do progresso, não incrementado)
    // 10.9HT — AUDITORIA (Thiago: "o intuito é ter conteúdo até fechar todas as árvores void, e o
    // personagem ficar forte pro PvP/boss/missões"). A árvore inteira (Poder+Sobrevivência+
    // Agilidade+1 classe, com os 9+3 nós "infinitos" de 50 ranks) custa ~867-871 pontos. Só de
    // nível (1 a cada 2), isso só fecha no Nv~1735 (~1.690h a 60k xp/h) — 54× além do fim do
    // conteúdo desenhado (Nv32) e bem acima até do próprio Nv1000 de referência (657h, mesmo nível
    // do teto de tier de equipamento T14). Em vez de acelerar o nível cedo (já foi tentado: "1 por
    // nível" deixava forte rápido demais), a Profundidade agora também paga pontos — CONTEÚDO
    // repetível de fim de jogo fecha a árvore, não só nível cru. Medido: no Nv1000 (657h) + ~100
    // voltas de Profundidade, nível dá 500 pontos (499+bossBonus) e Profundidade dá +400 (100×4) =
    // 900, cobrindo os ~867-871 com folga. `Game.onAreaCompleted` soma isso em `progress.voidPointsBought`.
    pointsPerDepth: 4,
    dominance: { kills: 5, window: 10, buffPct: 0.1, duration: 5 }, // Void Dominance (Power)
    secondWind: { threshold: 0.25, healPct: 0.25, cooldown: 30 }, // Second Wind (Survival)
    unbreakable: { cooldown: 120 }, // Unbreakable (Survival) — sobrevive com 1 HP
    voidStep: { reductionPct: 0.3, duration: 0.6 }, // Void Step (Agility) — redução no início do sprint
    predator: { moveBuffPct: 0.2, duration: 2 }, // Predator (Agility) — move após kill
  },

  // Combat Power: número de RESUMO (não é fórmula real de combate).
  combatPower: { damage: 2.2, maxHp: 0.12, armor: 3, critChance: 260, critDamage: 60, attackSpeed: 200, moveSpeed: 12 },

  // 10.5K — fórmula de dano completa (bloqueio/defesa por tipo). Ver DamageSystem.
  combat: {
    // 10.6G — fraqueza/resistência por ARMA. Números altos o bastante para o jogador SENTIR
    // (e trocar de arma), mas sem transformar a arma errada em parede.
    // três níveis, do jeito que se lê num ícone colorido:
    weakBonus: 0.5, // VERDE  — arma que o monstro teme: +50% de dano
    toughCut: 0.15, // LARANJA — arma mediana contra ele: -15%
    resistCut: 0.35, // VERMELHO — arma que ele segura bem: -35%

    blockReduction: 0.5, // golpe BLOQUEADO causa 50% do dano (e metade do empurrão)
    blockChanceCap: 0.6, // teto da chance de bloqueio
    typeDefenseCap: 0.75, // teto da defesa por tipo de dano
    // 10.9S — TETO de velocidade de ataque. Sem isso, os nós INFINITOS de atkspd (pow/agi_inf_atkspd,
    // até +60% cada) empilhavam e viravam "metralhadora". 2.4/s = golpe a cada ~0,42s (rápido, não absurdo).
    maxAttacksPerSecond: 2.4,
    // 10.7G — ANTI-IMORTALIDADE: as reduções (armor × resil × typeDef × block) se
    // MULTIPLICAM e chegavam a 98%. Agora o player SEMPRE toma no mínimo (1-maxTotalReduction)
    // do golpe bruto. Sem isso, árvore + equipamento + altar deixavam o Caçador imortal.
    maxTotalReduction: 0.80, // player toma no mínimo 20% de cada golpe
    // Regen contínua nunca cura mais que este % do HP máximo por segundo (senão, com HP
    // gigante, a cura superava qualquer dano recebido = imortal).
    maxRegenPerSecPct: 0.02,
    // 10.9EQ — ROUBO DE VIDA: teto do % (arvore + equipamento) e teto de CURA por segundo, em % da
    // vida maxima. Sem o 2o teto, um golpe de area em 20 monstros curava a barra inteira (imortal).
    lifeStealCap: 0.25,
    maxLifeStealPerSecPct: 0.04,
    // 10.7N — DANO PERCENTUAL de elite/campeão/boss: some uma fatia do HP MÁX do player
    // que IGNORA armadura/resiliência/bloqueio. É o que ameaça um tank de 80K HP que mata
    // tudo rápido e a regen cobre o resto — o piso de dano sozinho não bastava. VALIDADO.
    elitePctMaxHp: 0.05,    // 10.9HS: era 0.04 — elite tira 5% do HP máx por golpe (furando defesa)
    championPctMaxHp: 0.09, // 10.9HS: era 0.07 — campeão 9%
    bossPctMaxHp: 0.10,     // boss 10%
  },

  // ===================== FASE 8: WORLD / REGIÕES =====================
  world: {
    startRegion: "forgottenForest",
    // 10.5V — ciclo dia/noite (segundos por volta completa) e piso de luz da noite
    dayNight: { period: 420, startAt: 0.35 },
    // Portal entre regiões: geometria/energia (visual em RiftGate.ts)
    rift: { interactRadius: 3.0, transitionMs: 520 },
    regions: [
      {
        id: "forgottenForest",
        nameKey: "region2.forgottenForest",
        tier: 1,
        recommendedLevel: 1,
        spawn: { x: 0, z: 0 },
        sanctuary: { center: { x: 0, z: 0 }, radius: SANCTUARY_RADIUS },
        // 10.6Q — Área 1 GIGANTE para exploração de 1h. Home (col1,row2) com folga da Sanctuary.
        arenaHalfSize: 220,
        grid: { cols: 3, rows: 5, colWeights: [1.3, 1.1, 1.1], rowWeights: [1.2, 0.85, 1.3, 0.85, 1.2] },
        enemyTable: [
          { id: "voidling", weight: 52 },
          { id: "voidRunner", weight: 22 },
          { id: "voidBrute", weight: 14 },
          { id: "voidWisp", weight: 12 }, // 10.5H: voador na Área 1
          { id: "voidSpitter", weight: 10 }, // 10.6I: exclusivo da Área 1
          { id: "voidHusk", weight: 6 }, // 10.6I: exclusivo da Área 1
        ],
        maxAlive: 220, // 10.9EZ era 110 // 10.7L: HORDAS — Área 1 (15 territórios, mapa 440x440)
        subAreas: [
          { id: "forestEntrance", nameKey: "region.forestEntrance", zMin: 21, zMax: 999, tint: 0x1a2a16 },
          { id: "voidClearing", nameKey: "region.voidClearing", zMin: -21, zMax: 21, tint: 0x17200f },
          { id: "corruptedGrove", nameKey: "region.corruptedGrove", zMin: -63, zMax: -21, tint: 0x2a1420 },
          { id: "alphaDen", nameKey: "region.alphaDen", zMin: -999, zMax: -63, tint: 0x1a1030 },
        ],
        theme: {
          ground: 0x5a9e42, grid: 0x4a8038, ambient: 0xbcd6ff, ambientIntensity: 1.4,
          sun: 0xfff2d6, sunIntensity: 1.45, fog: 0xbfe2c0, accent: 0x9d7bff, stone: 0x8a7d64,
        },
        // 10.9BS — Floresta: verde-acinzentado claro, névoa leve
        mood: { sky: 0xc2dccb, fog: 0xb9d6c2, density: 0.0085, grade: { tint: 0xfff3e2, saturation: 1.12, contrast: 1.05, lift: 0.0, vignette: 0.3 }, bloom: 0.45 }, // 10.9GR: quente, viva
        // Rift Gate aparece perto do Alpha Den; acende após Apex Fang (voidAlpha)
        // 10.9AU — estava em (8,-34), DENTRO do Covil do Alfa (0,-42 r15): perto dele aparecia
        // "Desafiar Boss" em vez de "Viajar". Agora fica a leste do Covil, fora da arena.
        rift: { x: 26, z: -42, toRegion: "shatteredWilds", requiresBoss: "voidAlpha", nameKey: "rift.toWilds" },
      },
      {
        id: "shatteredWilds",
        nameKey: "region2.shatteredWilds",
        tier: 2,
        recommendedLevel: 12, // 10.9HS: era 10 — acompanha o Portal Rift mais exigente da Floresta
        spawn: { x: 0, z: 0 },
        sanctuary: { center: { x: 0, z: 0 }, radius: SANCTUARY_RADIUS },
        // 10.6Q — Área 2 vasta. Home (col1,row1) com margem da Sanctuary em (0,0).
        arenaHalfSize: 260,
        grid: { cols: 4, rows: 4, colWeights: [0.7, 1.6, 0.85, 1.05], rowWeights: [0.7, 1.6, 0.9, 1.0] },
        enemyTable: [
          { id: "shardfang", weight: 40 },
          { id: "aetherwing", weight: 25 },
          { id: "glintback", weight: 20 },
          { id: "craghorn", weight: 15 },
          { id: "shardCrawler", weight: 18 }, // 10.6I: exclusivo da Área 2
          { id: "veinLurker", weight: 12 }, // 10.6I: exclusivo da Área 2
        ],
        maxAlive: 240, // 10.9EZ era 130 // 10.7L: HORDAS — Área 2 (16 territórios, mapa 520x520)
        subAreas: [
          { id: "crystalFlats", nameKey: "region.crystalFlats", zMin: -6, zMax: 999, tint: 0x14283a },
          { id: "brokenPass", nameKey: "region.brokenPass", zMin: -24, zMax: -6, tint: 0x172a3d },
          { id: "aetherCliffs", nameKey: "region.aetherCliffs", zMin: -44, zMax: -24, tint: 0x172038 },
          { id: "wardenGrounds", nameKey: "region.wardenGrounds", zMin: -999, zMax: -44, tint: 0x101a30 },
        ],
        theme: {
          ground: 0x4f8a70, grid: 0x3f7a5e, ambient: 0xcfe4ff, ambientIntensity: 1.4,
          sun: 0xfff0d8, sunIntensity: 1.4, fog: 0xbfe0d8, accent: 0x62e8ff, stone: 0x8496a0,
        },
        // 10.9BS — Terras Partidas: ciano-frio, névoa um pouco mais densa
        mood: { sky: 0xb4dde4, fog: 0xa9d3dc, density: 0.0095, grade: { tint: 0xe6f0ff, saturation: 1.06, contrast: 1.08, lift: 0.01, vignette: 0.36 }, bloom: 0.5 }, // 10.9GR: fria, cristalina
        // Rift de volta para a Forgotten Forest (sempre ativo na Região 2)
        rift: { x: 8, z: -30, toRegion: "forgottenForest", nameKey: "rift.toForest" },
      },
      // ===================== FASE 10: TEMPEST REACH (Tier 3) =====================
      {
        id: "tempestReach",
        nameKey: "region.tempestReach",
        tier: 3,
        recommendedLevel: 24, // 10.9HS: era 20 — acompanha o Portal Rift mais exigente das Terras
        spawn: { x: 0, z: 0 },
        sanctuary: { center: { x: 0, z: 0 }, radius: SANCTUARY_RADIUS },
        // 10.6Q — Área 3 épica, a maior. Home (col1,row1) com folga da Sanctuary.
        arenaHalfSize: 310,
        grid: { cols: 4, rows: 5, colWeights: [0.65, 1.6, 0.85, 1.1], rowWeights: [0.5, 2.3, 0.75, 0.7, 0.95] },
        enemyTable: [
          { id: "galestrider", weight: 34 },
          { id: "arcbeak", weight: 26 },
          { id: "stormback", weight: 22 },
          { id: "cloudstalker", weight: 18 },
          { id: "stormLance", weight: 16 }, // 10.6I: exclusivo da Área 3
          { id: "ruinSentinel", weight: 10 }, // 10.6I: exclusivo da Área 3
        ],
        maxAlive: 260, // 10.9EZ era 150 // 10.7L: HORDAS — Área 3 (20 territórios, mapa 620x620)
        subAreas: [
          { id: "windwardPath", nameKey: "region.windwardPath", zMin: -6, zMax: 999, tint: 0x24405e },
          { id: "skyruinTerrace", nameKey: "region.skyruinTerrace", zMin: -24, zMax: -6, tint: 0x27456a },
          { id: "thunderstoneRidge", nameKey: "region.thunderstoneRidge", zMin: -44, zMax: -24, tint: 0x223d64 },
          { id: "tempestCrown", nameKey: "region.tempestCrown", zMin: -999, zMax: -44, tint: 0x1b2f52 },
        ],
        theme: {
          ground: 0x5a7488, grid: 0x4a6a80, ambient: 0xd0e2ff, ambientIntensity: 1.4,
          sun: 0xfff2e0, sunIntensity: 1.35, fog: 0xc0d4e0, accent: 0x7fd6ff, stone: 0x9aa8bc,
        },
        // 10.9BS — Tempestade: azul-chumbo, névoa pesada
        mood: { sky: 0x9fb0c4, fog: 0x94a6bb, density: 0.011, grade: { tint: 0xdde4f4, saturation: 0.96, contrast: 1.1, lift: 0.02, vignette: 0.42 }, bloom: 0.55 }, // 10.9GR: dessaturada, pesada
        // Rift de volta (destino padrão; a UI de viagem lista todas as regiões desbloqueadas)
        rift: { x: 8, z: -30, toRegion: "shatteredWilds", nameKey: "rift.toWilds" },
      },
    ] as WorldRegionDef[],
    // Elite exclusivo da Região 2 (spawna em Warden Grounds enquanto explora)
    wildsElite: { id: "shardWarden", zMin: -60, zMax: -46, xSpread: 20, maxAlive: 2, interval: 2.2 }, // 10.9HS: mais frequente
    // Elite exclusivo da Região 3 (Tempest Warden — spawna em Thunderstone Ridge/Tempest Crown)
    tempestElite: { id: "tempestWarden", zMin: -60, zMax: -40, xSpread: 20, maxAlive: 2, interval: 2.4 }, // 10.9HS: mais frequente
  },

  // ============ 10.5M: TERRITÓRIOS — 30 PEDAÇOS DE MAPA, CADA UM COM SUA GANGUE ============
  // O mundo deixou de ser uma arena pequena com spawn ao redor do player: cada região é uma
  // GRADE de territórios; cada território tem nome, tier, bioma (visual do chão/decoração) e
  // uma GANGUE (tabela de inimigos + campeão elite). Os monstros nascem espalhados pelo mapa,
  // nos territórios perto do player, e voltam a nascer quando a gangue é dizimada.
  territories: {
    activeRadius: 185, // 10.9CS: raio maior → mais territórios vivos ao redor (menos "vazio" longe da base)
    despawnRadius: 210, // 10.6Q: inimigo além disso é reciclado
    // 10.9HS — AUDITORIA (Thiago: "ainda está fácil pra abrir todos"): abates por tier pra
    // conquistar o território (era 12/19/25/32/38 — 1 a 3 min de horda por pedaço). Régua média.
    huntTargetByTier: [20, 34, 48, 62, 76],
    // 10.9HS — TRAVA DE NÍVEL nas portas, por TIER × PROFUNDIDADE. Antes era nível fixo por tier
    // (+8 linear por volta) — mas depth NUNCA relouqueava território conquistado (cleared() fica
    // true pra sempre), então esse número nunca chegava a valer depois da 1ª volta completa: o
    // mapa inteiro ficava aberto de vez a partir do Nv~40. Agora CADA VOLTA relouqueia tudo
    // (onAreaCompleted) e o nível pedido CRESCE MULTIPLICANDO pelo tier — gateLevel(tier, depth) =
    // min(999, gateLevelByTier[tier] × (1+gateLevelDepthGrowth)^(depth-1)). Isso alinha o T5 (o
    // mais duro) com a régua de TIER DE EQUIPAMENTO (Nv20/35/50/70/90/150/200/250/300/400/500/700/
    // 1000 — Rarity.ts): T5 pede Nv24→34(volta10)→75(30)→164(50)→352(70)→999(≈105), acompanhando
    // T3→T5→T7→T10→T13→T14 do equipamento. T1-T4 sobem a MESMA taxa mas de uma base menor —
    // ficam sempre território "fácil" mesmo em profundidade alta (T1: Nv1→49 na volta 100).
    gateLevelByTier: [1, 5, 10, 16, 24],
    gateLevelDepthGrowth: 0.04, // +4% composto por volta de Profundidade (por tier)
    // 10.9EZ — Thiago: "50 monstros em cada mapa" (cada território). Os de perto viram monstros de
    // verdade (teto maxAlive da região); os de longe ficam na população virtual (minimapa/Mapa do Mundo).
    alivePerTerritory: 50, // 10.9CS: HORDAS mais densas (era 10) — mapa mais povoado longe da área 1
    championRespawn: 35, // 10.9HS: era 50 — Thiago: "elites com mais frequência"
    // 10.9HS — ELITE DE CAMPO: fora do campeão (1 por território), o mesmo bicho elite da região
    // agora pode nascer na gangue comum como um "elite de campo" — rola essa chance por spawn no
    // território que TEM campeão definido (o mesmo id, sem o título de campeão). Mais frequente,
    // dropa pela tabela "elite/warden/stormWarden" (já melhor que a normal) e bate mais forte
    // (elitePctMaxHp/championPctMaxHp subiram — ver combat).
    fieldEliteChance: 0.07,
    spawnMinFromPlayer: 9, // 10.9R: era 12 — o território onde você está enche melhor perto de você
    checkInterval: 0.8, // s entre varreduras de spawn (barato)
    // 10.7T — GUARNIÇÃO DA SAFEZONE: o território "home" é gigante, então a densidade perto da
    // base ficava baixíssima (só ~3 monstros à vista). Aqui mantemos uma HORDA viva num anel
    // logo fora da zona segura, ao redor do Santuário, em TODAS as áreas. Ignora o
    // spawnMinFromPlayer de propósito (a queixa é falta de monstro perto da base).
    homeGarrison: { count: 16, maxR: 48, activeWithin: 95 },
    // 10.9DW — HORDA AO REDOR DO CACADOR. A guarnicao acima so vale perto da base; no campo o
    // territorio e GIGANTE (o Covil do Alfa tem 138x69m = 9.578 m2), entao os 14 da gangue ficam
    // a ~28m um do outro e o Cacador ve 1 monstro num raio de 25m ("so tem 3 monstros").
    // Aqui mantemos uma horda viva num anel ao redor do player ONDE ELE ESTIVER, com a gangue e
    // o tier do territorio dono de cada ponto. E o mesmo remedio da base, aplicado ao mapa todo.
    playerHorde: { count: 12, minR: 15, maxR: 34 },
    // 10.9DX — POPULACAO FIXA POR TERRITORIO: cada pedaco mantem a horda dele viva no mapa mesmo
    // longe do Cacador (como PONTOS, sem modelo/IA — o celular nao aguentaria 210 bichos reais).
    // materializedRadius: dentro disso quem manda sao os inimigos REAIS do spawner (e o mapa
    // desenha eles). E MENOR que o activeRadius de proposito: no limite do raio ativo o spawner
    // ja esbarra no teto global, entao aqueles pedacos ficariam "vazios" no mapa sem estarem.
    population: { refillInterval: 6, materializedRadius: 100 },
    // paletas/densidades por bioma (decoração procedural do Arena)
    /** 10.6F — o tier do território agora PESA: vida e dano dos bichos por tier (1, 2, 3). */
    // 10.9K — curva mais SUAVE entre tiers (Thiago: "escala desigual/picos"). Passos de HP e
    // dano quase iguais entre tiers (~+0,3 por degrau), sem o salto do tier alto (HP 3.0→2.5,
    // dano 2.35→2.0). Assim mudar de território não vira um paredão nem fica trivial.
    tierScale: [
      { hp: 1, dmg: 1 },
      { hp: 1.3, dmg: 1.22 },
      { hp: 1.65, dmg: 1.45 },
      { hp: 2.05, dmg: 1.7 },
      { hp: 2.5, dmg: 2.0 },
    ],
    biomes: {
      grove: { ground: 0x6fae52, trunk: 0x3a2a1c, foliage: [0x1e3a27, 0x27492e, 0x2c5536], rock: 0x3b3f4a, crystal: 0x7760d0, trees: 22, rocks: 9, grass: 26, crystals: 3 },
      thicket: { ground: 0x437a38, trunk: 0x2c1f14, foliage: [0x16301f, 0x1c3a24, 0x224429], rock: 0x33384a, crystal: 0x6a4fd0, trees: 34, rocks: 7, grass: 28, crystals: 3 },
      crystal: { ground: 0x4fa79f, trunk: 0x35304a, foliage: [0x235244, 0x2a6152, 0x2f6b58], rock: 0x46506b, crystal: 0x62e8ff, trees: 13, rocks: 11, grass: 18, crystals: 11 },
      rocky: { ground: 0x9a9a66, trunk: 0x453425, foliage: [0x2c4a2a, 0x35552f], rock: 0x555c69, crystal: 0x8ea2ff, trees: 9, rocks: 24, grass: 14, crystals: 4 },
      marsh: { ground: 0x4f8992, trunk: 0x2f2a2a, foliage: [0x1f4442, 0x27524a], rock: 0x3d4a52, crystal: 0x8fe8ff, trees: 15, rocks: 8, grass: 32, crystals: 5 },
      // 10.5S — biomas próprios das Áreas 2 e 3 (antes elas usavam a paleta da floresta)
      shard: { ground: 0x5289a0, trunk: 0x2f3f56, foliage: [0x2a6f8a, 0x3a86a0, 0x46a0b4], rock: 0x50627d, crystal: 0x62e8ff, trees: 12, rocks: 14, grass: 14, crystals: 12 },
      crag: { ground: 0x7d90a8, trunk: 0x3a4352, foliage: [0x3d5a63, 0x466a70], rock: 0x8496a0, crystal: 0x8ea2ff, trees: 6, rocks: 26, grass: 8, crystals: 4 },
      fissure: { ground: 0x556a90, trunk: 0x2a2f45, foliage: [0x27455e, 0x2f5570], rock: 0x424e69, crystal: 0x9d7bff, trees: 10, rocks: 12, grass: 18, crystals: 7 },
      wind: { ground: 0x7d9a80, trunk: 0x4a4436, foliage: [0x3c6248, 0x487256, 0x52815e], rock: 0x8a97a8, crystal: 0x9fe6ff, trees: 16, rocks: 10, grass: 24, crystals: 4 },
      storm: { ground: 0x627490, trunk: 0x333a4a, foliage: [0x33526b, 0x3c6076], rock: 0x6c86a8, crystal: 0x7fd6ff, trees: 10, rocks: 16, grass: 12, crystals: 8 },
      ruin: { ground: 0x97a0b3, trunk: 0x59606e, foliage: [0x4a6a6a, 0x557878], rock: 0x9aa8bc, crystal: 0xbfefff, trees: 7, rocks: 22, grass: 10, crystals: 5 },
      // 10.6N — BIOMAS ELEMENTAIS. Entrar sem o anel certo custa vida por segundo.
      frost: { hazard: "frost", ground: 0x9fc7de, trunk: 0x5d6f80, foliage: [0x7fa8bf, 0x93bcd2], rock: 0xb6cfdd, crystal: 0xd8f2ff, trees: 9, rocks: 18, grass: 6, crystals: 9 },
      galewind: { hazard: "gale", ground: 0x86a893, trunk: 0x4a5a4c, foliage: [0x5e8a72, 0x6ea486], rock: 0x93a8b4, crystal: 0xc7f2e0, trees: 5, rocks: 14, grass: 20, crystals: 6 },
      thunder: { hazard: "storm", ground: 0x4e5f88, trunk: 0x33405e, foliage: [0x3d5f8a, 0x4a72a4], rock: 0x6c86b8, crystal: 0x9fd0ff, trees: 6, rocks: 16, grass: 8, crystals: 12 },
      ember: { hazard: "ember", ground: 0xa8563a, trunk: 0x5a2a1c, foliage: [0xb35a2a, 0xd07a34], rock: 0x7a3a2a, crystal: 0xffa64d, trees: 7, rocks: 22, grass: 5, crystals: 8 },
      den: { ground: 0x6a5ca0, trunk: 0x2a2038, foliage: [0x2a1f45, 0x33264f], rock: 0x3a3352, crystal: 0x9d7bff, trees: 14, rocks: 14, grass: 16, crystals: 8 },
    } as Record<string, BiomeDef>,
    list: [
      // ---------------- FORGOTTEN FOREST (15 territórios · grade 3×5) ----------------
      // linha 0 = norte extremo (z mais negativo) … linha 4 = sul extremo
      { id: "t_ravine", regionId: "forgottenForest", nameKey: "terr.ravine", col: 0, row: 0, tier: 3, biome: "rocky", champion: "corruptedBrute", gang: [{ id: "voidBrute", weight: 40 }, { id: "voidRunner", weight: 30 }, { id: "voidling", weight: 15 }, { id: "voidHusk", weight: 15 }] },
      { id: "t_crest", regionId: "forgottenForest", nameKey: "terr.crest", col: 1, row: 0, tier: 3, biome: "den", champion: "corruptedBrute", gang: [{ id: "voidWisp", weight: 35 }, { id: "voidBrute", weight: 35 }, { id: "voidRunner", weight: 15 }, { id: "voidSpitter", weight: 15 }] },
      { id: "t_shardfield", regionId: "forgottenForest", nameKey: "terr.shardfield", col: 2, row: 0, tier: 3, biome: "crystal", champion: "corruptedBrute", gang: [{ id: "voidWisp", weight: 40 }, { id: "voidling", weight: 30 }, { id: "voidBrute", weight: 30 }] },
      { id: "t_grove", regionId: "forgottenForest", nameKey: "terr.grove", col: 0, row: 1, tier: 2, biome: "thicket", champion: "corruptedBrute", gang: [{ id: "voidRunner", weight: 40 }, { id: "voidBrute", weight: 30 }, { id: "voidling", weight: 30 }] },
      { id: "t_den", regionId: "forgottenForest", nameKey: "terr.den", col: 1, row: 1, tier: 3, biome: "den", gang: [{ id: "voidBrute", weight: 45 }, { id: "voidRunner", weight: 30 }, { id: "voidWisp", weight: 25 }] },
      { id: "t_fangtrail", regionId: "forgottenForest", nameKey: "terr.fangtrail", col: 2, row: 1, tier: 2, biome: "grove", champion: "corruptedBrute", gang: [{ id: "voidRunner", weight: 45 }, { id: "voidling", weight: 35 }, { id: "voidWisp", weight: 20 }] },
      { id: "t_whisper", regionId: "forgottenForest", nameKey: "terr.whisper", col: 0, row: 2, tier: 1, biome: "grove", gang: [{ id: "voidling", weight: 55 }, { id: "voidRunner", weight: 30 }, { id: "voidWisp", weight: 15 }] },
      { id: "t_clearing", regionId: "forgottenForest", nameKey: "terr.clearing", col: 1, row: 2, tier: 1, biome: "grove", home: true, gang: [{ id: "voidling", weight: 70 }, { id: "voidRunner", weight: 30 }] },
      { id: "t_garden", regionId: "forgottenForest", nameKey: "terr.garden", col: 2, row: 2, tier: 1, biome: "crystal", gang: [{ id: "voidling", weight: 45 }, { id: "voidWisp", weight: 35 }, { id: "voidSpitter", weight: 20 }] },
      { id: "t_marsh", regionId: "forgottenForest", nameKey: "terr.marsh", col: 0, row: 3, tier: 2, biome: "marsh", gang: [{ id: "voidling", weight: 40 }, { id: "voidWisp", weight: 35 }, { id: "voidRunner", weight: 25 }] },
      { id: "t_entrance", regionId: "forgottenForest", nameKey: "terr.entrance", col: 1, row: 3, tier: 2, biome: "grove", gang: [{ id: "voidling", weight: 45 }, { id: "voidRunner", weight: 35 }, { id: "voidBrute", weight: 20 }] },
      { id: "t_hills", regionId: "forgottenForest", nameKey: "terr.hills", col: 2, row: 3, tier: 2, biome: "rocky", gang: [{ id: "voidRunner", weight: 40 }, { id: "voidBrute", weight: 35 }, { id: "voidHusk", weight: 25 }] },
      { id: "t_roots", regionId: "forgottenForest", nameKey: "terr.roots", col: 0, row: 4, tier: 3, biome: "thicket", champion: "corruptedBrute", gang: [{ id: "voidBrute", weight: 40 }, { id: "voidRunner", weight: 30 }, { id: "voidWisp", weight: 30 }] },
      { id: "t_sleepgate", regionId: "forgottenForest", nameKey: "terr.sleepgate", col: 1, row: 4, tier: 3, biome: "den", champion: "corruptedBrute", gang: [{ id: "voidWisp", weight: 40 }, { id: "voidBrute", weight: 35 }, { id: "voidRunner", weight: 25 }] },
      { id: "t_southmist", regionId: "forgottenForest", nameKey: "terr.southmist", col: 2, row: 4, tier: 3, biome: "marsh", gang: [{ id: "voidWisp", weight: 45 }, { id: "voidling", weight: 30 }, { id: "voidRunner", weight: 25 }] },

      // ---------------- SHATTERED WILDS (9 territórios · grade 3×3) ----------------
      { id: "t_vein", regionId: "shatteredWilds", nameKey: "terr.vein", col: 0, row: 0, tier: 4, biome: "shard", champion: "shardWarden", gang: [{ id: "shardfang", weight: 35 }, { id: "craghorn", weight: 35 }, { id: "aetherwing", weight: 30 }] },
      { id: "t_wardens", regionId: "shatteredWilds", nameKey: "terr.wardens", col: 1, row: 0, tier: 4, biome: "crag", champion: "shardWarden", gang: [{ id: "craghorn", weight: 40 }, { id: "glintback", weight: 30 }, { id: "shardfang", weight: 30 }] },
      { id: "t_mesa", regionId: "shatteredWilds", nameKey: "terr.mesa", col: 2, row: 0, tier: 4, biome: "crag", champion: "shardWarden", gang: [{ id: "aetherwing", weight: 40 }, { id: "shardfang", weight: 35 }, { id: "craghorn", weight: 25 }] },
      { id: "t_cliffs", regionId: "shatteredWilds", nameKey: "terr.cliffs", col: 0, row: 1, tier: 3, biome: "fissure", gang: [{ id: "aetherwing", weight: 45 }, { id: "shardfang", weight: 35 }, { id: "glintback", weight: 20 }] },
      { id: "t_flats", regionId: "shatteredWilds", nameKey: "terr.flats", col: 1, row: 1, tier: 3, biome: "shard", home: true, gang: [{ id: "shardfang", weight: 50 }, { id: "glintback", weight: 30 }, { id: "aetherwing", weight: 20 }] },
      { id: "t_glint", regionId: "shatteredWilds", nameKey: "terr.glint", col: 2, row: 1, tier: 3, biome: "shard", gang: [{ id: "glintback", weight: 45 }, { id: "shardfang", weight: 35 }, { id: "aetherwing", weight: 20 }] },
      { id: "t_shardpit", regionId: "shatteredWilds", nameKey: "terr.shardpit", col: 0, row: 2, tier: 4, biome: "fissure", gang: [{ id: "shardfang", weight: 40 }, { id: "craghorn", weight: 35 }, { id: "shardCrawler", weight: 25 }] },
      { id: "t_brokenpass", regionId: "shatteredWilds", nameKey: "terr.brokenpass", col: 1, row: 2, tier: 3, biome: "crag", gang: [{ id: "craghorn", weight: 40 }, { id: "shardfang", weight: 35 }, { id: "aetherwing", weight: 25 }] },
      { id: "t_fissures", regionId: "shatteredWilds", nameKey: "terr.fissures", col: 2, row: 2, tier: 4, biome: "fissure", gang: [{ id: "aetherwing", weight: 40 }, { id: "glintback", weight: 35 }, { id: "craghorn", weight: 25 }] },
      // 10.6H — Área 2 cresceu para 4×4: 7 pedaços novos
      { id: "t_spires", regionId: "shatteredWilds", nameKey: "terr.spires", col: 3, row: 0, tier: 4, biome: "crag", champion: "shardWarden", gang: [{ id: "craghorn", weight: 40 }, { id: "aetherwing", weight: 35 }, { id: "shardfang", weight: 25 }] },
      { id: "t_quarry", regionId: "shatteredWilds", nameKey: "terr.quarry", col: 3, row: 1, tier: 3, biome: "crag", gang: [{ id: "glintback", weight: 40 }, { id: "craghorn", weight: 35 }, { id: "shardfang", weight: 25 }] },
      { id: "t_hollow", regionId: "shatteredWilds", nameKey: "terr.hollow", col: 3, row: 2, tier: 4, biome: "ember", gang: [{ id: "shardfang", weight: 40 }, { id: "aetherwing", weight: 35 }, { id: "veinLurker", weight: 25 }] },
      { id: "t_lowreach", regionId: "shatteredWilds", nameKey: "terr.lowreach", col: 0, row: 3, tier: 3, biome: "shard", gang: [{ id: "shardfang", weight: 45 }, { id: "glintback", weight: 30 }, { id: "craghorn", weight: 25 }] },
      { id: "t_saltflat", regionId: "shatteredWilds", nameKey: "terr.saltflat", col: 1, row: 3, tier: 4, biome: "frost", gang: [{ id: "craghorn", weight: 45 }, { id: "shardfang", weight: 30 }, { id: "aetherwing", weight: 25 }] },
      { id: "t_geode", regionId: "shatteredWilds", nameKey: "terr.geode", col: 2, row: 3, tier: 4, biome: "shard", champion: "shardWarden", gang: [{ id: "glintback", weight: 45 }, { id: "aetherwing", weight: 30 }, { id: "shardCrawler", weight: 25 }] },
      { id: "t_deepvein", regionId: "shatteredWilds", nameKey: "terr.deepvein", col: 3, row: 3, tier: 5, biome: "fissure", champion: "shardWarden", gang: [{ id: "craghorn", weight: 38 }, { id: "glintback", weight: 32 }, { id: "veinLurker", weight: 30 }] },

      // ---------------- TEMPEST REACH (6 territórios · grade 3×2) ----------------
      { id: "t_thunder", regionId: "tempestReach", nameKey: "terr.thunder", col: 0, row: 0, tier: 5, biome: "storm", champion: "tempestWarden", gang: [{ id: "stormback", weight: 40 }, { id: "galestrider", weight: 35 }, { id: "arcbeak", weight: 25 }] },
      { id: "t_crown", regionId: "tempestReach", nameKey: "terr.crown", col: 1, row: 0, tier: 5, biome: "storm", champion: "tempestWarden", gang: [{ id: "cloudstalker", weight: 40 }, { id: "arcbeak", weight: 35 }, { id: "stormback", weight: 25 }] },
      { id: "t_gorge", regionId: "tempestReach", nameKey: "terr.gorge", col: 2, row: 0, tier: 5, biome: "ruin", gang: [{ id: "arcbeak", weight: 45 }, { id: "galestrider", weight: 30 }, { id: "cloudstalker", weight: 25 }] },
      { id: "t_overlook", regionId: "tempestReach", nameKey: "terr.overlook", col: 0, row: 1, tier: 4, biome: "wind", gang: [{ id: "galestrider", weight: 45 }, { id: "arcbeak", weight: 30 }, { id: "cloudstalker", weight: 25 }] },
      { id: "t_windward", regionId: "tempestReach", nameKey: "terr.windward", col: 1, row: 1, tier: 4, biome: "wind", home: true, gang: [{ id: "galestrider", weight: 50 }, { id: "arcbeak", weight: 30 }, { id: "stormback", weight: 20 }] },
      { id: "t_terrace", regionId: "tempestReach", nameKey: "terr.terrace", col: 2, row: 1, tier: 4, biome: "ruin", gang: [{ id: "stormback", weight: 40 }, { id: "cloudstalker", weight: 35 }, { id: "galestrider", weight: 25 }] },
      // 10.6H — Área 3 cresceu para 4×5: 14 pedaços novos (a maior do jogo)
      { id: "t_skyshelf", regionId: "tempestReach", nameKey: "terr.skyshelf", col: 3, row: 0, tier: 5, biome: "storm", champion: "tempestWarden", gang: [{ id: "stormback", weight: 38 }, { id: "cloudstalker", weight: 32 }, { id: "arcbeak", weight: 30 }] },
      { id: "t_updraft", regionId: "tempestReach", nameKey: "terr.updraft", col: 3, row: 1, tier: 4, biome: "galewind", gang: [{ id: "galestrider", weight: 45 }, { id: "arcbeak", weight: 30 }, { id: "stormback", weight: 25 }] },
      { id: "t_boneyard", regionId: "tempestReach", nameKey: "terr.boneyard", col: 0, row: 2, tier: 4, biome: "frost", gang: [{ id: "cloudstalker", weight: 42 }, { id: "stormback", weight: 33 }, { id: "galestrider", weight: 25 }] },
      { id: "t_gale", regionId: "tempestReach", nameKey: "terr.gale", col: 1, row: 2, tier: 4, biome: "wind", gang: [{ id: "galestrider", weight: 48 }, { id: "cloudstalker", weight: 28 }, { id: "arcbeak", weight: 24 }] },
      { id: "t_arcfield", regionId: "tempestReach", nameKey: "terr.arcfield", col: 2, row: 2, tier: 5, biome: "ember", gang: [{ id: "arcbeak", weight: 44 }, { id: "stormback", weight: 31 }, { id: "stormLance", weight: 25 }] },
      { id: "t_spiredge", regionId: "tempestReach", nameKey: "terr.spiredge", col: 3, row: 2, tier: 5, biome: "ruin", champion: "tempestWarden", gang: [{ id: "stormback", weight: 40 }, { id: "arcbeak", weight: 32 }, { id: "ruinSentinel", weight: 28 }] },
      { id: "t_stillwind", regionId: "tempestReach", nameKey: "terr.stillwind", col: 0, row: 3, tier: 4, biome: "wind", gang: [{ id: "galestrider", weight: 44 }, { id: "stormback", weight: 30 }, { id: "arcbeak", weight: 26 }] },
      { id: "t_pillars", regionId: "tempestReach", nameKey: "terr.pillars", col: 1, row: 3, tier: 5, biome: "ruin", gang: [{ id: "cloudstalker", weight: 40 }, { id: "galestrider", weight: 32 }, { id: "stormback", weight: 28 }] },
      { id: "t_thunderpit", regionId: "tempestReach", nameKey: "terr.thunderpit", col: 2, row: 3, tier: 5, biome: "thunder", champion: "tempestWarden", gang: [{ id: "stormback", weight: 42 }, { id: "arcbeak", weight: 33 }, { id: "stormLance", weight: 25 }] },
      { id: "t_highroost", regionId: "tempestReach", nameKey: "terr.highroost", col: 3, row: 3, tier: 5, biome: "galewind", gang: [{ id: "arcbeak", weight: 48 }, { id: "galestrider", weight: 28 }, { id: "cloudstalker", weight: 24 }] },
      { id: "t_shroud", regionId: "tempestReach", nameKey: "terr.shroud", col: 0, row: 4, tier: 5, biome: "frost", gang: [{ id: "cloudstalker", weight: 50 }, { id: "stormback", weight: 28 }, { id: "arcbeak", weight: 22 }] },
      { id: "t_stormeye", regionId: "tempestReach", nameKey: "terr.stormeye", col: 1, row: 4, tier: 5, biome: "thunder", champion: "tempestWarden", gang: [{ id: "stormback", weight: 38 }, { id: "cloudstalker", weight: 32 }, { id: "arcbeak", weight: 30 }] },
      { id: "t_lastledge", regionId: "tempestReach", nameKey: "terr.lastledge", col: 2, row: 4, tier: 5, biome: "ruin", gang: [{ id: "galestrider", weight: 38 }, { id: "stormback", weight: 34 }, { id: "cloudstalker", weight: 28 }] },
      { id: "t_apex", regionId: "tempestReach", nameKey: "terr.apex", col: 3, row: 4, tier: 5, biome: "storm", champion: "tempestWarden", gang: [{ id: "stormback", weight: 36 }, { id: "arcbeak", weight: 32 }, { id: "ruinSentinel", weight: 32 }] },
    ] as TerritoryDef[],
  },

  // ===================== 10.5O: MISSÕES (com recompensa de verdade) =====================
  // Sem timer/energia/daily: 3 objetivos ativos; cumpriu, recebe na hora e entra outro no lugar
  // com alvo/prêmio maiores. Recompensa escala com o nível (Fragmentos + XP + shards).
  missions: {
    slots: 3,
    coinsPerLevel: 2, // 10.9AS: era 3 (antes 6) — gold estava fácil demais
    xpPerLevel: 14,
    repeatGrowth: 0.3, // +30% de alvo/prêmio a cada vez que a mesma missão é cumprida
    templates: [
      { id: "m_kill", kind: "kill" as const, target: 20, coins: 20, xp: 70, shards: 0 },
      { id: "m_elite", kind: "elite" as const, target: 2, coins: 40, xp: 140, shards: 2 },
      { id: "m_chest", kind: "chest" as const, target: 2, coins: 30, xp: 90, shards: 1 },
      { id: "m_explore", kind: "territory" as const, target: 3, coins: 26, xp: 110, shards: 0 },
      { id: "m_hunt", kind: "kill" as const, target: 45, coins: 48, xp: 180, shards: 2 },
      { id: "m_boss", kind: "boss" as const, target: 1, coins: 95, xp: 400, shards: 5 },
    ],
  },

  save: {
    key: "voidhunter.save",
    version: 8, // v8: Tempest Reach (Região 3), Regional Mastery e Rift Surges
    // 10.9V — RESET GLOBAL controlado: BUMPAR este número zera TODOS os jogadores 1x na próxima
    // vez que abrirem (mantém só as configurações). Use nos testes p/ começar do zero. 1 = sem reset.
    // 10.9BV — reset geral dos testes (2026-09-15) · 10.9EQ — 2o reset geral (2026-09-18), pedido do
    // Thiago: "limpar tudo pra comecarmos do 0". Junto com a correcao dos atributos em fracao que
    // saiam 100% — os itens velhos com esses valores errados somem com o reset.
    // 10.9IJ — Thiago: "vamos resetar todo o jogo pra iniciar oficialmente os testes".
    // 3 -> 4 zera TODO mundo uma vez na proxima abertura: local e nuvem (o blob com o token
    // velho vale -1 no placar, entao nunca volta por cima do save novo). Configuracoes de
    // idioma, audio e graficos sao preservadas.
    wipeToken: 4,
    // 10.9FL — BRINDE unico: 3 armas fortes de compensacao (bug das armas que sumiam). Cada personagem
    // recebe 1x quando abrir. BUMPAR este numero da o brinde de novo. 0 = sem brinde.
    // 10.9IJ — DESLIGADO junto com o reset. O brinde era compensacao por um bug de armas que
    // sumiam; quem comeca agora nunca passou por ele. Com o save zerado, `giftClaimed` volta a
    // 0 e TODO personagem novo abriria o jogo com 3 armas LENDARIAS + 500 moedas + 20 diamantes
    // — teste de progressao do zero nenhum sobrevive a isso.
    giftToken: 0,
    longAbsenceMin: 30, // 10.9CC — minutos no fundo a partir dos quais o app recarrega ao voltar
    autosaveInterval: 30, // segundos
  },
} as const;

export type GameConfigType = typeof GameConfig;
